import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import XLSX from 'xlsx-js-style';
import api from '../utils/api';
import { PageHeader, Modal, DetailModal, ConfirmDialog, Badge, attendanceBadgeMap, sessionTypeBadgeMap, severityBadgeMap, requestBadgeMap, disciplinaryStatusBadgeMap, Spinner, EmptyState, useToast, usePagination, Pagination, useSort, SortTh, SearchableSelect, CapacityResolveModal, intakeOptionsFor, FilterBar, FilterSelect, InlineSpinner, TableSkeletonRows, DateRangePicker } from '../components/UI';
import { usePermissions } from '../context/PermissionsContext';
import { fmtDate, fmtDateTime, fmtSessionName, fmtPct } from '../utils/format';
import FileAttachments from '../components/FileAttachments';
import { REQUEST_TYPE_OPTIONS, DATE_RANGE_TYPES } from '../utils/requestTypes';

// ─── SHARED ──────────────────────────────────────────────────────────────────
function FormField({ id, label, type = 'text', value, onChange, options, textarea, required, disabled, hint }) {
  return (
    <div className="col-12 col-sm-6">
      <label htmlFor={id} style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>{label}{required && ' *'}</label>
      {options ? (
        <SearchableSelect id={id} value={value} onChange={onChange} options={options} disabled={disabled} />
      ) : textarea ? (
        <textarea id={id} className="sms-form-control" rows={3} value={value || ''} onChange={onChange} style={{ resize: 'vertical' }} disabled={disabled} />
      ) : (
        <input id={id} type={type} className="sms-form-control" value={value || ''} onChange={onChange} disabled={disabled} />
      )}
      {hint && <div style={{ fontSize: 12, lineHeight: 1.35, color: 'var(--sms-txt3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function SaveButtons({ saving, disabled, onCancel, label = 'Create' }) {
  return (
    <div className="d-flex justify-content-end gap-2 mt-4">
      <button type="button" className="sms-btn sms-btn-outline" onClick={onCancel}>Cancel</button>
      <button type="submit" className="sms-btn sms-btn-primary" disabled={saving || disabled}>
        {saving ? <><InlineSpinner style={{ marginRight: 4 }} />Saving…</> : label}
      </button>
    </div>
  );
}

function localDateISO(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ─── INTAKES ─────────────────────────────────────────────────────────────────
const INTAKE_EMPTY = { intake_code: '', course_code: '', soc_date: '', eoc_date: '', intake_type: '', capacity: 50, min_attendance_rate: '', min_assessment_result: '', is_active: 1 };

export function IntakesPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const [rows,    setRows]    = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(INTAKE_EMPTY);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [delId,   setDelId]   = useState(null);
  const [viewing, setViewing] = useState(null);
  const [bulkTarget, setBulkTarget] = useState(null); // intake row being bulk-transferred out of
  const [bulkForm,   setBulkForm]   = useState({ to_intake_code: '', transfer_date: '', attendance_migration: 'from_date' });
  const [bulkConfirmText, setBulkConfirmText] = useState('');
  const [bulkErrors, setBulkErrors] = useState({});
  const [bulkBusy,   setBulkBusy]   = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { moved, skipped } shown after a successful transfer
  const [capacityLeftover, setCapacityLeftover] = useState([]);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [timingFilter, setTimingFilter] = useState('');
  const [showCourseIntakes, setShowCourseIntakes] = useState(false);
  const [modules, setModules] = useState([]);
  const [modulesTarget, setModulesTarget] = useState(null); // intake row whose modules are being edited
  const [moduleSelection, setModuleSelection] = useState([]);
  const [moduleDates, setModuleDates] = useState({}); // { [module_code]: { assessment_start_date, assessment_end_date } }
  const [moduleSearch, setModuleSearch] = useState('');
  const [modulesBusy, setModulesBusy] = useState(false);
  const [bulkAssign, setBulkAssign] = useState(false); // bulk "assign module to intakes" modal open
  const [bulkAssignModules, setBulkAssignModules] = useState([]);
  const [bulkAssignIntakes, setBulkAssignIntakes] = useState([]);
  const [bulkAssignSearch, setBulkAssignSearch] = useState('');
  const [bulkAssignBusy, setBulkAssignBusy] = useState(false);
  const [bulkAssignResult, setBulkAssignResult] = useState(null); // per-module { module_code, module_name, added, alreadyAssigned } list shown after a bulk assign
  const [bulkAssignDateMode, setBulkAssignDateMode] = useState('same'); // 'same' (one range for every intake) | 'per_intake' (each intake sets its own) | 'per_module' (each module sets its own, applied across every checked intake)
  const [bulkAssignSharedDates, setBulkAssignSharedDates] = useState({ assessment_start_date: '', assessment_end_date: '' });
  const [bulkAssignIntakeDates, setBulkAssignIntakeDates] = useState({}); // { [intake_id]: { assessment_start_date, assessment_end_date } }
  const [bulkAssignModuleDates, setBulkAssignModuleDates] = useState({}); // { [module_code]: { assessment_start_date, assessment_end_date } }
  const [bulkAssignIntakeModules, setBulkAssignIntakeModules] = useState({}); // { [intake_id]: string[] module_codes already on that intake } — fetched lazily as intakes get checked, used to warn about overlaps before submitting
  const [editModules, setEditModules] = useState(null); // modules assigned to the intake being edited (read-only preview)

  const load = () => {
    setLoading(true);
    api.get('/intakes').then(r => setRows(r.data.data)).catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { api.get('/courses').then(r => setCourses(r.data.data)).catch(() => {}); }, []);
  useEffect(() => { api.get('/modules').then(r => setModules(r.data.data)).catch(() => {}); }, []);

  const openModules = r => {
    setModulesTarget(r);
    setModuleSelection([]);
    setModuleDates({});
    setModuleSearch('');
    api.get(`/intakes/${r.id}/modules`).then(res => {
      setModuleSelection(res.data.data.map(m => m.module_code));
      setModuleDates(Object.fromEntries(res.data.data.map(m => [m.module_code, {
        assessment_start_date: m.assessment_start_date?.slice(0, 10) || '',
        assessment_end_date: m.assessment_end_date?.slice(0, 10) || '',
      }])));
    }).catch(() => toast('Failed to load modules', 'error'));
  };
  const setModuleDate = (code, field, value) =>
    setModuleDates(d => ({ ...d, [code]: { ...d[code], [field]: value } }));

  // Deep-linked here from the Students import flow's "Set up modules" button
  // (state: { openModulesForIntake, pendingModuleIntakes }) — auto-opens the
  // modal for the first intake that came back with no modules, once `rows`
  // has actually loaded (can't look the intake up before then). The nav
  // state is cleared right after so navigating away and back (or refreshing)
  // doesn't re-trigger it.
  useEffect(() => {
    if (!location.state?.openModulesForIntake || !rows.length) return;
    const target = rows.find(r => r.intake_code === location.state.openModulesForIntake);
    if (target) openModules(target);
    const pending = location.state.pendingModuleIntakes || [];
    if (pending.length) {
      toast(`${pending.length} more intake(s) still need modules set up: ${pending.join(', ')}`, 'info');
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [rows]);
  const toggleModuleSelection = code => setModuleSelection(sel => sel.includes(code) ? sel.filter(c => c !== code) : [...sel, code]);
  const handleSaveModules = async () => {
    setModulesBusy(true);
    try {
      const modulesPayload = moduleSelection.map(code => ({
        module_code: code,
        assessment_start_date: moduleDates[code]?.assessment_start_date || null,
        assessment_end_date: moduleDates[code]?.assessment_end_date || null,
      }));
      await api.put(`/intakes/${modulesTarget.id}/modules`, { modules: modulesPayload });
      toast('Modules updated', 'success');
      setModulesTarget(null);
    } catch (err) { toast(err.response?.data?.message || 'Failed to update modules', 'error'); }
    finally { setModulesBusy(false); }
  };

  const openBulkAssign = () => {
    setBulkAssignModules([]); setBulkAssignIntakes([]); setBulkAssignSearch(''); setBulkAssignResult(null);
    setBulkAssignDateMode('same');
    setBulkAssignSharedDates({ assessment_start_date: '', assessment_end_date: '' });
    setBulkAssignIntakeDates({});
    setBulkAssignModuleDates({});
    setBulkAssignIntakeModules({});
    setBulkAssign(true);
  };
  const toggleBulkAssignModuleSel = code => setBulkAssignModules(sel => sel.includes(code) ? sel.filter(c => c !== code) : [...sel, code]);
  const toggleBulkAssignIntake = id => {
    setBulkAssignIntakes(sel => sel.includes(id) ? sel.filter(i => i !== id) : [...sel, id]);
    // Fetch the intake's current modules once, on first check — used to warn
    // when a selected module is already assigned to a selected intake.
    setBulkAssignIntakeModules(cache => {
      if (cache[id]) return cache;
      api.get(`/intakes/${id}/modules`)
        .then(res => setBulkAssignIntakeModules(c => ({ ...c, [id]: res.data.data.map(m => m.module_code) })))
        .catch(() => {});
      return cache;
    });
  };
  // { module_code -> intake_code[] } for every selected module already
  // assigned to a selected intake — informational, doesn't block Assign
  // since the backend already skips these pairs untouched.
  const bulkAssignOverlaps = bulkAssignModules.reduce((acc, code) => {
    const intakeCodes = bulkAssignIntakes
      .filter(id => (bulkAssignIntakeModules[id] || []).includes(code))
      .map(id => rows.find(r => r.id === id)?.intake_code)
      .filter(Boolean);
    if (intakeCodes.length) acc[code] = intakeCodes;
    return acc;
  }, {});
  const setBulkAssignIntakeDate = (id, field, value) =>
    setBulkAssignIntakeDates(d => ({ ...d, [id]: { ...d[id], [field]: value } }));
  const setBulkAssignModuleDate = (code, field, value) =>
    setBulkAssignModuleDates(d => ({ ...d, [code]: { ...d[code], [field]: value } }));
  // Assessment period is mandatory for a bulk assign — where it's missing
  // depends on the active date mode (one shared range, one per intake, or
  // one per module).
  const bulkAssignDatesIncomplete = () => {
    if (bulkAssignDateMode === 'same') {
      return !bulkAssignSharedDates.assessment_start_date || !bulkAssignSharedDates.assessment_end_date;
    }
    if (bulkAssignDateMode === 'per_intake') {
      return bulkAssignIntakes.some(id => {
        const d = bulkAssignIntakeDates[id] || {};
        return !d.assessment_start_date || !d.assessment_end_date;
      });
    }
    return bulkAssignModules.some(code => {
      const d = bulkAssignModuleDates[code] || {};
      return !d.assessment_start_date || !d.assessment_end_date;
    });
  };
  const handleBulkAssignModule = async () => {
    if (!bulkAssignModules.length || !bulkAssignIntakes.length) { toast('Select at least one module and one intake', 'error'); return; }
    if (bulkAssignDatesIncomplete()) { toast('Set an assessment start and end date for every selected item', 'error'); return; }
    setBulkAssignBusy(true);
    try {
      // "same" mode copies one shared range onto every selected intake;
      // "per_intake" lets each intake keep its own; "per_module" instead
      // keeps the range with the module and applies it across every checked
      // intake (via module_dates, which the backend prefers over the
      // per-intake range for that module) — nothing stops any of these from
      // ending up identical, they're just typed independently.
      const intakesPayload = bulkAssignIntakes.map(id => {
        const dates = bulkAssignDateMode === 'same' ? bulkAssignSharedDates : (bulkAssignIntakeDates[id] || {});
        return { id, assessment_start_date: dates.assessment_start_date || null, assessment_end_date: dates.assessment_end_date || null };
      });
      const moduleDatesPayload = bulkAssignDateMode === 'per_module'
        ? Object.fromEntries(bulkAssignModules.map(code => {
            const dates = bulkAssignModuleDates[code] || {};
            return [code, { assessment_start_date: dates.assessment_start_date || null, assessment_end_date: dates.assessment_end_date || null }];
          }))
        : undefined;
      const res = await api.post('/intakes/modules/bulk-assign', { module_codes: bulkAssignModules, intakes: intakesPayload, module_dates: moduleDatesPayload });
      setBulkAssignResult(res.data.results);
      load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to assign modules', 'error'); }
    finally { setBulkAssignBusy(false); }
  };

  const openCreate = () => { setEditing(null); setForm(INTAKE_EMPTY); setError(''); setShowCourseIntakes(false); setEditModules(null); setShow(true); };
  const openEdit   = r  => {
    setEditing(r); setForm({ intake_code: r.intake_code, course_code: r.course_code||'', soc_date: r.soc_date?.slice(0,10)||'', eoc_date: r.eoc_date?.slice(0,10)||'', intake_type: r.intake_type||'', capacity: r.capacity ?? 50, min_attendance_rate: r.min_attendance_rate ?? '', min_assessment_result: r.min_assessment_result ?? '', is_active: r.is_active });
    setError(''); setShowCourseIntakes(false); setShow(true);
    setEditModules(null);
    api.get(`/intakes/${r.id}/modules`).then(res => setEditModules(res.data.data)).catch(() => setEditModules([]));
  };

  const codeTaken = !editing && !!form.intake_code &&
    rows.some(r => r.intake_code.trim().toLowerCase() === form.intake_code.trim().toLowerCase());

  const handleSave = async e => {
    e.preventDefault();
    if (!form.intake_code || !form.soc_date || !form.eoc_date || form.is_active === '' || form.is_active === null || form.is_active === undefined) { setError('All fields are required.'); return; }
    if (codeTaken) { setError('Intake code already exists.'); return; }
    setSaving(true);
    try {
      editing ? await api.put(`/intakes/${editing.id}`, form) : await api.post('/intakes', form);
      toast(`Intake ${editing ? 'updated' : 'created'}`, 'success');
      setShow(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/intakes/${delId}`); toast('Intake deleted','success'); setDelId(null); load(); }
    catch (err) { toast(err.response?.data?.message||'Delete failed','error'); }
  };

  const openBulkTransfer = r => {
    setBulkTarget(r);
    setBulkForm({ course_code: r.course_code || '', to_intake_code: '', transfer_date: new Date().toISOString().slice(0, 10), attendance_migration: 'from_date' });
    setBulkConfirmText('');
    setBulkErrors({});
    setBulkResult(null);
  };

  const handleBulkTransfer = async () => {
    if (!bulkForm.to_intake_code) {
      setBulkErrors({ to_intake_code: true });
      toast('Please select a destination intake', 'error');
      return;
    }
    if (!bulkForm.transfer_date) {
      setBulkErrors({ transfer_date: true });
      toast('Please select a transfer date', 'error');
      return;
    }
    if (bulkConfirmText.trim() !== bulkTarget.intake_code) {
      setBulkErrors({ confirm: true });
      toast(`Type "${bulkTarget.intake_code}" to confirm`, 'error');
      return;
    }
    setBulkErrors({});
    setBulkBusy(true);
    try {
      const res = await api.post(`/intakes/${bulkTarget.id}/transfer-all`, bulkForm);
      toast(`Transferred ${res.data.moved} student${res.data.moved !== 1 ? 's' : ''} to ${bulkForm.to_intake_code}`, 'success');
      if (res.data.attendanceWarning) toast(res.data.attendanceWarning, 'error');
      setBulkResult({ moved: res.data.moved, skipped: res.data.skipped, notTransferred: res.data.notTransferred || [] });
      load();
      if (res.data.notTransferred?.length) {
        setCapacityLeftover(res.data.notTransferred.map(s => ({
          key: s.student_course_id, name: s.name, detail: `destination '${bulkForm.to_intake_code}' was full`,
          student_no: s.student_no, student_course_id: s.student_course_id,
        })));
        setShowCapacityModal(true);
      }
    } catch (err) {
      toast(err.response?.data?.message || 'Bulk transfer failed', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const assignLeftoverIntake = async (row, course_code, intake_code) => {
    await api.post(`/students/${row.student_no}/courses/${row.student_course_id}/transfer`, {
      to_intake_code: intake_code, transfer_date: new Date().toISOString().slice(0, 10), attendance_migration: 'from_date',
    });
    load();
  };

  const activeCourseOptions = courses.filter(c => c.status === 'Active').map(c => ({ value: c.course_code, label: c.course_name }));
  const today = new Date().toISOString().slice(0, 10);
  const soonCutoff = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const filtered = rows.filter(r =>
    r.intake_code.toLowerCase().includes(search.toLowerCase()) &&
    (!statusFilter || (statusFilter === 'active' ? !!r.is_active : !r.is_active)) &&
    (!courseFilter || r.course_code === courseFilter) &&
    (!timingFilter || (timingFilter === 'starting_soon' && r.soc_date >= today && r.soc_date <= soonCutoff))
  );
  const { sorted: intakeSorted, sortKey: intakeSK, sortDir: intakeSD, toggleSort: intakeSort } = useSort(filtered);
  const { paged: intakePaged, page: intakePage, setPage: setIntakePage, pageSize: intakePageSize, setPageSize: setIntakePageSize, totalPages: intakeTotalPages, total: intakeTotal, start: intakeStart } = usePagination(intakeSorted);

  return (
    <>
      <PageHeader title="Course / Intake Master" subtitle={`${intakeTotal} intakes`}
        actions={<div className="d-flex gap-2">
          {can('intake_master','update') && <button className="sms-btn sms-btn-outline" onClick={openBulkAssign}><i className="bi bi-collection" /> Assign Module</button>}
          {can('intake_master','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Intake</button>}
        </div>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search intake code…"
        onClear={() => { setSearch(''); setStatusFilter(''); setCourseFilter(''); setTimingFilter(''); }}>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        <FilterSelect value={courseFilter} onChange={setCourseFilter} placeholder="All courses" options={activeCourseOptions} />
        <FilterSelect value={timingFilter} onChange={setTimingFilter} placeholder="Any timing"
          options={[{ value: 'starting_soon', label: 'Starting soon (30 days)' }]} />
      </FilterBar>
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-table" message="No intakes found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Course" field="course_name" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} />
                <SortTh label="Intake Code" field="intake_code" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} />
                <SortTh label="SOC" field="soc_date" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} />
                <SortTh label="EOC" field="eoc_date" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} />
                <SortTh label="Min. Attendance" field="min_attendance_rate" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} style={{ textAlign: "right" }} />
                <SortTh label="Min. Assessment" field="min_assessment_result" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} style={{ textAlign: "right" }} />
                <SortTh label="Status" field="is_active" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} />
                <SortTh label="Students" field="student_count" sortKey={intakeSK} sortDir={intakeSD} onSort={intakeSort} />
                {(can('intake_master','update')||can('intake_master','delete')) && <th style={{ width: 120 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={9 + ((can('intake_master','update')||can('intake_master','delete')) ? 1 : 0)} /> : intakePaged.map((r, idx) => (
                  <tr key={r.id} onClick={() => setViewing(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{intakeStart + idx + 1}</td>
                    <td>{r.course_name || '—'}</td>
                    <td style={{ fontWeight: 500 }}>{r.intake_code}</td>
                    <td>{fmtDate(r.soc_date)}</td>
                    <td>{fmtDate(r.eoc_date)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.min_attendance_rate != null ? `${fmtPct(r.min_attendance_rate)}` : '—'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.min_assessment_result != null ? `${fmtPct(r.min_assessment_result)}` : '—'}</td>
                    <td><span className={`badge rounded-pill ${r.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>{r.student_count}</td>
                    {(can('intake_master','update')||can('intake_master','delete')) && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          {can('intake_master','update') && <button className="sms-btn sms-btn-outline-info" style={{ padding: '4px 8px' }} title="Edit intake" onClick={() => openEdit(r)}><i className="bi bi-pencil" /></button>}
                          {can('intake_master','update') && (
                            <button className="sms-btn sms-btn-outline-warning" style={{ padding: '4px 8px' }} title="Add or remove modules" onClick={() => openModules(r)}>
                              <i className="bi bi-collection" />
                            </button>
                          )}
                          {can('intake_master','update') && r.student_count > 0 && (
                            <button className="sms-btn sms-btn-outline-success" style={{ padding: '4px 8px' }} title="Transfer all students to another intake" onClick={() => openBulkTransfer(r)}>
                              <i className="bi bi-arrow-left-right" />
                            </button>
                          )}
                          {can('intake_master','delete') && <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 8px' }} title="Delete intake" onClick={() => setDelId(r.id)}><i className="bi bi-trash" /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={intakePage} totalPages={intakeTotalPages} total={intakeTotal} pageSize={intakePageSize} setPage={setIntakePage} setPageSize={setIntakePageSize} start={intakeStart} />}
          </>
        )}
      </div>
      <Modal show={show} onClose={() => setShow(false)} title={editing ? 'Edit Intake' : 'Add Intake'}>
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <FormField id="crs" label="Course" value={form.course_code || ''} onChange={e => { setForm(f=>({...f,course_code:e.target.value})); setShowCourseIntakes(false); }}
              options={courses.filter(c => c.status === 'Active' || c.course_code === form.course_code).map(c => ({ value: c.course_code, label: c.course_name }))} />
            <div className="col-12 col-sm-6">
              <label htmlFor="ic" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake Code *</label>
              <input id="ic" type="text" className="sms-form-control" value={form.intake_code || ''} onChange={e => setForm(f=>({...f,intake_code:e.target.value}))}
                disabled={!!(editing && editing.student_count > 0)} />
              {editing && editing.student_count > 0 && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>Cannot change — {editing.student_count} student{editing.student_count !== 1 ? 's' : ''} enrolled</div>}
              {codeTaken && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 4 }}>This intake code already exists.</div>}
            </div>
            <FormField id="soc" label="Start of Course (SOC)" type="date" value={form.soc_date} onChange={e => setForm(f=>({...f,soc_date:e.target.value}))} required />
            <FormField id="eoc" label="End of Course (EOC)" type="date" value={form.eoc_date} onChange={e => setForm(f=>({...f,eoc_date:e.target.value}))} required />
            <FormField id="cap" label="Capacity" type="number" value={form.capacity ?? 50} onChange={e => setForm(f=>({...f,capacity:Number(e.target.value)}))} required />
            <FormField id="st" label="Status" value={form.is_active ?? 1} onChange={e => setForm(f=>({...f,is_active:Number(e.target.value)}))} options={[{value:1,label:'Active'},{value:0,label:'Inactive'}]} required />
            <FormField id="mar" label="Minimum Attendance Rate (%)" type="number" value={form.min_attendance_rate ?? ''}
              onChange={e => setForm(f=>({...f,min_attendance_rate:e.target.value === '' ? '' : Math.trunc(Number(e.target.value))}))}
              hint={<><span>Required rate from SOC to EOC. </span><Link to={`/students/attendance?intake_code=${encodeURIComponent(form.intake_code || '')}`}>Check attendance</Link></>} />
            <FormField id="masr" label="Minimum Assessment Result (%)" type="number" value={form.min_assessment_result ?? ''}
              onChange={e => setForm(f=>({...f,min_assessment_result:e.target.value === '' ? '' : Math.trunc(Number(e.target.value))}))}
              hint={<><span>Minimum score required. </span><Link to="/students/academic?tab=assessment">Check assessments</Link></>} />
          </div>
          {editing && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', marginBottom: 6 }}>
                Modules ({editModules?.length ?? 0})
              </div>
              {editModules === null ? (
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>Loading…</div>
              ) : editModules.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>No modules assigned yet. Use the <i className="bi bi-collection" /> action on the intake row to add some.</div>
              ) : (
                <div className="d-flex flex-column gap-1" style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {editModules.map(m => (
                    <div key={m.module_code} className="d-flex align-items-center justify-content-between gap-2" style={{
                      border: '1px solid var(--sms-bdr)', borderRadius: 8, padding: '6px 10px',
                    }}>
                      <span>
                        <span style={{ fontSize: 12.5, fontWeight: 500 }}>{m.module_name}</span>
                        <span style={{ fontSize: 12, color: 'var(--sms-txt3)', marginLeft: 6 }}>{m.module_code} · {m.language} · {fmtPct(m.exam_weightage)}</span>
                        {(m.assessment_start_date || m.assessment_end_date) && (
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--sms-txt2)', marginTop: 2 }}>
                            Assessment: {fmtDate(m.assessment_start_date)} – {fmtDate(m.assessment_end_date)}
                          </span>
                        )}
                      </span>
                      <span className={`badge rounded-pill ${m.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{m.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {form.course_code && (() => {
            const courseIntakes = rows.filter(r => r.course_code === form.course_code && r.id !== editing?.id);
            return (
              <div className="sms-card d-none" style={{ marginTop: 14, padding: '10px 12px', background: 'var(--sms-bg2)' }}>
                {courseIntakes.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>No existing intakes for this course yet — this will be the first.</div>
                ) : (
                  <>
                    <button type="button" onClick={() => setShowCourseIntakes(v => !v)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, width: '100%', fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', cursor: 'pointer' }}>
                      <i className={`bi bi-chevron-${showCourseIntakes ? 'down' : 'right'}`} style={{ fontSize: 12 }} />
                      {showCourseIntakes ? 'Hide' : 'Show'} existing intakes for this course ({courseIntakes.length})
                    </button>
                    {showCourseIntakes && (
                      <div className="d-flex flex-column gap-1" style={{ marginTop: 8, maxHeight: 180, overflowY: 'auto' }}>
                        {courseIntakes.map(ci => (
                          <div key={ci.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--sms-txt2)' }}>
                            <span>{ci.intake_code}</span>
                            <span style={{ color: 'var(--sms-txt3)' }}>{fmtDate(ci.soc_date)} – {fmtDate(ci.eoc_date)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
          <SaveButtons saving={saving} disabled={codeTaken} onCancel={() => setShow(false)} label={editing ? 'Update' : 'Create'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this intake?" onConfirm={handleDelete} onCancel={() => setDelId(null)} />
      <DetailModal show={!!viewing} onClose={() => setViewing(null)} title="Intake Details" fields={viewing ? [
        { label: 'Intake Code',      value: viewing.intake_code },
        { label: 'Course',           value: viewing.course_name },
        { label: 'Intake Type',      value: viewing.intake_type },
        { label: 'Start of Course',  value: fmtDate(viewing.soc_date) },
        { label: 'End of Course',    value: fmtDate(viewing.eoc_date) },
        { label: 'Capacity',         value: viewing.capacity },
        { label: 'Minimum Attendance Rate', value: <>
          <div>{viewing.min_attendance_rate != null ? `${fmtPct(viewing.min_attendance_rate)}` : '—'}</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 3 }}>
            Required rate from SOC to EOC. <Link to={`/students/attendance?intake_code=${encodeURIComponent(viewing.intake_code || '')}`}>Check attendance</Link>
          </div>
        </> },
        { label: 'Minimum Assessment Result', value: <>
          <div>{viewing.min_assessment_result != null ? `${fmtPct(viewing.min_assessment_result)}` : '—'}</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 3 }}>
            Minimum score required. <Link to="/students/academic?tab=assessment">Check assessments</Link>
          </div>
        </> },
        { label: 'Status',           value: <span className={`badge rounded-pill ${viewing.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{viewing.is_active ? 'Active' : 'Inactive'}</span> },
        { label: 'Students Enrolled',value: viewing.student_count },
        { label: 'Created By',       value: viewing.created_by_name },
        { label: 'Created At',       value: fmtDateTime(viewing.created_at) },
      ] : []} />

      <Modal show={!!bulkTarget} size="md" title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>Transfer All Students</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 2 }}>
            Moves every active student out of {bulkTarget?.intake_code} and updates their status
          </div>
        </div>
      } onClose={() => setBulkTarget(null)}>
        {bulkTarget && (bulkResult ? (
          <div className="d-flex flex-column gap-3">
            <div style={{ fontSize: 13 }}>
              <i className="bi bi-check-circle-fill" style={{ color: 'var(--sms-btn-success-bg)', marginRight: 6 }} />
              Transferred <b>{bulkResult.moved}</b> student{bulkResult.moved !== 1 ? 's' : ''} to <b>{bulkForm.to_intake_code}</b>.
            </div>
            {bulkResult.skipped.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', marginBottom: 6 }}>
                  Skipped ({bulkResult.skipped.length}) — already enrolled in {bulkForm.to_intake_code}:
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--sms-bdr)', borderRadius: 8 }}>
                  {bulkResult.skipped.map(s => (
                    <div key={s.student_no} style={{ padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid var(--sms-bdr)' }}>
                      {s.name} <span style={{ color: 'var(--sms-txt3)' }}>({s.student_no})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {bulkResult.notTransferred.length > 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--sms-txt2)' }}>
                <i className="bi bi-exclamation-triangle-fill" style={{ color: 'var(--sms-btn-outline-danger-txt)', marginRight: 6 }} />
                {bulkResult.notTransferred.length} student{bulkResult.notTransferred.length !== 1 ? 's' : ''} left behind — {bulkForm.to_intake_code} ran out of seats. Assign them a different intake below.
              </div>
            )}
            <div className="d-flex justify-content-end">
              <button type="button" className="sms-btn sms-btn-primary" onClick={() => setBulkTarget(null)}>Done</button>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-start gap-2">
              <div style={{ flex: 1, minWidth: 0, border: '1px solid var(--sms-bdr)', borderRadius: 8, padding: '8px 10px', background: 'var(--sms-sur2)' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase' }}>From</div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{bulkTarget.intake_code}</div>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 2 }}>
                  {bulkTarget.student_count} active student{bulkTarget.student_count !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="d-flex justify-content-center align-items-center">
                <i className="bi bi-arrow-down d-sm-none" style={{ color: 'var(--sms-txt3)' }} />
                <i className="bi bi-arrow-right d-none d-sm-block" style={{ color: 'var(--sms-txt3)', marginTop: 10 }} />
              </div>
              <div style={{ flex: 1.4, minWidth: 0, borderRadius: 8, padding: '8px 10px', border: '1px solid var(--sms-bdr)' }}>
                <div style={{ fontSize: 12, color: 'var(--sms-txt3)', textTransform: 'uppercase' }}>Course</div>
                <SearchableSelect value={bulkForm.course_code}
                  onChange={e => { setBulkForm(f => ({ ...f, course_code: e.target.value, to_intake_code: '' })); setBulkErrors({}); }}
                  placeholder="-- Select course --"
                  options={activeCourseOptions} />
              </div>
              <div className="d-flex justify-content-center align-items-center">
                <i className="bi bi-arrow-down d-sm-none" style={{ color: 'var(--sms-txt3)' }} />
                <i className="bi bi-arrow-right d-none d-sm-block" style={{ color: 'var(--sms-txt3)', marginTop: 10 }} />
              </div>
              <div style={{
                flex: 1, minWidth: 0, borderRadius: 8, padding: '8px 10px',
                border: `1px solid ${bulkErrors.to_intake_code ? 'var(--sms-btn-outline-danger-txt)' : 'var(--sms-acc)'}`,
                background: 'var(--sms-acc-bg)',
              }}>
                <div style={{ fontSize: 12, color: bulkErrors.to_intake_code ? 'var(--sms-btn-outline-danger-txt)' : 'var(--sms-acc)', textTransform: 'uppercase' }}>To</div>
                <SearchableSelect value={bulkForm.to_intake_code}
                  onChange={e => { setBulkForm(f => ({ ...f, to_intake_code: e.target.value })); setBulkErrors({}); }}
                  placeholder={bulkForm.course_code ? '-- Select intake --' : '-- Select course first --'}
                  disabled={!bulkForm.course_code}
                  options={intakeOptionsFor(rows, bulkForm.course_code, { excludeIntakeCode: bulkTarget.intake_code, dateRange: (soc, eoc) => `${fmtDate(soc)} – ${fmtDate(eoc)}` })} />
              </div>
            </div>
            {bulkErrors.to_intake_code && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: -8 }}>Please select a destination intake.</div>}

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Transfer date</label>
              <input type="date" className="sms-form-control" style={{ fontSize: 13 }}
                max={new Date().toISOString().slice(0, 10)}
                value={bulkForm.transfer_date} onChange={e => setBulkForm(f => ({ ...f, transfer_date: e.target.value }))} />
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Attendance</label>
              <div className="d-flex flex-column gap-2">
                {[
                  { value: 'from_date', title: `Move sessions from ${bulkForm.transfer_date ? fmtDate(bulkForm.transfer_date) : 'transfer date'} onward`, sub: 'Recommended. Earlier sessions stay recorded under the old intake as history.' },
                  { value: 'all',       title: 'Move all attendance history', sub: `Every session record for these students moves to ${bulkForm.to_intake_code || 'the new intake'}, including past ones.` },
                  { value: 'none',      title: `Leave attendance with ${bulkTarget.intake_code}`, sub: 'No records move. Only the enrolments themselves are transferred.' },
                ].map(opt => (
                  <label key={opt.value} className="d-flex align-items-start gap-2" style={{
                    border: `1.5px solid ${bulkForm.attendance_migration === opt.value ? 'var(--sms-acc)' : 'var(--sms-bdr)'}`,
                    background: bulkForm.attendance_migration === opt.value ? 'var(--sms-acc-bg)' : 'transparent',
                    borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
                  }}>
                    <input type="radio" name="bulk_attendance_migration" style={{ marginTop: 3 }}
                      checked={bulkForm.attendance_migration === opt.value}
                      onChange={() => setBulkForm(f => ({ ...f, attendance_migration: opt.value }))} />
                    <span>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600 }}>{opt.title}</span>
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--sms-txt3)', lineHeight: 1.4 }}>{opt.sub}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>
                Type <span style={{  }}>{bulkTarget.intake_code}</span> to confirm
              </label>
              <input type="text" className="sms-form-control" style={{ fontSize: 13, borderColor: bulkErrors.confirm ? 'var(--sms-btn-outline-danger-txt)' : undefined }}
                value={bulkConfirmText} onChange={e => { setBulkConfirmText(e.target.value); setBulkErrors({}); }}
                placeholder={bulkTarget.intake_code} />
              {bulkErrors.confirm && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 4 }}>Intake code doesn't match.</div>}
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setBulkTarget(null)}>Cancel</button>
              <button type="button" className="sms-btn sms-btn-primary"
                disabled={bulkBusy || !bulkForm.to_intake_code || !bulkForm.transfer_date || bulkConfirmText.trim() !== bulkTarget.intake_code}
                onClick={handleBulkTransfer}>Confirm Transfer</button>
            </div>
          </div>
        ))}
      </Modal>

      <CapacityResolveModal show={showCapacityModal} onClose={()=>setShowCapacityModal(false)}
        title="Assign remaining students" rows={capacityLeftover}
        onResolved={key => setCapacityLeftover(rowsLeft => rowsLeft.filter(r => r.key !== key))}
        courses={activeCourseOptions.map(c => ({ course_code: c.value, course_name: c.label }))}
        intakesForCourse={courseCode => intakeOptionsFor(rows, courseCode, { dateRange: (soc, eoc) => `${fmtDate(soc)} – ${fmtDate(eoc)}` })}
        onAssign={assignLeftoverIntake} />

      <Modal show={!!modulesTarget} size="md" title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>Modules — {modulesTarget?.intake_code}</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 2 }}>Check the modules assigned to this intake</div>
        </div>
      } onClose={() => setModulesTarget(null)}>
        {modulesTarget && (() => {
          // Only active modules can be newly added; a module already assigned stays
          // visible (so it can still be unchecked/removed) even if since deactivated.
          const selectable = modules.filter(m => m.is_active || moduleSelection.includes(m.module_code));
          const visible = selectable.filter(m =>
            m.module_code.toLowerCase().includes(moduleSearch.toLowerCase()) ||
            m.module_name.toLowerCase().includes(moduleSearch.toLowerCase())
          );
          return (
          <div className="d-flex flex-column gap-2">
            {selectable.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--sms-txt3)' }}>No active modules exist yet — add one in Module Master first.</div>
            ) : (
              <>
              {selectable.length > 6 && (
                <input className="sms-form-control mb-1" style={{ fontSize: 13 }} placeholder="Search module code or name…"
                  value={moduleSearch} onChange={e => setModuleSearch(e.target.value)} />
              )}
              <div className="d-flex flex-column gap-1" style={{ maxHeight: 320, overflowY: 'auto' }}>
                {visible.map(m => {
                  const checked = moduleSelection.includes(m.module_code);
                  const dates = moduleDates[m.module_code] || {};
                  return (
                  <div key={m.module_code} className={`aperiod-row${checked ? ' checked' : ''}`}>
                    <label className="d-flex align-items-center justify-content-between gap-2 flex-wrap" style={{ cursor: 'pointer' }}>
                      <span className="d-flex align-items-center gap-2">
                        <input type="checkbox" checked={checked} onChange={() => toggleModuleSelection(m.module_code)} />
                        <span>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{m.module_name}</span>
                          <span style={{ display: 'block', fontSize: 12, color: 'var(--aperiod-muted, var(--sms-txt3))' }}>{m.module_code} · {m.language} · {fmtPct(m.exam_weightage)}</span>
                        </span>
                      </span>
                      <span className={`badge rounded-pill ${m.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{m.is_active ? 'Active' : 'Inactive'}</span>
                    </label>
                    {checked && (
                      <div className="aperiod-row-dates" style={{ marginTop: 8, paddingLeft: 26 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--aperiod-muted, var(--sms-txt2))', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                          <i className="bi bi-calendar-range" style={{ color: 'var(--sms-stat-cyan)' }} /> Assessment period
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <input type="date" className="sms-form-control" style={{ fontSize: 12, padding: '5px 8px' }}
                            value={dates.assessment_start_date || ''}
                            onChange={e => setModuleDate(m.module_code, 'assessment_start_date', e.target.value)} />
                          <span style={{ fontSize: 12, color: 'var(--aperiod-muted, var(--sms-txt3))' }}>–</span>
                          <input type="date" className="sms-form-control" style={{ fontSize: 12, padding: '5px 8px' }}
                            value={dates.assessment_end_date || ''}
                            onChange={e => setModuleDate(m.module_code, 'assessment_end_date', e.target.value)} />
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                {visible.length === 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--sms-txt3)', padding: '8px 2px' }}>No modules match "{moduleSearch}".</div>
                )}
              </div>
              </>
            )}
            <div className="d-flex justify-content-end gap-2 mt-2">
              <button type="button" className="sms-btn sms-btn-outline" onClick={() => setModulesTarget(null)}>Cancel</button>
              <button type="button" className="sms-btn sms-btn-primary" disabled={modulesBusy || selectable.length === 0}
                title={selectable.length === 0 ? 'Add a module in Module Master first' : undefined} onClick={handleSaveModules}>
                {modulesBusy ? <><InlineSpinner style={{ marginRight: 4 }} />Saving…</> : 'Save'}
              </button>
            </div>
          </div>
          );
        })()}
      </Modal>

      <Modal show={bulkAssign} size="md" title={
        <div>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>Assign Modules to Intakes</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--sms-txt3)', marginTop: 2 }}>Adds the selected modules to every intake selected below</div>
        </div>
      } onClose={() => setBulkAssign(false)}>
        {bulkAssignResult ? (
          <div className="d-flex flex-column gap-3">
            {bulkAssignResult.map(r => (
              <div key={r.module_code}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{r.module_name} <span style={{ fontWeight: 400, color: 'var(--sms-txt3)', fontSize: 12 }}>({r.module_code})</span></div>
                <div style={{ fontSize: 12.5, color: 'var(--sms-txt2)', marginBottom: 6 }}>
                  <i className="bi bi-check-circle-fill" style={{ color: 'var(--sms-btn-success-bg)', marginRight: 6 }} />
                  Assigned to <b>{r.added.length}</b> new intake{r.added.length !== 1 ? 's' : ''}
                  {r.alreadyAssigned.length > 0 && <> — <b>{r.alreadyAssigned.length}</b> already had it</>}
                </div>
                {r.added.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mb-1">
                    {r.added.map(code => (
                      <span key={code} className="badge rounded-pill badge-active" style={{ fontSize: 12 }}>{code}</span>
                    ))}
                  </div>
                )}
                {r.alreadyAssigned.length > 0 && (
                  <div className="d-flex flex-wrap gap-1">
                    {r.alreadyAssigned.map(code => (
                      <span key={code} className="badge rounded-pill badge-inactive" style={{ fontSize: 12 }} title="Already assigned — skipped">{code}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="d-flex justify-content-end">
              <button type="button" className="sms-btn sms-btn-primary" onClick={() => setBulkAssign(false)}>Done</button>
            </div>
          </div>
        ) : (
        <div className="d-flex flex-column gap-3">
          <div>
            <div className="aperiod-section-head">
              <span className="aperiod-icon" style={{ background: 'color-mix(in srgb, var(--sms-stat-violet) 14%, transparent)', color: 'var(--sms-stat-violet)' }}>
                <i className="bi bi-collection" />
              </span>
              <span className="aperiod-section-title">Modules ({bulkAssignModules.length} selected)</span>
            </div>
            <SearchableSelect value="" onChange={e => { const code = e.target.value; if (code) toggleBulkAssignModuleSel(code); }}
              placeholder="Search and select a module to add…"
              options={modules.filter(m => m.is_active && !bulkAssignModules.includes(m.module_code))
                .map(m => ({ value: m.module_code, label: `${m.module_name} (${m.module_code})` }))} />
            {bulkAssignModules.length > 0 && (
              <div className="d-flex flex-wrap gap-1" style={{ marginTop: 8 }}>
                {bulkAssignModules.map(code => {
                  const m = modules.find(x => x.module_code === code);
                  return (
                    <span key={code} className="badge rounded-pill badge-graduated d-flex align-items-center gap-1" style={{ fontSize: 12, padding: '5px 6px 5px 10px' }}>
                      {m ? m.module_name : code}
                      <button type="button" onClick={() => toggleBulkAssignModuleSel(code)}
                        style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, color: 'inherit', cursor: 'pointer' }}
                        aria-label={`Remove ${m ? m.module_name : code}`}>
                        <i className="bi bi-x-lg" style={{ fontSize: 12 }} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <div className="aperiod-panel">
            <div className="aperiod-head">
              <span className="aperiod-icon" style={{ background: 'color-mix(in srgb, var(--sms-stat-cyan) 14%, transparent)', color: 'var(--sms-stat-cyan)' }}>
                <i className="bi bi-calendar-range" />
              </span>
              <div>
                <div className="aperiod-title">Assessment period</div>
                <div className="aperiod-sub">Required for every selected {bulkAssignDateMode === 'per_module' ? 'module' : 'intake'}</div>
              </div>
              <div className="sched-view-toggle" role="tablist" aria-label="Assessment period mode" style={{ marginLeft: 'auto' }}>
                <button type="button" role="tab" aria-selected={bulkAssignDateMode === 'same'}
                  className={`sched-view-btn${bulkAssignDateMode === 'same' ? ' active' : ''}`}
                  onClick={() => setBulkAssignDateMode('same')}>
                  Same for every intake
                </button>
                <button type="button" role="tab" aria-selected={bulkAssignDateMode === 'per_intake'}
                  className={`sched-view-btn${bulkAssignDateMode === 'per_intake' ? ' active' : ''}`}
                  onClick={() => setBulkAssignDateMode('per_intake')}>
                  Set per intake
                </button>
                <button type="button" role="tab" aria-selected={bulkAssignDateMode === 'per_module'}
                  className={`sched-view-btn${bulkAssignDateMode === 'per_module' ? ' active' : ''}`}
                  onClick={() => setBulkAssignDateMode('per_module')}>
                  Set per module
                </button>
              </div>
            </div>
            {bulkAssignDateMode === 'same' ? (
              <div className="aperiod-row-dates">
                <DateRangePicker startDate={bulkAssignSharedDates.assessment_start_date} endDate={bulkAssignSharedDates.assessment_end_date}
                  onChange={({ start, end }) => setBulkAssignSharedDates({ assessment_start_date: start || '', assessment_end_date: end || '' })} />
              </div>
            ) : bulkAssignDateMode === 'per_intake' ? (
              <div className="aperiod-row-dates" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                <i className="bi bi-arrow-down-short" /> Set below, per selected intake.
              </div>
            ) : bulkAssignModules.length === 0 ? (
              <div className="aperiod-row-dates" style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>
                Select modules above first.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {bulkAssignModules.map(code => {
                  const m = modules.find(x => x.module_code === code);
                  const dates = bulkAssignModuleDates[code] || {};
                  return (
                    <div key={code} className="d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 600, minWidth: 90, flex: '0 0 auto' }}>{m ? m.module_name : code}</span>
                      <input type="date" className="sms-form-control" style={{ fontSize: 12, padding: '5px 8px', borderColor: dates.assessment_start_date ? undefined : 'var(--sms-stat-red)' }}
                        value={dates.assessment_start_date || ''}
                        onChange={e => setBulkAssignModuleDate(code, 'assessment_start_date', e.target.value)} />
                      <span style={{ fontSize: 12, color: 'var(--sms-txt3)', flex: '0 0 auto' }}>–</span>
                      <input type="date" className="sms-form-control" style={{ fontSize: 12, padding: '5px 8px', borderColor: dates.assessment_end_date ? undefined : 'var(--sms-stat-red)' }}
                        value={dates.assessment_end_date || ''}
                        onChange={e => setBulkAssignModuleDate(code, 'assessment_end_date', e.target.value)} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <div className="aperiod-section-head">
              <span className="aperiod-icon" style={{ background: 'color-mix(in srgb, var(--sms-stat-blue) 14%, transparent)', color: 'var(--sms-stat-blue)' }}>
                <i className="bi bi-mortarboard" />
              </span>
              <span className="aperiod-section-title">Intakes ({bulkAssignIntakes.length} selected)</span>
            </div>
            <input className="sms-form-control mb-2" style={{ fontSize: 13 }} placeholder="Search intake code…"
              value={bulkAssignSearch} onChange={e => setBulkAssignSearch(e.target.value)} />
            <div className="d-flex flex-column gap-1" style={{ maxHeight: 240, overflowY: 'auto' }}>
              {rows.filter(r => r.intake_code.toLowerCase().includes(bulkAssignSearch.toLowerCase())).map(r => {
                const checked = bulkAssignIntakes.includes(r.id);
                const dates = bulkAssignIntakeDates[r.id] || {};
                return (
                <div key={r.id} className={`aperiod-row${checked ? ' checked' : ''}`}>
                  <label className="d-flex align-items-center gap-2 flex-wrap" style={{ cursor: 'pointer', fontSize: 12.5 }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleBulkAssignIntake(r.id)} />
                    <span style={{ fontWeight: 500 }}>{r.intake_code}</span>
                    <span style={{ color: 'var(--aperiod-muted, var(--sms-txt3))' }}>{r.course_name || '—'}</span>
                    {checked && <i className="bi bi-check-circle-fill" style={{ color: 'var(--sms-acc)', marginLeft: 'auto' }} />}
                  </label>
                  {checked && bulkAssignDateMode === 'per_intake' && (
                    <div className="aperiod-row-dates d-flex align-items-center gap-2" style={{ marginTop: 6, paddingLeft: 24 }}>
                      <input type="date" className="sms-form-control" style={{ fontSize: 12, padding: '5px 8px', borderColor: dates.assessment_start_date ? undefined : 'var(--sms-stat-red)' }}
                        value={dates.assessment_start_date || ''}
                        onChange={e => setBulkAssignIntakeDate(r.id, 'assessment_start_date', e.target.value)} />
                      <span style={{ fontSize: 12, color: 'var(--aperiod-muted, var(--sms-txt3))' }}>–</span>
                      <input type="date" className="sms-form-control" style={{ fontSize: 12, padding: '5px 8px', borderColor: dates.assessment_end_date ? undefined : 'var(--sms-stat-red)' }}
                        value={dates.assessment_end_date || ''}
                        onChange={e => setBulkAssignIntakeDate(r.id, 'assessment_end_date', e.target.value)} />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          {Object.keys(bulkAssignOverlaps).length > 0 && (
            <div className="d-flex flex-column gap-1" style={{
              fontSize: 12, color: 'var(--sms-stat-blue)',
              background: 'color-mix(in srgb, var(--sms-stat-blue) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--sms-stat-blue) 30%, transparent)',
              borderRadius: 8, padding: '8px 12px',
            }}>
              <div className="d-flex align-items-center gap-2" style={{ fontWeight: 600 }}>
                <i className="bi bi-info-circle-fill" /> Already assigned — these will be skipped, untouched:
              </div>
              {Object.entries(bulkAssignOverlaps).map(([code, intakeCodes]) => {
                const m = modules.find(x => x.module_code === code);
                return (
                  <div key={code} style={{ paddingLeft: 22 }}>
                    <b>{m ? m.module_name : code}</b> already on {intakeCodes.join(', ')}
                  </div>
                );
              })}
            </div>
          )}
          {bulkAssignModules.length > 0 && bulkAssignIntakes.length > 0 && bulkAssignDatesIncomplete() && (
            <div className="d-flex align-items-center gap-2" style={{
              fontSize: 12, color: 'var(--sms-stat-amber)',
              background: 'color-mix(in srgb, var(--sms-stat-amber) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--sms-stat-amber) 35%, transparent)',
              borderRadius: 8, padding: '8px 12px',
            }}>
              <i className="bi bi-exclamation-triangle-fill" />
              Set an assessment start and end date for every selected {bulkAssignDateMode === 'per_module' ? 'module' : bulkAssignDateMode === 'per_intake' ? 'intake' : 'range'} — the Assign button unlocks once they're all filled in.
            </div>
          )}
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setBulkAssign(false)}>Cancel</button>
            <button type="button" className="sms-btn sms-btn-primary" disabled={bulkAssignBusy || !bulkAssignModules.length || !bulkAssignIntakes.length}
              onClick={handleBulkAssignModule}>
              {bulkAssignBusy ? <><InlineSpinner style={{ marginRight: 4 }} />Assigning…</> : 'Assign'}
            </button>
          </div>
        </div>
        )}
      </Modal>
    </>
  );
}

// ─── COURSE LEVELS ───────────────────────────────────────────────────────────
const COURSE_LEVEL_EMPTY = { level_code: '', level_name: '', sequence: '', is_active: 1 };

export function CourseLevelsPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(COURSE_LEVEL_EMPTY);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [delId,   setDelId]   = useState(null);
  const [viewing, setViewing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/course-levels').then(r => setRows(r.data.data)).catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm(COURSE_LEVEL_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ level_code: r.level_code, level_name: r.level_name, sequence: r.sequence, is_active: r.is_active }); setError(''); setShow(true); };

  const codeTaken = !editing && !!form.level_code &&
    rows.some(r => r.level_code.trim().toLowerCase() === form.level_code.trim().toLowerCase());

  const handleSave = async e => {
    e.preventDefault();
    if (!form.level_code || !form.level_name || form.sequence === '' || form.sequence === null || form.sequence === undefined) { setError('All fields are required.'); return; }
    if (codeTaken) { setError('Level code already exists.'); return; }
    setSaving(true);
    try {
      editing ? await api.put(`/course-levels/${editing.level_code}`, form) : await api.post('/course-levels', form);
      toast(`Course level ${editing ? 'updated' : 'created'}`, 'success');
      setShow(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/course-levels/${delId}`); toast('Course level deleted','success'); setDelId(null); load(); }
    catch (err) { toast(err.response?.data?.message||'Delete failed','error'); }
  };

  const filtered = rows.filter(r =>
    (r.level_code.toLowerCase().includes(search.toLowerCase()) || r.level_name.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || (statusFilter === 'active' ? !!r.is_active : !r.is_active))
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); };

  return (
    <>
      <PageHeader title="Course Level Master" subtitle={`${total} levels`}
        actions={can('course_level_master','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Level</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search level code or name…" onClear={clearFilters}>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
      </FilterBar>
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-bar-chart-steps" message="No course levels found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Level Code" field="level_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Level Name" field="level_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Sequence" field="sequence" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Status" field="is_active" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {(can('course_level_master','update')||can('course_level_master','delete')) && <th style={{ width: 90 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={5 + ((can('course_level_master','update')||can('course_level_master','delete')) ? 1 : 0)} /> : paged.map((r, idx) => (
                  <tr key={r.level_code} onClick={() => setViewing(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.level_code}</td>
                    <td>{r.level_name}</td>
                    <td>{r.sequence}</td>
                    <td><span className={`badge rounded-pill ${r.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                    {(can('course_level_master','update')||can('course_level_master','delete')) && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          {can('course_level_master','update') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={() => openEdit(r)}><i className="bi bi-pencil" /></button>}
                          {can('course_level_master','delete') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-stat-red)' }} onClick={() => setDelId(r.level_code)}><i className="bi bi-trash" /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>
      <Modal show={show} onClose={() => setShow(false)} title={editing ? 'Edit Course Level' : 'Add Course Level'}>
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label htmlFor="lc" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Level Code *</label>
              <input id="lc" type="text" className="sms-form-control" value={form.level_code || ''} onChange={e => setForm(f=>({...f,level_code:e.target.value}))}
                disabled={!!editing} />
              {editing && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>Level code cannot be changed after creation.</div>}
              {codeTaken && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 4 }}>This level code already exists.</div>}
            </div>
            <FormField id="ln" label="Level Name" value={form.level_name} onChange={e => setForm(f=>({...f,level_name:e.target.value}))} required />
            <FormField id="sq" label="Sequence" type="number" value={form.sequence} onChange={e => setForm(f=>({...f,sequence:e.target.value === '' ? '' : Number(e.target.value)}))} required />
            {editing && (
              <FormField id="st" label="Status" value={form.is_active ?? 1} onChange={e => setForm(f=>({...f,is_active:Number(e.target.value)}))} options={[{value:1,label:'Active'},{value:0,label:'Inactive'}]} required />
            )}
          </div>
          <SaveButtons saving={saving} disabled={codeTaken} onCancel={() => setShow(false)} label={editing ? 'Update' : 'Create'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this course level?" onConfirm={handleDelete} onCancel={() => setDelId(null)} />
      <DetailModal show={!!viewing} onClose={() => setViewing(null)} title="Course Level Details" fields={viewing ? [
        { label: 'Level Code', value: viewing.level_code },
        { label: 'Level Name', value: viewing.level_name },
        { label: 'Sequence',   value: viewing.sequence },
        { label: 'Status',     value: <span className={`badge rounded-pill ${viewing.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{viewing.is_active ? 'Active' : 'Inactive'}</span> },
      ] : []} />
    </>
  );
}

// ─── COURSES ─────────────────────────────────────────────────────────────────
const COURSE_EMPTY = { course_code: '', course_name: '', course_level: '', program_type: '', description: '', require_guiding_language: false, require_course_preview: false, status: 'Active' };

export function CoursesPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,   setRows]   = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading,setLoading]= useState(true);
  const [search, setSearch] = useState('');
  const [show,   setShow]   = useState(false);
  const [editing,setEditing]= useState(null);
  const [form,   setForm]   = useState(COURSE_EMPTY);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [delId,  setDelId]  = useState(null);
  const [viewing,setViewing]= useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter,  setLevelFilter]  = useState('');

  const load = () => {
    setLoading(true);
    api.get('/course-master').then(r => setRows(r.data.data)).catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { api.get('/course-levels').then(r => setLevels(r.data.data)).catch(() => {}); }, []);

  const levelOptions = levels.filter(l => l.is_active).map(l => ({ value: l.level_code, label: l.level_name }));
  const levelName = code => levels.find(l => l.level_code === code)?.level_name || code;

  const openCreate = () => { setEditing(null); setForm(COURSE_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({
    course_code: r.course_code, course_name: r.course_name, course_level: r.course_level || '',
    program_type: r.program_type || '', description: r.description || '',
    require_guiding_language: !!r.require_guiding_language, require_course_preview: !!r.require_course_preview,
    status: r.status || 'Active',
  }); setError(''); setShow(true); };

  const codeTaken = !editing && !!form.course_code &&
    rows.some(r => r.course_code.trim().toLowerCase() === form.course_code.trim().toLowerCase());

  const handleSave = async e => {
    e.preventDefault();
    if (!form.course_code || !form.course_name) { setError('Course code and course name are required.'); return; }
    if (codeTaken) { setError('Course code already exists.'); return; }
    setSaving(true);
    try {
      editing ? await api.put(`/course-master/${editing.course_code}`, form) : await api.post('/course-master', form);
      toast(`Course ${editing ? 'updated' : 'created'}`, 'success');
      setShow(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/course-master/${delId}`); toast('Course deleted','success'); setDelId(null); load(); }
    catch (err) { toast(err.response?.data?.message||'Delete failed','error'); }
  };

  const filtered = rows.filter(r =>
    (r.course_code.toLowerCase().includes(search.toLowerCase()) || r.course_name.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || r.status === statusFilter) &&
    (!levelFilter || r.course_level === levelFilter)
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setLevelFilter(''); };

  return (
    <>
      <PageHeader title="Course Master" subtitle={`${total} courses`}
        actions={can('course_master','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Course</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search course code or name…" onClear={clearFilters}>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }]} />
        <FilterSelect value={levelFilter} onChange={setLevelFilter} placeholder="All levels" options={levelOptions} />
      </FilterBar>
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-journal-bookmark" message="No courses found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Course Code" field="course_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Course Name" field="course_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Level" field="course_level" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Program Type" field="program_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {(can('course_master','update')||can('course_master','delete')) && <th style={{ width: 90 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={6 + ((can('course_master','update')||can('course_master','delete')) ? 1 : 0)} /> : paged.map((r, idx) => (
                  <tr key={r.course_code} onClick={() => setViewing(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.course_code}</td>
                    <td>{r.course_name}</td>
                    <td>{r.course_level ? levelName(r.course_level) : '—'}</td>
                    <td>{r.program_type || '—'}</td>
                    <td><span className={`badge rounded-pill ${r.status === 'Active' ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{r.status}</span></td>
                    {(can('course_master','update')||can('course_master','delete')) && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          {can('course_master','update') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={() => openEdit(r)}><i className="bi bi-pencil" /></button>}
                          {can('course_master','delete') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-stat-red)' }} onClick={() => setDelId(r.course_code)}><i className="bi bi-trash" /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>
      <Modal show={show} onClose={() => setShow(false)} title={editing ? 'Edit Course' : 'Add Course'}>
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label htmlFor="cc" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Course Code *</label>
              <input id="cc" type="text" className="sms-form-control" value={form.course_code || ''} onChange={e => setForm(f=>({...f,course_code:e.target.value}))}
                disabled={!!editing} />
              {editing && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>Course code cannot be changed after creation.</div>}
              {codeTaken && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 4 }}>This course code already exists.</div>}
            </div>
            <FormField id="cn" label="Course Name" value={form.course_name} onChange={e => setForm(f=>({...f,course_name:e.target.value}))} required />
            <FormField id="cl" label="Course Level" value={form.course_level} onChange={e => setForm(f=>({...f,course_level:e.target.value}))} options={levelOptions} />
            <FormField id="pt" label="Program Type" value={form.program_type} onChange={e => setForm(f=>({...f,program_type:e.target.value}))} />
            <FormField id="ds" label="Description" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} textarea />
            {editing && (
              <FormField id="st" label="Status" value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))} options={[{value:'Active',label:'Active'},{value:'Inactive',label:'Inactive'}]} required />
            )}
            <div className="col-12 d-flex flex-column gap-2">
              <label className="d-flex align-items-center gap-2" style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>
                <input type="checkbox" checked={form.require_guiding_language} onChange={e => setForm(f=>({...f,require_guiding_language:e.target.checked}))} />
                Requires guiding language
              </label>
              <label className="d-flex align-items-center gap-2" style={{ fontSize: 13, color: 'var(--sms-txt2)' }}>
                <input type="checkbox" checked={form.require_course_preview} onChange={e => setForm(f=>({...f,require_course_preview:e.target.checked}))} />
                Requires course preview
              </label>
            </div>
          </div>
          <SaveButtons saving={saving} disabled={codeTaken} onCancel={() => setShow(false)} label={editing ? 'Update' : 'Create'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this course?" onConfirm={handleDelete} onCancel={() => setDelId(null)} />
      <DetailModal show={!!viewing} onClose={() => setViewing(null)} title="Course Details" fields={viewing ? [
        { label: 'Course Code',   value: viewing.course_code },
        { label: 'Course Name',   value: viewing.course_name },
        { label: 'Course Level',  value: viewing.course_level ? levelName(viewing.course_level) : '—' },
        { label: 'Program Type',  value: viewing.program_type || '—' },
        { label: 'Description',   value: viewing.description || '—' },
        { label: 'Guiding Language Required', value: viewing.require_guiding_language ? 'Yes' : 'No' },
        { label: 'Course Preview Required',   value: viewing.require_course_preview ? 'Yes' : 'No' },
        { label: 'Status',        value: <span className={`badge rounded-pill ${viewing.status === 'Active' ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{viewing.status}</span> },
      ] : []} />
    </>
  );
}

// ─── MODULES ─────────────────────────────────────────────────────────────────
const MODULE_EMPTY = { module_code: '', module_name: '', exam_weightage: '', pass_threshold: 50, scheme_id: '', language: '', is_active: 1 };

export function ModulesPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,    setRows]    = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(MODULE_EMPTY);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [delId,   setDelId]   = useState(null);
  const [viewing, setViewing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [langFilter,   setLangFilter]   = useState('');
  const [schemeTarget, setSchemeTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/modules').then(r => setRows(r.data.data)).catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };
  useEffect(load, []);
  const loadSchemes = () => {
    api.get('/component-master/list').then(r => setSchemes(r.data.data)).catch(() => {});
  };
  useEffect(() => { loadSchemes(); }, []);
  const schemeName = id => schemes.find(s => s.id === id)?.name;
  const canSetupScheme = can('module_master', 'update') && can('component_master', 'update');

  const openCreate = () => { setEditing(null); setForm(MODULE_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ module_code: r.module_code, module_name: r.module_name, exam_weightage: Math.round(Number(r.exam_weightage || 0)), pass_threshold: Math.round(Number(r.pass_threshold ?? 50)), scheme_id: r.scheme_id || '', language: r.language || '', is_active: r.is_active }); setError(''); setShow(true); };

  const codeTaken = !editing && !!form.module_code &&
    rows.some(r => r.module_code.trim().toLowerCase() === form.module_code.trim().toLowerCase());

  const handleSave = async e => {
    e.preventDefault();
    if (!form.module_code || !form.module_name || !form.language || form.exam_weightage === '' || form.exam_weightage === null || form.exam_weightage === undefined) { setError('All fields are required.'); return; }
    if (form.pass_threshold === '' || form.pass_threshold === null || form.pass_threshold === undefined || Number.isNaN(Number(form.pass_threshold)) || form.pass_threshold < 0 || form.pass_threshold > 100) {
      setError('Pass threshold must be a number between 0 and 100.'); return;
    }
    if (codeTaken) { setError('Module code already exists.'); return; }
    setSaving(true);
    try {
      editing ? await api.put(`/modules/${editing.module_code}`, form) : await api.post('/modules', form);
      toast(`Module ${editing ? 'updated' : 'created'}`, 'success');
      setShow(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/modules/${delId}`); toast('Module deleted','success'); setDelId(null); load(); }
    catch (err) { toast(err.response?.data?.message||'Delete failed','error'); }
  };

  const langOptions = [...new Set(rows.map(r => r.language).filter(Boolean))].sort().map(l => ({ value: l, label: l }));
  const filtered = rows.filter(r =>
    (r.module_code.toLowerCase().includes(search.toLowerCase()) || r.module_name.toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || (statusFilter === 'active' ? !!r.is_active : !r.is_active)) &&
    (!langFilter || r.language === langFilter)
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setLangFilter(''); };

  return (
    <>
      <PageHeader title="Module Master" subtitle={`${total} modules`}
        actions={can('module_master','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Module</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search module code or name…" onClear={clearFilters}>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        <FilterSelect value={langFilter} onChange={setLangFilter} placeholder="All languages" options={langOptions} />
      </FilterBar>
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-collection" message="No modules found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Module Code" field="module_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Module Name" field="module_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Exam Weightage" field="exam_weightage" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Pass Threshold" field="pass_threshold" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Marking Scheme</th>
                <SortTh label="Language" field="language" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Status" field="is_active" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                {(can('module_master','update')||can('module_master','delete')) && <th style={{ width: 180 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={8 + ((can('module_master','update')||can('module_master','delete')) ? 1 : 0)} /> : paged.map((r, idx) => (
                  <tr key={r.module_code} onClick={() => setViewing(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.module_code}</td>
                    <td>{r.module_name}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(Math.round(r.exam_weightage))}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(Math.round(r.pass_threshold ?? 50))}</td>
                    <td>{r.scheme_id ? (schemeName(r.scheme_id) || '—') : <span style={{ color: 'var(--sms-txt3)' }}>Not assigned</span>}</td>
                    <td>{r.language || '—'}</td>
                    <td><span className={`badge rounded-pill ${r.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                    {(can('module_master','update')||can('module_master','delete')) && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          {canSetupScheme && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', fontSize: 12 }} title="Assign a Component Master scheme to this module"
                            onClick={() => setSchemeTarget(r)}><i className="bi bi-link-45deg" /> Assign Scheme</button>}
                          {can('module_master','update') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={() => openEdit(r)}><i className="bi bi-pencil" /></button>}
                          {can('module_master','delete') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-stat-red)' }} onClick={() => setDelId(r.module_code)}><i className="bi bi-trash" /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>
      <Modal show={show} onClose={() => setShow(false)} title={editing ? 'Edit Module' : 'Add Module'}>
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label htmlFor="mc" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module Code *</label>
              <input id="mc" type="text" className="sms-form-control" value={form.module_code || ''} onChange={e => setForm(f=>({...f,module_code:e.target.value}))}
                disabled={!!editing} />
              {editing && <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>Module code cannot be changed after creation.</div>}
              {codeTaken && <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt)', marginTop: 4 }}>This module code already exists.</div>}
            </div>
            <FormField id="mn" label="Module Name" value={form.module_name} onChange={e => setForm(f=>({...f,module_name:e.target.value}))} required />
            <FormField id="ew" label="Exam Weightage (%)" type="number" value={form.exam_weightage} onChange={e => setForm(f=>({...f,exam_weightage:e.target.value === '' ? '' : Number(e.target.value)}))} required
              hint={<><span>Shown in intake modules. </span><Link to="/records/intakes">View intakes</Link></>} />
            <FormField id="pt" label="Pass Threshold (%)" type="number" value={form.pass_threshold} onChange={e => setForm(f=>({...f,pass_threshold:e.target.value === '' ? '' : Number(e.target.value)}))} required
              hint={<><span>Minimum mark required to pass. </span><Link to="/records/marks-review">Open Marks Review</Link></>} />
            <FormField id="sch" label="Marking Scheme" value={form.scheme_id} onChange={e => setForm(f=>({...f,scheme_id:e.target.value === '' ? '' : Number(e.target.value)}))}
              options={[{value:'',label:'— None —'}, ...schemes.filter(s => s.is_active).map(s => ({value:s.id,label:`${s.name} (${s.component_count} components)`}))]} />
            <FormField id="lg" label="Language" value={form.language} onChange={e => setForm(f=>({...f,language:e.target.value}))}
              options={[{value:'English',label:'English'},{value:'Chinese',label:'Chinese'}]} required />
            {editing && (
              <FormField id="st" label="Status" value={form.is_active ?? 1} onChange={e => setForm(f=>({...f,is_active:Number(e.target.value)}))} options={[{value:1,label:'Active'},{value:0,label:'Inactive'}]} required />
            )}
          </div>
          <SaveButtons saving={saving} disabled={codeTaken} onCancel={() => setShow(false)} label={editing ? 'Update' : 'Create'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this module?" onConfirm={handleDelete} onCancel={() => setDelId(null)} />
      <DetailModal show={!!viewing} onClose={() => setViewing(null)} title="Module Details" fields={viewing ? [
        { label: 'Module Code',    value: viewing.module_code },
        { label: 'Module Name',    value: viewing.module_name },
        { label: 'Exam Weightage', value: <><div>{fmtPct(Math.round(viewing.exam_weightage))}</div><div style={{ fontSize: 12, lineHeight: 1.35, color: 'var(--sms-txt3)', marginTop: 4 }}>Shown in intake modules. <Link to="/records/intakes">View intakes</Link></div></> },
        { label: 'Pass Threshold', value: <><div>{fmtPct(Math.round(viewing.pass_threshold ?? 50))}</div><div style={{ fontSize: 12, lineHeight: 1.35, color: 'var(--sms-txt3)', marginTop: 4 }}>Minimum mark required to pass. <Link to="/records/marks-review">Open Marks Review</Link></div></> },
        { label: 'Marking Scheme', value: viewing.scheme_id ? (schemeName(viewing.scheme_id) || '—') : 'Not assigned' },
        { label: 'Language',       value: viewing.language || '—' },
         { label: 'Status',         value: <span className={`badge rounded-pill ${viewing.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{viewing.is_active ? 'Active' : 'Inactive'}</span> },
       ] : []} />
      <ComponentSetupModal
        show={!!schemeTarget}
        onClose={() => setSchemeTarget(null)}
        moduleCode={schemeTarget?.module_code}
        moduleLabel={schemeTarget ? `${schemeTarget.module_name} (${schemeTarget.module_code})` : ''}
        allowCreate={false}
        onSaved={() => { setSchemeTarget(null); load(); loadSchemes(); }}
      />
    </>
  );
}

// ─── COMPONENT MASTER ────────────────────────────────────────────────────────
let _tempIdSeq = 0;
const newTempId = () => `tmp_${Date.now()}_${_tempIdSeq++}`;

const EMPTY_COMPONENT_ROW = () => ({ tempId: newTempId(), name: '', percentage: '', is_active: 1, children: [] });

// Flat marking_components rows (parent_id null/not) -> nested tree the editor works with.
function buildComponentTree(rows) {
  const parents = rows.filter(r => !r.parent_id);
  return parents.map(p => ({
    id: p.id, tempId: newTempId(), name: p.name, percentage: Number(p.percentage), is_active: p.is_active,
    children: rows.filter(r => r.parent_id === p.id).map(c => ({
      id: c.id, tempId: newTempId(), name: c.name, percentage: Number(c.percentage), is_active: c.is_active,
    })),
  }));
}

export function ComponentMasterPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [show,    setShow]    = useState(false);
  const [editingId, setEditingId] = useState(null); // scheme id, or null for new
  const [locked,  setLocked]  = useState(false);
  const [name,    setName]    = useState('');
  const [components, setComponents] = useState([]);
  const [setupActive, setSetupActive] = useState(1); // marking_schemes.is_active
  const [saving,  setSaving]  = useState(false);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [error,   setError]   = useState('');
  const [preview, setPreview] = useState(false);
  const [delId,   setDelId]   = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/component-master/list').then(r => setRows(r.data.data)).catch(() => toast('Load failed','error')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const resetForm = () => { setName(''); setComponents([]); setSetupActive(1); setError(''); setPreview(false); setLocked(false); };

  const openCreate = () => {
    resetForm();
    setEditingId(null);
    setShow(true);
  };

  const openEdit = row => {
    resetForm();
    setEditingId(row.id);
    setShow(true);
    setLoadingSetup(true);
    api.get('/component-master', { params: { id: row.id } })
      .then(r => {
        setName(r.data.data.name);
        setSetupActive(r.data.data.is_active);
        setLocked(r.data.data.locked);
        setComponents(buildComponentTree(r.data.data.components));
      })
      .catch(() => toast('Failed to load scheme','error'))
      .finally(() => setLoadingSetup(false));
  };

  const toggleActive = async () => {
    const next = setupActive ? 0 : 1;
    try {
      await api.put(`/component-master/${editingId}/active`, { is_active: next });
      setSetupActive(next);
      toast(`Scheme ${next ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch (err) { toast(err.response?.data?.message || 'Failed to update', 'error'); }
  };

  const addComponent = () => setComponents(cs => [...cs, EMPTY_COMPONENT_ROW()]);
  const removeComponent = tempId => setComponents(cs => cs.filter(c => c.tempId !== tempId));
  const updateComponent = (tempId, field, value) =>
    setComponents(cs => cs.map(c => c.tempId === tempId ? { ...c, [field]: value } : c));

  const addChild = parentTempId =>
    setComponents(cs => cs.map(c => c.tempId === parentTempId
      ? { ...c, children: [...c.children, { tempId: newTempId(), name: '', percentage: '', is_active: 1 }] }
      : c));
  const removeChild = (parentTempId, childTempId) =>
    setComponents(cs => cs.map(c => c.tempId === parentTempId
      ? { ...c, children: c.children.filter(ch => ch.tempId !== childTempId) }
      : c));
  const updateChild = (parentTempId, childTempId, field, value) =>
    setComponents(cs => cs.map(c => c.tempId === parentTempId
      ? { ...c, children: c.children.map(ch => ch.tempId === childTempId ? { ...ch, [field]: value } : ch) }
      : c));

  // Drag-to-reorder for component rows, and separately for sub-category rows
  // within their parent — dragTempId tracks whichever row is currently held.
  const [dragTempId, setDragTempId] = useState(null);
  const [dragOverTempId, setDragOverTempId] = useState(null);
  const reorderComponents = (fromTempId, toTempId) => {
    if (fromTempId === toTempId) return;
    setComponents(cs => {
      const from = cs.findIndex(c => c.tempId === fromTempId);
      const to = cs.findIndex(c => c.tempId === toTempId);
      if (from === -1 || to === -1) return cs;
      const next = [...cs];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const reorderChildren = (parentTempId, fromTempId, toTempId) => {
    if (fromTempId === toTempId) return;
    setComponents(cs => cs.map(c => {
      if (c.tempId !== parentTempId) return c;
      const from = c.children.findIndex(ch => ch.tempId === fromTempId);
      const to = c.children.findIndex(ch => ch.tempId === toTempId);
      if (from === -1 || to === -1) return c;
      const children = [...c.children];
      const [moved] = children.splice(from, 1);
      children.splice(to, 0, moved);
      return { ...c, children };
    }));
  };

  const leafComponents = components.flatMap(c => c.children.length ? c.children.map(ch => ({ ...ch, parentName: c.name })) : [{ ...c, parentName: null }]);

  // Whole number or at most 1 decimal place, e.g. 20 or 20.5 — not 20.55.
  const isValidPercentage = v => /^\d+(\.\d)?$/.test(String(v).trim());
  const totalWeight = components.reduce((sum, c) => sum + (Number(c.percentage) || 0), 0);

  const handleSave = async e => {
    e.preventDefault();
    if (!name.trim()) { setError('Scheme name is required.'); return; }
    if (!components.length) { setError('Add at least one component.'); return; }
    for (const c of components) {
      if (!c.name.trim()) { setError('Every component needs a name.'); return; }
      if (c.percentage === '' || c.percentage === null || Number.isNaN(Number(c.percentage))) { setError(`Percentage is required for "${c.name}".`); return; }
      if (!isValidPercentage(c.percentage)) { setError(`"${c.name}" weight must be a whole number or have at most 1 decimal place.`); return; }
      for (const ch of c.children) {
        if (!ch.name.trim()) { setError(`Every sub-category under "${c.name}" needs a name.`); return; }
        if (ch.percentage === '' || ch.percentage === null || Number.isNaN(Number(ch.percentage))) { setError(`Percentage is required for "${ch.name}".`); return; }
        if (!isValidPercentage(ch.percentage)) { setError(`"${ch.name}" weight must be a whole number or have at most 1 decimal place.`); return; }
      }
    }
    if (Math.round(totalWeight * 10) / 10 !== 100) { setError(`Component weights must total 100% (currently ${totalWeight}%).`); return; }
    setSaving(true);
    try {
      await api.put('/component-master', {
        id: editingId, name: name.trim(),
        components: components.map(c => ({
          id: c.id, name: c.name, percentage: Number(c.percentage), is_active: setupActive,
          children: c.children.map(ch => ({ id: ch.id, name: ch.name, percentage: Number(ch.percentage), is_active: setupActive })),
        })),
      });
      toast(`Scheme ${editingId ? 'updated' : 'created'}`, 'success');
      setShow(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete('/component-master', { params: { id: delId } });
      toast('Scheme deleted','success'); setDelId(null); load();
    } catch (err) { toast(err.response?.data?.message||'Delete failed','error'); }
  };

  const filtered = rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered);
  const { paged, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sorted);

  return (
    <>
      <PageHeader title="Component Master" subtitle={`${total} marking schemes`}
        actions={can('component_master','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Scheme</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search scheme name…" onClear={() => setSearch('')} />
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && filtered.length === 0 ? <EmptyState icon="bi-diagram-3" message="No marking schemes found" /> : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{ width: 40 }}>#</th>
                <SortTh label="Scheme Name" field="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Components" field="component_count" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortTh label="Status" field="is_active" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th style={{ width: 70 }}></th>
                {(can('component_master','update')||can('component_master','delete')) && <th style={{ width: 90 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={5 + ((can('component_master','update')||can('component_master','delete')) ? 1 : 0)} /> : paged.map((r, idx) => (
                  <tr key={r.id} onClick={() => openEdit(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{start + idx + 1}</td>
                    <td style={{ fontWeight: 500 }}>{r.name}</td>
                    <td>{r.component_count}</td>
                    <td><span className={`badge rounded-pill ${r.is_active ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: 12 }}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td>{r.locked && <span title="Locked — marks have been recorded against this scheme"><i className="bi bi-lock-fill" style={{ color: 'var(--sms-txt3)' }} /></span>}</td>
                    {(can('component_master','update')||can('component_master','delete')) && (
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          {can('component_master','update') && <button className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} onClick={() => openEdit(r)}><i className={`bi ${r.locked ? 'bi-eye' : 'bi-pencil'}`} /></button>}
                          {can('component_master','delete') && <button className="sms-btn sms-btn-outline" disabled={r.locked} title={r.locked ? "Locked schemes can't be deleted — deactivate instead" : undefined} style={{ padding: '4px 8px', color: 'var(--sms-btn-outline-danger-txt)' }} onClick={() => setDelId(r.id)}><i className="bi bi-trash" /></button>}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} setPage={setPage} setPageSize={setPageSize} start={start} />}
          </>
        )}
      </div>

      <Modal show={show} onClose={() => setShow(false)} title={editingId ? 'Edit Marking Scheme' : 'Add Marking Scheme'} size="lg">
        {loadingSetup ? <Spinner minHeight="200px" /> : (
          <form onSubmit={handleSave} noValidate>
            {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}
            {locked && (
              <div className="alert alert-warning py-2 d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                <i className="bi bi-lock-fill" /> Marks have been recorded against this scheme — its name, weights and components are frozen. You can still activate/deactivate it, or create a new scheme instead.
              </div>
            )}
            <div className="row g-3 mb-3">
              <div className="col-12 col-sm-8">
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Scheme Name *</label>
                <input type="text" className="sms-form-control" value={name} disabled={locked} placeholder="e.g. Standard 20/20/60"
                  onChange={e => setName(e.target.value)} />
              </div>
              <div className="col-12 col-sm-4">
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Status *</label>
                <div className="sms-form-control d-flex align-items-center gap-2">
                  <button type="button" role="switch" aria-checked={!!setupActive} className="sms-switch"
                    onClick={() => editingId && locked ? toggleActive() : setSetupActive(a => a ? 0 : 1)} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: setupActive ? 'var(--sms-stat-green)' : 'var(--sms-btn-outline-danger-txt)' }}>
                    {setupActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-2" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Components</span>
                {!!components.length && (
                  <span style={{ fontSize: 12, fontWeight: 500, color: Math.round(totalWeight * 10) / 10 === 100 ? 'var(--sms-stat-green)' : 'var(--sms-btn-outline-danger-txt)' }}>
                    Total: {fmtPct(totalWeight)}
                  </span>
                )}
              </div>
              <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
                <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} disabled={locked} onClick={addComponent}><i className="bi bi-plus-lg" /> Add component</button>
                <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} disabled={!components.length} onClick={() => setPreview(p => !p)}>
                  <i className={`bi ${preview ? 'bi-eye-slash' : 'bi-eye'}`} /> {preview ? 'Hide preview' : 'Preview'}
                </button>
              </div>
            </div>

            {!components.length ? (
              <EmptyState icon="bi-diagram-3" message="No components yet — add one to get started" />
            ) : (
              <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                <table className="sms-table sms-component-table">
                  <thead><tr>
                    <th style={{ width: 28 }}></th>
                    <th>Name</th>
                    <th style={{ width: 110 }}>Weight %</th>
                    <th style={{ width: 90 }}></th>
                  </tr></thead>
                  <tbody>
                    {components.map(c => (
                      <React.Fragment key={c.tempId}>
                        <tr
                          draggable={!locked}
                          onDragStart={() => setDragTempId(c.tempId)}
                          onDragOver={e => { e.preventDefault(); if (dragOverTempId !== c.tempId) setDragOverTempId(c.tempId); }}
                          onDragLeave={() => setDragOverTempId(id => id === c.tempId ? null : id)}
                          onDrop={e => { e.preventDefault(); reorderComponents(dragTempId, c.tempId); setDragTempId(null); setDragOverTempId(null); }}
                          onDragEnd={() => { setDragTempId(null); setDragOverTempId(null); }}
                          style={{
                            opacity: dragTempId === c.tempId ? 0.4 : 1,
                            boxShadow: dragOverTempId === c.tempId && dragTempId !== c.tempId ? 'inset 0 2px 0 var(--sms-acc)' : 'none',
                            transition: 'opacity .15s ease, box-shadow .1s ease',
                          }}
                        >
                          <td style={{ cursor: locked ? 'default' : 'grab', textAlign: 'center' }} title={locked ? undefined : 'Drag to reorder'}><i className="bi bi-grip-vertical" /></td>
                          <td data-label="Name"><input type="text" className="sms-form-control" value={c.name} placeholder="e.g. Assignment" disabled={locked}
                            onChange={e => updateComponent(c.tempId, 'name', e.target.value)} /></td>
                          <td data-label="Weight %"><input type="number" step="0.1" min="0" max="100" className="sms-form-control" value={c.percentage} disabled={locked}
                            onChange={e => updateComponent(c.tempId, 'percentage', e.target.value === '' ? '' : Number(e.target.value))} /></td>
                          <td>
                            <div className="d-flex gap-1">
                              <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} disabled={locked} title="Add sub-category" onClick={() => addChild(c.tempId)}><i className="bi bi-diagram-2" /></button>
                              <button type="button" className="sms-btn sms-btn-outline" disabled={locked} style={{ padding: '4px 8px', color: 'var(--sms-btn-outline-danger-txt)' }} onClick={() => removeComponent(c.tempId)}><i className="bi bi-trash" /></button>
                            </div>
                          </td>
                        </tr>
                        {c.children.map(ch => (
                          <tr
                            key={ch.tempId}
                            draggable={!locked}
                            onDragStart={() => setDragTempId(ch.tempId)}
                            onDragOver={e => { e.preventDefault(); if (dragOverTempId !== ch.tempId) setDragOverTempId(ch.tempId); }}
                            onDragLeave={() => setDragOverTempId(id => id === ch.tempId ? null : id)}
                            onDrop={e => { e.preventDefault(); reorderChildren(c.tempId, dragTempId, ch.tempId); setDragTempId(null); setDragOverTempId(null); }}
                            onDragEnd={() => { setDragTempId(null); setDragOverTempId(null); }}
                            style={{
                              background: 'var(--sms-sur2)',
                              opacity: dragTempId === ch.tempId ? 0.4 : 1,
                              boxShadow: dragOverTempId === ch.tempId && dragTempId !== ch.tempId ? 'inset 0 2px 0 var(--sms-acc)' : 'none',
                              transition: 'opacity .15s ease, box-shadow .1s ease',
                            }}
                          >
                            <td style={{ cursor: locked ? 'default' : 'grab', color: 'var(--sms-acc)', textAlign: 'center' }} title={locked ? undefined : 'Drag to reorder sub-category'}><i className="bi bi-arrows-move" /></td>
                            <td data-label="Name" style={{ paddingLeft: 12 }}>
                              <span style={{ color: 'var(--sms-txt3)', marginRight: 6 }}>—</span>
                              <input type="text" className="sms-form-control" style={{ display: 'inline-block', width: 'calc(100% - 20px)' }} value={ch.name} placeholder="e.g. Q1" disabled={locked}
                                onChange={e => updateChild(c.tempId, ch.tempId, 'name', e.target.value)} />
                            </td>
                            <td data-label="Weight %"><input type="number" step="0.1" min="0" max="100" className="sms-form-control" value={ch.percentage} disabled={locked}
                              onChange={e => updateChild(c.tempId, ch.tempId, 'percentage', e.target.value === '' ? '' : Number(e.target.value))} /></td>
                            <td>
                              <button type="button" className="sms-btn sms-btn-outline" disabled={locked} style={{ padding: '4px 8px', color: 'var(--sms-btn-outline-danger-txt)' }} onClick={() => removeChild(c.tempId, ch.tempId)}><i className="bi bi-trash" /></button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview && !!leafComponents.length && (
              <div className="sms-card" style={{ marginBottom: 12, background: 'var(--sms-sur2)' }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', marginBottom: 8 }}>
                  Preview — how trainers will see this when marking
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="sms-table" style={{ minWidth: 400 }}>
                    <thead>
                      <tr>
                        <th rowSpan={2}>Student</th>
                        {components.map(c => c.children.length
                          ? <th key={c.tempId} colSpan={c.children.length} style={{ textAlign: 'center' }}>{c.name || '(unnamed)'}</th>
                          : (
                            <th key={c.tempId} rowSpan={2} style={{ textAlign: 'center' }}>
                              {c.name || '(unnamed)'}
                              <div style={{ fontWeight: 400, color: 'var(--sms-txt3)', fontSize: 12 }}>{fmtPct(c.percentage || 0)}</div>
                            </th>
                          ))}
                      </tr>
                      {components.some(c => c.children.length) && (
                        <tr>
                          {components.filter(c => c.children.length).flatMap(c => c.children.map(ch => (
                            <th key={ch.tempId} style={{ textAlign: 'center' }}>
                              {ch.name || '(unnamed)'}
                              <div style={{ fontWeight: 400, color: 'var(--sms-txt3)', fontSize: 12 }}>{fmtPct(ch.percentage || 0)}</div>
                            </th>
                          )))}
                        </tr>
                      )}
                    </thead>
                    <tbody>
                      <tr>
                        <td>Sample Student</td>
                        {leafComponents.map(l => (
                          <td key={l.tempId} style={{ textAlign: 'center' }}>
                            <input type="text" className="sms-form-control" style={{ width: 60, textAlign: 'center' }} disabled placeholder="0" />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {locked ? (
              <div className="d-flex justify-content-end mt-4">
                <button type="button" className="sms-btn sms-btn-outline" onClick={() => setShow(false)}>Close</button>
              </div>
            ) : (
              <SaveButtons saving={saving} onCancel={() => setShow(false)} label={editingId ? 'Update' : 'Create'} />
            )}
          </form>
        )}
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this marking scheme? This can't be undone." onConfirm={handleDelete} onCancel={() => setDelId(null)} />
    </>
  );
}

// ─── MARKS REVIEW & RELEASE ──────────────────────────────────────────────────
// Admin checkpoint between trainer marking (/trainer/component-marks) and the
// student portal: trainer-entered scores land as 'pending' in
// marks_review_status (editable by the trainer). Admin can either:
//  - Lock: sets status 'locked' — blocks further trainer edits
//    (enforced server-side in saveComponentMarks) but does NOT publish to
//    the student yet. Lets admin freeze a row mid-review without rushing to
//    release it.
//  - Release: sets status 'released' — publishes to the student AND locks
//    the trainer out, whether coming from 'pending' or 'locked'. This is
//    the one-way door; there's no unlocking a released row.
// Unlock reverts a 'locked' (not-yet-released) row back to 'pending' so the
// trainer can edit again.
const MARKS_STATUS_BADGE = {
  pending:  { label: 'Pending',  cls: 'badge-pending',  icon: 'bi-hourglass-split' },
  locked:   { label: 'Locked',   cls: 'badge-approved', icon: 'bi-lock-fill' },
  released: { label: 'Released', cls: 'badge-released', icon: 'bi-broadcast' },
};

export function MarksReviewPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  const canUpdate = can('marks_review', 'update');

  const [options, setOptions] = useState({ intakes: [], modules: [], intakeModules: [] });
  const [intakeCode, setIntakeCode] = useState(searchParams.get('intake_code') || '');
  const [moduleCode, setModuleCode] = useState(searchParams.get('module_code') || '');
  const [examType, setExamType] = useState(searchParams.get('exam_type') || 'main');

  // Arriving from the Class Passing Rate page's drill-down clears the query
  // params after seeding state above, so refreshing this page doesn't keep
  // re-jumping to the same roster.
  useEffect(() => {
    if (searchParams.has('intake_code')) setSearchParams({}, { replace: true });
  }, []);
  const [components, setComponents] = useState([]);
  const [roster, setRoster] = useState([]);
  const [threshold, setThreshold] = useState(null);
  const [showThresholdSetup, setShowThresholdSetup] = useState(false);
  const [thresholdInput, setThresholdInput] = useState('');
  const [thresholdError, setThresholdError] = useState('');
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [statModal, setStatModal] = useState(null); // 'pending' | 'locked' | null
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const loadStats = () => {
    api.get('/marks-review/stats').then(r => setStats(r.data.data)).catch(() => {});
  };

  useEffect(() => {
    api.get('/marks-review/options').then(r => setOptions(r.data.data)).catch(() => toast('Failed to load intakes/modules', 'error'));
    loadStats();
  }, []);

  const modulesForIntake = intakeCode ? options.intakeModules.filter(im => im.intake_code === intakeCode) : [];
  const moduleName = code => options.modules.find(m => m.module_code === code)?.module_name || code;
  const selectedModule = options.modules.find(m => m.module_code === moduleCode);
  const hasPassThreshold = selectedModule?.pass_threshold !== null && selectedModule?.pass_threshold !== undefined && selectedModule?.pass_threshold !== '';

  const openThresholdSetup = () => {
    setThresholdInput(selectedModule?.pass_threshold ?? '');
    setThresholdError('');
    setShowThresholdSetup(true);
  };

  const savePassThreshold = async e => {
    e.preventDefault();
    const value = Number(thresholdInput);
    if (thresholdInput === '' || Number.isNaN(value) || value < 0 || value > 100) {
      setThresholdError('Enter a number between 0 and 100.');
      return;
    }
    setThresholdSaving(true);
    try {
      await api.put(`/modules/${moduleCode}/pass-threshold`, { pass_threshold: value });
      setOptions(current => ({
        ...current,
        modules: current.modules.map(m => m.module_code === moduleCode ? { ...m, pass_threshold: value } : m),
      }));
      setThreshold(value);
      setShowThresholdSetup(false);
      toast('Pass threshold updated', 'success');
    } catch (err) {
      setThresholdError(err.response?.data?.message || 'Failed to update pass threshold.');
    } finally {
      setThresholdSaving(false);
    }
  };

  const loadRoster = () => {
    if (!intakeCode || !moduleCode) { setRoster([]); setComponents([]); setThreshold(null); return; }
    if (!selectedModule) return;
    if (!hasPassThreshold) { setRoster([]); setComponents([]); setThreshold(null); return; }
    setLoading(true);
    setSelected(new Set());
    api.get('/marks-review/roster', { params: { intake_code: intakeCode, module_code: moduleCode, exam_type: examType } })
      .then(r => { setRoster(r.data.data.roster); setComponents(r.data.data.components); setThreshold(r.data.data.pass_threshold); })
      .catch(() => toast('Failed to load roster', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(loadRoster, [intakeCode, moduleCode, examType, selectedModule?.module_code, selectedModule?.pass_threshold]);

  // Same flattening TrainerComponentMarksPage uses so header columns and body
  // cells line up: a top-level item, or its children if it's a group, plus a
  // groupStart flag marking where a vertical divider belongs.
  const topLevel = components.filter(c => !c.parent_id);
  const orderedLeaves = [];
  let leafIdx = 0;
  topLevel.forEach(t => {
    const children = components.filter(c => c.parent_id === t.id);
    const items = children.length ? children : [t];
    items.forEach((c, ci) => {
      orderedLeaves.push({ ...c, groupStart: leafIdx === 0 || (children.length > 0 && ci === 0) });
      leafIdx++;
    });
  });
  const initials = name => (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  // Round to at most 1 decimal place, then drop it entirely if it's a whole
  // number — 20.55 -> "20.6", 20.5 -> "20.5", 20.00 -> "20".
  const fmtScore = v => {
    const rounded = Math.round(Number(v) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  // Students with no scores at all ("Not fully marked", result === null) can't
  // be released — server rejects it too, this just keeps the checkbox from
  // offering an action that's guaranteed to fail.
  const markableRoster = roster.filter(r => r.result !== null);

  const toggleOne = student_no => setSelected(s => {
    const next = new Set(s);
    next.has(student_no) ? next.delete(student_no) : next.add(student_no);
    return next;
  });
  const allSelected = markableRoster.length > 0 && markableRoster.every(r => selected.has(r.student_no));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(markableRoster.map(r => r.student_no)));

  const selectedRows = roster.filter(r => selected.has(r.student_no));
  // Lock only makes sense on rows still open for editing.
  const canLock = selectedRows.length > 0 && selectedRows.every(r => r.status === 'pending');
  // Release works from pending or locked straight through to released.
  const canRelease = selectedRows.length > 0 && selectedRows.every(r => r.status !== 'released');
  // Only locked-but-not-released rows can be unlocked — released is a one-way door.
  const canUnlock = selectedRows.length > 0 && selectedRows.every(r => r.status === 'locked');

  // Jump straight to a specific student's roster from the drill-down modal —
  // saves re-picking intake/module/exam type by hand for the row that's
  // actually being reviewed.
  const goToRoster = row => {
    setIntakeCode(row.intake_code);
    setModuleCode(row.module_code);
    setExamType(row.exam_type);
    setStatModal(null);
  };

  const statModalRows = !stats?.rows ? [] :
    statModal === 'pending' ? stats.rows.filter(r => r.status === 'pending') :
    statModal === 'locked' ? stats.rows.filter(r => r.status === 'locked') : [];
  const statModalTitle = {
    pending: 'Pending review',
    locked: 'Locked, not released',
  }[statModal];

  const runAction = async (action, verb) => {
    setBusy(true);
    try {
      await api.put(`/marks-review/${action}`, {
        intake_code: intakeCode, module_code: moduleCode, exam_type: examType,
        student_nos: [...selected],
      });
      toast(`Marks ${verb}`, 'success');
      loadRoster();
      loadStats();
    } catch (err) {
      toast(err.response?.data?.message || `Failed to mark as ${verb}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Marks Review & Release"
        subtitle={`Approve trainer-submitted marks, then release them to students — pass threshold ${threshold != null ? `${fmtPct(threshold)}` : 'not set'}`}
        actions={
          <button type="button" className="sms-btn sms-btn-outline sms-btn-setup-guide" onClick={() => setShowSetupGuide(true)}>
            <i className="bi bi-question-circle" aria-hidden="true" /> How Marks Review Works
          </button>
        }
      />

      <div className="row g-3 mb-3">
        <div className="col-6 col-sm-4">
          <div className="sms-stat-card" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
            onClick={() => setStatModal('pending')} onKeyDown={e => e.key === 'Enter' && setStatModal('pending')}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pending review</span>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'color-mix(in srgb, var(--sms-stat-amber) 14%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-hourglass-split" style={{ color: 'var(--sms-stat-amber)', fontSize: 16 }} />
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{stats?.pending_review ?? '—'}</div>
          </div>
        </div>
        <div className="col-6 col-sm-4">
          <div className="sms-stat-card" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
            onClick={() => setStatModal('locked')} onKeyDown={e => e.key === 'Enter' && setStatModal('locked')}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Locked, not released</span>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'color-mix(in srgb, var(--sms-stat-blue) 14%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-lock-fill" style={{ color: 'var(--sms-stat-blue)', fontSize: 16 }} />
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{stats?.locked_not_released ?? '—'}</div>
          </div>
        </div>
        <div className="col-6 col-sm-4">
          <div className="sms-stat-card" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
            onClick={() => navigate('/reports/passing-rate')} onKeyDown={e => e.key === 'Enter' && navigate('/reports/passing-rate')}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Class passing rate
                <i
                  className="bi bi-info-circle" aria-hidden="true" tabIndex={0}
                  onClick={e => e.stopPropagation()}
                  style={{ color: 'var(--sms-acc)', fontSize: 12, cursor: 'help' }}
                  title="Passed students ÷ fully-marked students, rounded to a whole percent — across every intake, module, and exam type, not just the one selected below. Released results use the total and pass threshold frozen at release time; students not yet released are judged against the module's current pass threshold and live marks."
                />
              </span>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: 'color-mix(in srgb, var(--sms-stat-green) 14%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-graph-up" style={{ color: 'var(--sms-stat-green)', fontSize: 16 }} />
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--sms-txt)', lineHeight: 1.1 }}>{stats?.class_passing_rate != null ? `${fmtPct(stats.class_passing_rate)}` : '—'}</div>
          </div>
        </div>
      </div>

      <Modal show={!!statModal} onClose={() => setStatModal(null)} title={statModalTitle} size="lg">
        {statModalRows.length === 0 ? (
          <EmptyState icon="bi-inbox" message="Nothing here right now" />
        ) : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th>Student</th>
                <th>Intake</th>
                <th>Module</th>
                <th>Exam</th>
                <th style={{ textAlign: 'right' }}>Weighted Total</th>
                <th>Result</th>
                <th>Status</th>
                <th></th>
              </tr></thead>
              <tbody>
                {statModalRows.map(r => (
                  <tr key={`${r.student_no}:${r.intake_code}:${r.module_code}:${r.exam_type}`}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--sms-txt2)' }}>{r.student_no}</div>
                    </td>
                    <td>{r.intake_code}</td>
                    <td>{r.module_name}</td>
                    <td style={{ textTransform: 'capitalize' }}>{r.exam_type}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(r.weighted_total)}</td>
                    <td>
                      <span className={`badge rounded-pill ${r.result === 'pass' ? 'badge-pass' : 'badge-fail'}`} style={{ fontSize: 12 }}>
                        {r.result === 'pass' ? 'Pass' : 'Fail'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge rounded-pill ${MARKS_STATUS_BADGE[r.status].cls}`} style={{ fontSize: 12 }}>
                        {MARKS_STATUS_BADGE[r.status].label}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '4px 8px' }} onClick={() => goToRoster(r)}>
                        View roster
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Modal>

      <Modal show={showSetupGuide} onClose={() => setShowSetupGuide(false)} title="How Marks Review Works" size="lg">
        <div style={{ color: 'var(--sms-txt2)', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
          Complete the setup in order. This page is the final step for reviewing and releasing marks; it does not create marking schemes or component marks.
        </div>
        <div className="row g-3">
          {[
            {
              number: 1,
              title: 'Create and connect the scheme',
              text: '1. In Component Master, click Add Scheme, add the components and percentages, make sure the total is 100%, then save. 2. In Module Master, click Assign Scheme for the module, choose the saved scheme, and click Assign to Module.',
              links: [
                { to: '/records/components', action: 'Open Component Master' },
                { to: '/records/modules', action: 'Open Module Master' },
              ],
              icon: 'bi-diagram-3',
            },
            {
              number: 2,
              title: 'Give the trainer access',
              text: 'In Marking Permissions, choose the course and intake. Select the trainer for Main or Resit, turn on Can enter marks, then click Save Permissions.',
              to: '/records/marking-permissions',
              action: 'Open Marking Permissions',
              icon: 'bi-person-check',
            },
            {
              number: 3,
              title: 'Choose what to review',
              text: 'After the trainer submits marks, choose the intake, module, and Main or Resit exam above. The roster shows the component marks and weighted total.',
              to: null,
              action: null,
              icon: 'bi-collection',
            },
            {
              number: 4,
              title: 'Review and release',
              text: 'Rows must be fully marked before they can be released. Use Lock to stop trainer edits, Release to publish results to students, or Unlock to send locked rows back for editing.',
              to: null,
              action: null,
              icon: 'bi-clipboard2-check',
            },
          ].map(step => (
            <div key={step.number} className="col-12 col-sm-6">
              <div className="sms-detail-field" style={{ height: '100%' }}>
                <div className="d-flex align-items-start gap-2">
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, flex: '0 0 26px', borderRadius: 8, background: 'var(--sms-acc-bg)', color: 'var(--sms-acc)', fontSize: 12, fontWeight: 700 }}>{step.number}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--sms-txt)', fontSize: 13, fontWeight: 700, marginBottom: 5 }}>
                      <i className={`bi ${step.icon}`} aria-hidden="true" /> {step.title}
                    </div>
                    <div style={{ color: 'var(--sms-txt2)', fontSize: 12, lineHeight: 1.45 }}>{step.text}</div>
                    {(step.links || (step.to ? [{ to: step.to, action: step.action }] : [])).map(link => (
                      <Link key={link.to} className="sms-btn sms-btn-outline sms-btn-setup-guide" style={{ display: 'inline-flex', marginTop: 12, marginRight: 6, padding: '5px 9px', fontSize: 12 }} to={link.to} onClick={() => setShowSetupGuide(false)}>{link.action}</Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="alert alert-info py-2 mt-3 mb-0" style={{ fontSize: 12 }}>
          <i className="bi bi-info-circle" style={{ marginRight: 6 }} aria-hidden="true" />
          If the module is missing from the selector, check that it is active, assigned to the selected intake, and connected to an active marking scheme. Trainer permissions control who can enter marks, not whether an administrator can review them.
        </div>
      </Modal>

      <div className="sms-card mb-3" style={{ padding: '12px 14px' }}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ flex: '1 1 200px', minWidth: 180 }}>
            <SearchableSelect value={intakeCode} placeholder="Select intake…"
              onChange={e => { setIntakeCode(e.target.value); setModuleCode(''); setThreshold(null); }}
              options={options.intakes.map(i => ({
                value: i.intake_code,
                label: i.intake_code,
                sublabel: i.soc_date && i.eoc_date ? `${fmtDate(i.soc_date)} – ${fmtDate(i.eoc_date)}` : 'SOC/EOC dates not set',
              }))} />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: 180 }}>
            <SearchableSelect value={moduleCode} disabled={!intakeCode} placeholder={intakeCode ? 'Select module…' : 'Select an intake first'}
              onChange={e => {
                const code = e.target.value;
                const module = options.modules.find(m => m.module_code === code);
                setModuleCode(code);
                setThreshold(module?.pass_threshold ?? null);
              }}
              options={modulesForIntake.map(im => ({ value: im.module_code, label: moduleName(im.module_code) }))} />
          </div>
          <div style={{ flex: '0 1 160px', minWidth: 140 }}>
            <SearchableSelect value={examType} onChange={e => setExamType(e.target.value)}
              options={[{ value: 'main', label: 'Main' }, { value: 'resit', label: 'Resit' }]} />
          </div>
        </div>
      </div>

      {!intakeCode || !moduleCode ? (
        <div className="sms-card"><EmptyState icon="bi-clipboard2-check" message="Select an intake and module to review marks" /></div>
      ) : (
        <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
          {selectedModule && !hasPassThreshold ? (
            <div style={{ padding: '36px 24px', textAlign: 'center' }}>
              <i className="bi bi-exclamation-circle" style={{ display: 'block', color: 'var(--sms-stat-amber)', fontSize: 30, marginBottom: 10 }} />
              <div style={{ color: 'var(--sms-txt)', fontSize: 15, fontWeight: 600, marginBottom: 5 }}>
                Pass threshold is not set for this module
              </div>
              <div style={{ color: 'var(--sms-txt2)', fontSize: 13, marginBottom: 16 }}>
                Set it before reviewing or releasing marks.
              </div>
              {can('module_master', 'update') ? (
                <button type="button" className="sms-btn sms-btn-primary" onClick={openThresholdSetup}>
                  <i className="bi bi-sliders2" /> Set Pass Threshold
                </button>
              ) : (
                <div style={{ color: 'var(--sms-txt2)', fontSize: 12 }}>
                  You need module configuration permission to set it.
                </div>
              )}
            </div>
          ) : !selectedModule ? (
            <Spinner minHeight="180px" />
          ) : !loading && roster.length === 0 ? (
            <EmptyState icon="bi-people" message="No students enrolled in this module" />
          ) : (
            <>
              {canUpdate && (
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between"
                  style={{ padding: '10px 16px', background: 'var(--sms-acc-bg)', borderBottom: '1px solid var(--sms-bdr)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sms-acc)' }}>
                    {selected.size} student{selected.size === 1 ? '' : 's'} selected
                  </span>
                  <div className="d-flex gap-2 flex-wrap">
                    <button type="button" className="sms-btn sms-btn-primary" style={{ fontSize: 12, padding: '6px 12px' }}
                      disabled={!canRelease || busy} title={selected.size && !canRelease ? 'Already released students can\'t be released again' : undefined}
                      onClick={() => runAction('release', 'released')}>
                      <i className="bi bi-send-check" /> Release to students
                    </button>
                    <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '6px 12px' }}
                      disabled={!canLock || busy} title={selected.size && !canLock ? 'Only pending students can be locked' : 'Block the trainer from editing, without releasing to students yet'}
                      onClick={() => runAction('approve', 'locked')}>
                      <i className="bi bi-lock" /> Lock
                    </button>
                    <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '6px 12px' }}
                      disabled={!canUnlock || busy} title={selected.size && !canUnlock ? 'Only locked (not yet released) students can be unlocked' : 'Send back to pending so the trainer can edit marks again'}
                      onClick={() => runAction('unlock', 'unlocked')}>
                      <i className="bi bi-unlock" /> Unlock
                    </button>
                  </div>
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table className="sms-table cm-table">
                  <thead>
                    <tr>
                      {canUpdate && <th rowSpan={2} style={{ width: 30, verticalAlign: 'bottom' }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>}
                      <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Student</th>
                      {topLevel.map(t => {
                        const children = components.filter(c => c.parent_id === t.id);
                        const groupStart = orderedLeaves.find(l => l.id === (children.length ? children[0].id : t.id))?.groupStart;
                        return children.length
                          ? <th key={t.id} colSpan={children.length} className={groupStart ? 'cm-group-start' : ''} style={{ textAlign: 'center' }}>{t.name}</th>
                          : <th key={t.id} rowSpan={2} className={groupStart ? 'cm-group-start' : ''} style={{ verticalAlign: 'bottom', textAlign: 'center' }}>{t.name}<span className="cm-max">{fmtPct(Number(t.percentage))}</span></th>;
                      })}
                      <th rowSpan={2} style={{ verticalAlign: 'bottom', textAlign: 'right' }}>Weighted Total</th>
                      <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Result</th>
                      <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Status</th>
                    </tr>
                    <tr>
                      {topLevel.filter(t => components.some(c => c.parent_id === t.id)).flatMap(t =>
                        components.filter(c => c.parent_id === t.id).map(ch => (
                          <th key={ch.id} className={orderedLeaves.find(l => l.id === ch.id)?.groupStart ? 'cm-group-start' : ''} style={{ textAlign: 'center', fontWeight: 400 }}>
                            {ch.name}<span className="cm-max">{fmtPct(Number(ch.percentage))}</span>
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <TableSkeletonRows columns={(canUpdate ? 1 : 0) + 1 + orderedLeaves.length + 3} /> : roster.map(r => (
                      <tr key={r.student_no} className={r.result === null ? 'sms-row-muted' : undefined}>
                        {canUpdate && (
                          <td>
                            <input type="checkbox" checked={selected.has(r.student_no)} disabled={r.result === null}
                              title={r.result === null ? "Not fully marked yet — can't be released" : undefined}
                              onChange={() => toggleOne(r.student_no)} />
                          </td>
                        )}
                        <td>
                          <div className="cm-stu-cell">
                            <span className="cm-avatar">{initials(r.name)}</span>
                            <span>
                              <div>{r.name}</div>
                              <div className="cm-stu-id">{r.student_no}</div>
                            </span>
                          </div>
                        </td>
                        {orderedLeaves.map(l => (
                          <td key={l.id} className={l.groupStart ? 'cm-group-start' : ''} style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                            {r.scores[l.id] === '' || r.scores[l.id] == null ? <span style={{ color: 'var(--sms-txt3)' }}>—</span> : fmtScore(r.scores[l.id])}
                          </td>
                        ))}
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {r.weighted_total === null ? '—' : `${fmtPct(r.weighted_total)}`}
                        </td>
                        <td>
                          {r.result === null ? <span style={{ color: 'var(--sms-txt3)', fontSize: 12 }}>Not fully marked</span> : (
                            <span className={`badge rounded-pill ${r.result === 'pass' ? 'badge-pass' : 'badge-fail'}`} style={{ fontSize: 12 }}>
                              {r.result === 'pass' ? 'Pass' : 'Fail'}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge rounded-pill ${MARKS_STATUS_BADGE[r.status].cls}`} style={{ fontSize: 12 }}>
                            <i className={`bi ${MARKS_STATUS_BADGE[r.status].icon}`} style={{ marginRight: 4 }} />
                            {MARKS_STATUS_BADGE[r.status].label}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <Modal show={showThresholdSetup} onClose={() => setShowThresholdSetup(false)} title="Set Pass Threshold">
        <form onSubmit={savePassThreshold} noValidate>
          {thresholdError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{thresholdError}</div>}
          <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginBottom: 14 }}>
            Module: <strong style={{ color: 'var(--sms-txt)' }}>{selectedModule?.module_name || moduleCode || '—'}</strong>
          </div>
          <FormField
            id="marks-pass-threshold"
            label="Pass Threshold (%)"
            type="number"
            value={thresholdInput}
            onChange={e => setThresholdInput(e.target.value === '' ? '' : Number(e.target.value))}
            hint="Minimum weighted total required to pass."
            required
          />
          <SaveButtons saving={thresholdSaving} onCancel={() => setShowThresholdSetup(false)} label="Save Threshold" />
        </form>
      </Modal>
    </>
  );
}

// Module-aware helper for syncing an existing Component Master scheme to a
// module. Component schemes are created in Component Master first; this modal
// only handles the module assignment via PUT /modules/:id/scheme.
export function ComponentSetupModal({ show, onClose, onSaved, moduleCode, moduleLabel, allowCreate = false }) {
  const toast = useToast();
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [schemes, setSchemes] = useState([]);
  const [selectedSchemeId, setSelectedSchemeId] = useState('');
  const [name, setName] = useState('');
  const [components, setComponents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);
  const [dragTempId, setDragTempId] = useState(null);
  const [dragOverTempId, setDragOverTempId] = useState(null);

  useEffect(() => {
    if (!show) return;
    setMode('existing'); setSelectedSchemeId(''); setName(''); setComponents([]); setError(''); setPreview(false);
    setLoadingSetup(true);
    api.get('/component-master/list')
      .then(r => setSchemes(r.data.data.filter(s => s.is_active)))
      .catch(() => toast('Failed to load marking schemes', 'error'))
      .finally(() => setLoadingSetup(false));
  }, [show]);

  const addComponent = () => setComponents(cs => [...cs, EMPTY_COMPONENT_ROW()]);
  const removeComponent = tempId => setComponents(cs => cs.filter(c => c.tempId !== tempId));
  const updateComponent = (tempId, field, value) =>
    setComponents(cs => cs.map(c => c.tempId === tempId ? { ...c, [field]: value } : c));

  const addChild = parentTempId =>
    setComponents(cs => cs.map(c => c.tempId === parentTempId
      ? { ...c, children: [...c.children, { tempId: newTempId(), name: '', percentage: '', is_active: 1 }] }
      : c));
  const removeChild = (parentTempId, childTempId) =>
    setComponents(cs => cs.map(c => c.tempId === parentTempId
      ? { ...c, children: c.children.filter(ch => ch.tempId !== childTempId) }
      : c));
  const updateChild = (parentTempId, childTempId, field, value) =>
    setComponents(cs => cs.map(c => c.tempId === parentTempId
      ? { ...c, children: c.children.map(ch => ch.tempId === childTempId ? { ...ch, [field]: value } : ch) }
      : c));

  const reorderComponents = (fromTempId, toTempId) => {
    if (fromTempId === toTempId) return;
    setComponents(cs => {
      const from = cs.findIndex(c => c.tempId === fromTempId);
      const to = cs.findIndex(c => c.tempId === toTempId);
      if (from === -1 || to === -1) return cs;
      const next = [...cs];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const reorderChildren = (parentTempId, fromTempId, toTempId) => {
    if (fromTempId === toTempId) return;
    setComponents(cs => cs.map(c => {
      if (c.tempId !== parentTempId) return c;
      const from = c.children.findIndex(ch => ch.tempId === fromTempId);
      const to = c.children.findIndex(ch => ch.tempId === toTempId);
      if (from === -1 || to === -1) return c;
      const children = [...c.children];
      const [moved] = children.splice(from, 1);
      children.splice(to, 0, moved);
      return { ...c, children };
    }));
  };

  const leafComponents = components.flatMap(c => c.children.length ? c.children.map(ch => ({ ...ch, parentName: c.name })) : [{ ...c, parentName: null }]);
  const isValidPercentage = v => /^\d+(\.\d)?$/.test(String(v).trim());
  const totalWeight = components.reduce((sum, c) => sum + (Number(c.percentage) || 0), 0);

  const handleSave = async e => {
    e.preventDefault();
    if (!allowCreate && mode !== 'existing') {
      setMode('existing');
      return;
    }
    if (mode === 'existing') {
      if (!selectedSchemeId) { setError('Pick a marking scheme.'); return; }
      setSaving(true);
      try {
        await api.put(`/modules/${moduleCode}/scheme`, { scheme_id: selectedSchemeId });
        toast('Marking scheme assigned', 'success');
        onSaved?.();
      } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
      finally { setSaving(false); }
      return;
    }
    if (!name.trim()) { setError('Scheme name is required.'); return; }
    if (!components.length) { setError('Add at least one component.'); return; }
    for (const c of components) {
      if (!c.name.trim()) { setError('Every component needs a name.'); return; }
      if (c.percentage === '' || c.percentage === null || Number.isNaN(Number(c.percentage))) { setError(`Percentage is required for "${c.name}".`); return; }
      if (!isValidPercentage(c.percentage)) { setError(`"${c.name}" weight must be a whole number or have at most 1 decimal place.`); return; }
      for (const ch of c.children) {
        if (!ch.name.trim()) { setError(`Every sub-category under "${c.name}" needs a name.`); return; }
        if (ch.percentage === '' || ch.percentage === null || Number.isNaN(Number(ch.percentage))) { setError(`Percentage is required for "${ch.name}".`); return; }
        if (!isValidPercentage(ch.percentage)) { setError(`"${ch.name}" weight must be a whole number or have at most 1 decimal place.`); return; }
      }
    }
    if (Math.round(totalWeight * 10) / 10 !== 100) { setError(`Component weights must total 100% (currently ${totalWeight}%).`); return; }
    setSaving(true);
    try {
      const saveRes = await api.put('/component-master', {
        name: name.trim(),
        components: components.map(c => ({
          id: c.id, name: c.name, percentage: Number(c.percentage), is_active: 1,
          children: c.children.map(ch => ({ id: ch.id, name: ch.name, percentage: Number(ch.percentage), is_active: 1 })),
        })),
      });
      await api.put(`/modules/${moduleCode}/scheme`, { scheme_id: saveRes.data.data.id });
      toast('Marking scheme created and assigned', 'success');
      onSaved?.();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal show={show} onClose={onClose} title={`${allowCreate ? 'Marking Scheme' : 'Assign Marking Scheme'} — ${moduleLabel || moduleCode}`} size="lg">
      {loadingSetup ? <Spinner minHeight="200px" /> : (
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{error}</div>}

          {!allowCreate && (
            <div className="alert alert-info py-2" style={{ fontSize: 12 }}>
              Create the scheme in <Link to="/records/components" onClick={onClose}>Component Master</Link> first, then choose it here to assign it to this module.
            </div>
          )}

          {allowCreate && <div className="sms-tabs" style={{ marginBottom: 16 }}>
            <button type="button" className={`sms-tab ${mode === 'existing' ? 'active' : ''}`} onClick={() => setMode('existing')}>Use existing scheme</button>
            <button type="button" className={`sms-tab ${mode === 'new' ? 'active' : ''}`} onClick={() => setMode('new')}>Create new scheme</button>
          </div>}

          {mode === 'existing' || !allowCreate ? (
            <div className="row g-3 mb-3">
              <div className="col-12">
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Marking Scheme *</label>
                {schemes.length === 0 ? (
                  <>
                    <EmptyState icon="bi-diagram-3" message={allowCreate ? "No marking schemes exist yet — switch to 'Create new scheme'" : 'No marking schemes exist yet — create one in Component Master first'} />
                    {!allowCreate && <div className="text-center" style={{ marginTop: -22 }}><Link to="/records/components" onClick={onClose} style={{ fontSize: 12 }}>Open Component Master</Link></div>}
                  </>
                ) : (
                  <SearchableSelect value={selectedSchemeId} placeholder="Select a scheme…"
                    onChange={e => setSelectedSchemeId(e.target.value)}
                    options={schemes.map(s => ({ value: s.id, label: `${s.name} (${s.component_count} components)` }))} />
                )}
              </div>
            </div>
          ) : (
          <>
          <div className="row g-3 mb-3">
            <div className="col-12">
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Scheme Name *</label>
              <input type="text" className="sms-form-control" value={name} placeholder="e.g. Standard 20/20/60" onChange={e => setName(e.target.value)} />
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Components</span>
              {!!components.length && (
                <span style={{ fontSize: 12, fontWeight: 500, color: Math.round(totalWeight * 10) / 10 === 100 ? 'var(--sms-stat-green)' : 'var(--sms-btn-outline-danger-txt)' }}>
                  Total: {fmtPct(totalWeight)}
                </span>
              )}
            </div>
            <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
              <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} onClick={addComponent}><i className="bi bi-plus-lg" /> Add component</button>
              <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12, padding: '4px 10px' }} disabled={!components.length} onClick={() => setPreview(p => !p)}>
                <i className={`bi ${preview ? 'bi-eye-slash' : 'bi-eye'}`} /> {preview ? 'Hide preview' : 'Preview'}
              </button>
            </div>
          </div>

          {!components.length ? (
            <EmptyState icon="bi-diagram-3" message="No components yet — add one to get started" />
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: 12 }}>
              <table className="sms-table sms-component-table">
                <thead><tr>
                  <th style={{ width: 28 }}></th>
                  <th>Name</th>
                  <th style={{ width: 110 }}>Weight %</th>
                  <th style={{ width: 90 }}></th>
                </tr></thead>
                <tbody>
                  {components.map(c => (
                    <React.Fragment key={c.tempId}>
                      <tr
                        draggable
                        onDragStart={() => setDragTempId(c.tempId)}
                        onDragOver={e => { e.preventDefault(); if (dragOverTempId !== c.tempId) setDragOverTempId(c.tempId); }}
                        onDragLeave={() => setDragOverTempId(id => id === c.tempId ? null : id)}
                        onDrop={e => { e.preventDefault(); reorderComponents(dragTempId, c.tempId); setDragTempId(null); setDragOverTempId(null); }}
                        onDragEnd={() => { setDragTempId(null); setDragOverTempId(null); }}
                        style={{
                          opacity: dragTempId === c.tempId ? 0.4 : 1,
                          boxShadow: dragOverTempId === c.tempId && dragTempId !== c.tempId ? 'inset 0 2px 0 var(--sms-acc)' : 'none',
                          transition: 'opacity .15s ease, box-shadow .1s ease',
                        }}
                      >
                        <td style={{ cursor: 'grab', textAlign: 'center' }} title="Drag to reorder"><i className="bi bi-grip-vertical" /></td>
                        <td data-label="Name"><input type="text" className="sms-form-control" value={c.name} placeholder="e.g. Assignment"
                          onChange={e => updateComponent(c.tempId, 'name', e.target.value)} /></td>
                        <td data-label="Weight %"><input type="number" step="0.1" min="0" max="100" className="sms-form-control" value={c.percentage}
                          onChange={e => updateComponent(c.tempId, 'percentage', e.target.value === '' ? '' : Number(e.target.value))} /></td>
                        <td>
                          <div className="d-flex gap-1">
                            <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '4px 8px' }} title="Add sub-category" onClick={() => addChild(c.tempId)}><i className="bi bi-diagram-2" /></button>
                            <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-btn-outline-danger-txt)' }} onClick={() => removeComponent(c.tempId)}><i className="bi bi-trash" /></button>
                          </div>
                        </td>
                      </tr>
                      {c.children.map(ch => (
                        <tr
                          key={ch.tempId}
                          draggable
                          onDragStart={() => setDragTempId(ch.tempId)}
                          onDragOver={e => { e.preventDefault(); if (dragOverTempId !== ch.tempId) setDragOverTempId(ch.tempId); }}
                          onDragLeave={() => setDragOverTempId(id => id === ch.tempId ? null : id)}
                          onDrop={e => { e.preventDefault(); reorderChildren(c.tempId, dragTempId, ch.tempId); setDragTempId(null); setDragOverTempId(null); }}
                          onDragEnd={() => { setDragTempId(null); setDragOverTempId(null); }}
                          style={{
                            background: 'var(--sms-sur2)',
                            opacity: dragTempId === ch.tempId ? 0.4 : 1,
                            boxShadow: dragOverTempId === ch.tempId && dragTempId !== ch.tempId ? 'inset 0 2px 0 var(--sms-acc)' : 'none',
                            transition: 'opacity .15s ease, box-shadow .1s ease',
                          }}
                        >
                          <td style={{ cursor: 'grab', color: 'var(--sms-acc)', textAlign: 'center' }} title="Drag to reorder sub-category"><i className="bi bi-arrows-move" /></td>
                          <td data-label="Name" style={{ paddingLeft: 12 }}>
                            <span style={{ color: 'var(--sms-txt3)', marginRight: 6 }}>—</span>
                            <input type="text" className="sms-form-control" style={{ display: 'inline-block', width: 'calc(100% - 20px)' }} value={ch.name} placeholder="e.g. Q1"
                              onChange={e => updateChild(c.tempId, ch.tempId, 'name', e.target.value)} />
                          </td>
                          <td data-label="Weight %"><input type="number" step="0.1" min="0" max="100" className="sms-form-control" value={ch.percentage}
                            onChange={e => updateChild(c.tempId, ch.tempId, 'percentage', e.target.value === '' ? '' : Number(e.target.value))} /></td>
                          <td>
                            <button type="button" className="sms-btn sms-btn-outline" style={{ padding: '4px 8px', color: 'var(--sms-btn-outline-danger-txt)' }} onClick={() => removeChild(c.tempId, ch.tempId)}><i className="bi bi-trash" /></button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview && !!leafComponents.length && (
            <div className="sms-card" style={{ marginBottom: 12, background: 'var(--sms-sur2)' }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', marginBottom: 8 }}>
                Preview — how trainers will see this when marking
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="sms-table" style={{ minWidth: 400 }}>
                  <thead>
                    <tr>
                      <th rowSpan={2}>Student</th>
                      {components.map(c => c.children.length
                        ? <th key={c.tempId} colSpan={c.children.length} style={{ textAlign: 'center' }}>{c.name || '(unnamed)'}</th>
                        : (
                          <th key={c.tempId} rowSpan={2} style={{ textAlign: 'center' }}>
                            {c.name || '(unnamed)'}
                            <div style={{ fontWeight: 400, color: 'var(--sms-txt3)', fontSize: 12 }}>{fmtPct(c.percentage || 0)}</div>
                          </th>
                        ))}
                    </tr>
                    {components.some(c => c.children.length) && (
                      <tr>
                        {components.filter(c => c.children.length).flatMap(c => c.children.map(ch => (
                          <th key={ch.tempId} style={{ textAlign: 'center' }}>
                            {ch.name || '(unnamed)'}
                            <div style={{ fontWeight: 400, color: 'var(--sms-txt3)', fontSize: 12 }}>{fmtPct(ch.percentage || 0)}</div>
                          </th>
                        )))}
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    <tr>
                      <td>Sample Student</td>
                      {leafComponents.map(l => (
                        <td key={l.tempId} style={{ textAlign: 'center' }}>
                          <input type="text" className="sms-form-control" style={{ width: 60, textAlign: 'center' }} disabled placeholder="0" />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>
          )}

          <SaveButtons saving={saving} onCancel={onClose} label={mode === 'existing' || !allowCreate ? 'Assign to Module' : 'Create & Assign'} />
        </form>
      )}
    </Modal>
  );
}

// ─── ATTENDANCE ──────────────────────────────────────────────────────────────
const DOT_LABEL = { present: 'Present', late:  'Late',    absent: 'Absent', mc: 'MC' };
const ATTENDANCE_STATUS_OPTIONS = Object.entries(DOT_LABEL).map(([value, label]) => ({ value, label }));
const sessionTypeKey = value => String(value || '').trim().toLowerCase();
const sessionTypeLabel = value => sessionTypeBadgeMap[sessionTypeKey(value)]?.label || (value ? String(value) : null);
const calculateAttendanceRate = ({ present = 0, late = 0, mc = 0, absent = 0, total = 0 }) =>
  total > 0 ? Math.round(((present + late + mc) / total) * 10000) / 100 : 0;

const fmtTime = dt => {
  if (!dt) return null;
  const d = new Date(dt);
  if (isNaN(d.getTime())) return String(dt).slice(11, 16);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
};

// "15 Jan" — used for narrow column headers
const fmtShortDate = value => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

// "2026" — year shown below the day/month in matrix column headers
const fmtYear = value => {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : String(d.getFullYear());
};

// "09:00" — used for attendance_sessions.start_time / end_time (MySQL TIME strings)
const fmtSessionTime = t => {
  if (!t) return null;
  const parts = String(t).split(':');
  return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : String(t);
};

// Safe sheet name: max 31 chars, no invalid chars
const safeSheet = str => str.replace(/[\\/*?[\]:]/g, '-').slice(0, 31);

// ── Excel styling palette ───────────────────────────────────────────────────
const XL = {
  brand:      'FF2563EB',
  brandText:  'FFFFFFFF',
  titleFill:  'FFEFF6FF',
  titleText:  'FF1E3A8A',
  band:       'FFF1F5F9',
  border:     'FF94A3B8',
  overallFill:'FFE2E8F0',
  present:    { fill: 'FFDCFCE7', text: 'FF065F46' },
  late:       { fill: 'FFFEF9C3', text: 'FF854D0E' },
  absent:     { fill: 'FFFEE2E2', text: 'FF991B1B' },
  mc:         { fill: 'FFE0F2FE', text: 'FF075985' },
  neutral:    { fill: 'FFE5E7EB', text: 'FF374151' },
};
const thinBorder = { style: 'thin', color: { rgb: XL.border } };
const allBorders  = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

function styleCell(ws, r, c, style) {
  const addr = XLSX.utils.encode_cell({ r, c });
  if (!ws[addr]) ws[addr] = { t: 's', v: '' };
  ws[addr].s = { ...(ws[addr].s || {}), ...style };
}
function setFormula(ws, r, c, formula, cachedValue) {
  const addr = XLSX.utils.encode_cell({ r, c });
  ws[addr] = { ...(ws[addr] || {}), t: 'n', f: formula, v: cachedValue, z: '0.00"%"' };
}
function styleRow(ws, r, colCount, style) {
  for (let c = 0; c < colCount; c++) styleCell(ws, r, c, style);
}
function rateStyle(pct) {
  const s = pct >= 80 ? XL.present : pct >= 60 ? XL.late : XL.absent;
  return {
    fill: { fgColor: { rgb: s.fill } },
    font: { bold: true, color: { rgb: s.text } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: allBorders,
  };
}
function statusStyle(status) {
  const s = status === 'present' ? XL.present : status === 'late' ? XL.late : status === 'absent' ? XL.absent : status === 'mc' ? XL.mc : XL.neutral;
  return {
    fill: { fgColor: { rgb: s.fill } },
    font: { bold: true, color: { rgb: s.text } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: allBorders,
  };
}
const headerStyle = {
  fill: { fgColor: { rgb: XL.brand } },
  font: { bold: true, color: { rgb: XL.brandText }, sz: 11 },
  alignment: { horizontal: 'center', vertical: 'center' },
  border: allBorders,
};
const titleStyle = {
  fill: { fgColor: { rgb: XL.titleFill } },
  font: { bold: true, color: { rgb: XL.titleText }, sz: 13 },
  alignment: { horizontal: 'left', vertical: 'center' },
};
const subTitleStyle = {
  font: { color: { rgb: 'FF1F2937' }, sz: 10 },
  alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
};
const bodyFont = { color: { rgb: 'FF111827' }, sz: 11 };
const bodyBorderStyle = { font: bodyFont, border: allBorders, alignment: { vertical: 'center' } };
const bandFillStyle   = { font: bodyFont, fill: { fgColor: { rgb: XL.band } }, border: allBorders, alignment: { vertical: 'center' } };
const overallRowStyle = {
  fill: { fgColor: { rgb: XL.overallFill } },
  font: { bold: true, color: { rgb: 'FF111827' } },
  border: { top: { style: 'medium', color: { rgb: 'FF475569' } }, bottom: thinBorder, left: thinBorder, right: thinBorder },
  alignment: { vertical: 'center' },
};
// Left for text, centre for short codes / numbers / times.
const alignLeft   = { horizontal: 'left',   vertical: 'center', wrapText: true };
const alignCenter = { horizontal: 'center', vertical: 'center' };
const withAlign = (style, alignment) => ({ ...style, alignment });
// Row heights (points): enough breathing room that 11pt text isn't cramped.
const XL_ROW = { title: 28, sub: 34, header: 26, body: 22 };

// `students` / `rows` are the already-filtered set the page is showing; `filterLabel`
// describes the active filters so the sheet says what it covers.
function exportAttendanceExcel({ intake, sessions, students, matrix, rows, filterLabel }) {
  const wb = XLSX.utils.book_new();
  const today = new Date().toISOString().slice(0, 10);

  // ── Sheet 1: Summary ──────────────────────────────────────────────────────
  const summaryHeader = ['#', 'Student Name', 'Present', 'Late', 'Absent', 'MC', 'Total Sessions', 'Actual (%)', 'Threshold (%)'];
  const summaryData = students.map((stu, idx) => {
    const stuRecs = matrix[stu.key] || {};
    let present = 0, late = 0, absent = 0, mc = 0;
    sessions.forEach(ses => {
      const r = stuRecs[ses.session_id];
      if (r) { if (r.status === 'present') present++; else if (r.status === 'late') late++; else if (r.status === 'mc') mc++; else absent++; }
    });
    const total    = sessions.length;
    const actual   = calculateAttendanceRate({ present, late, mc, absent, total });
    const threshold = 100 - actual;
    return [idx + 1, stu.name, present, late, absent, mc, total, actual, threshold];
  });

  const totP = rows.filter(r => r.status === 'present').length;
  const totL = rows.filter(r => r.status === 'late').length;
  const totA = rows.filter(r => r.status === 'absent').length;
  const totMc = rows.filter(r => r.status === 'mc').length;
  const totT = rows.length;
  const totActual = calculateAttendanceRate({ present: totP, late: totL, mc: totMc, absent: totA, total: totT });
  const totThreshold = 100 - totActual;

  const TITLE_ROW = 0, SUB_ROW = 1, RATE_GROUP_ROW = 2, HEADER_ROW = 3, DATA_START = 4;
  const RATE_COL_START = summaryHeader.length - 2; // Actual (%) column index
  const aoa = [];
  aoa[TITLE_ROW] = [`Attendance Summary — Intake ${intake}`];
  aoa[SUB_ROW]   = [`Intake: ${intake}  ·  Generated ${fmtDate(today)}  ·  ${students.length} students  ·  ${sessions.length} sessions\nFilters: ${filterLabel || 'None'}`];
  aoa[RATE_GROUP_ROW] = [];
  aoa[RATE_GROUP_ROW][RATE_COL_START] = 'Rate';
  aoa[HEADER_ROW] = summaryHeader;
  summaryData.forEach((row, i) => { aoa[DATA_START + i] = row; });
  const overallRowIdx = DATA_START + summaryData.length;
  aoa[overallRowIdx] = ['', 'OVERALL', totP, totL, totA, totMc, totT, totActual, totThreshold];

  const wsSum = XLSX.utils.aoa_to_sheet(aoa);
  summaryData.forEach((row, i) => {
    const excelRow = DATA_START + i + 1;
    setFormula(wsSum, DATA_START + i, 7, `=IFERROR(ROUND((C${excelRow}+D${excelRow}+F${excelRow})/G${excelRow}*100,2),0)`, row[7]);
    setFormula(wsSum, DATA_START + i, 8, `=100-H${excelRow}`, row[8]);
  });
  const overallExcelRow = overallRowIdx + 1;
  setFormula(wsSum, overallRowIdx, 7, `=IFERROR(ROUND((C${overallExcelRow}+D${overallExcelRow}+F${overallExcelRow})/G${overallExcelRow}*100,2),0)`, totActual);
  setFormula(wsSum, overallRowIdx, 8, `=100-H${overallExcelRow}`, totThreshold);
  wsSum['!cols'] = [{ wch: 4 }, { wch: 32 }, { wch: 10 }, { wch: 8 }, { wch: 10 }, { wch: 8 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
  wsSum['!merges'] = [
    { s: { r: TITLE_ROW, c: 0 }, e: { r: TITLE_ROW, c: summaryHeader.length - 1 } },
    { s: { r: SUB_ROW,   c: 0 }, e: { r: SUB_ROW,   c: summaryHeader.length - 1 } },
    { s: { r: RATE_GROUP_ROW, c: RATE_COL_START }, e: { r: RATE_GROUP_ROW, c: RATE_COL_START + 1 } },
  ];
  styleRow(wsSum, TITLE_ROW, summaryHeader.length, titleStyle);
  styleRow(wsSum, SUB_ROW, summaryHeader.length, subTitleStyle);
  styleCell(wsSum, RATE_GROUP_ROW, RATE_COL_START, headerStyle);
  styleCell(wsSum, RATE_GROUP_ROW, RATE_COL_START + 1, headerStyle);
  styleRow(wsSum, HEADER_ROW, summaryHeader.length, headerStyle);
  styleCell(wsSum, HEADER_ROW, 1, withAlign(headerStyle, alignLeft));
  summaryData.forEach((row, i) => {
    const r = DATA_START + i;
    const base = i % 2 === 1 ? bandFillStyle : bodyBorderStyle;
    for (let c = 0; c < summaryHeader.length; c++) {
      styleCell(wsSum, r, c, withAlign(base, c === 1 ? alignLeft : alignCenter));
    }
    styleCell(wsSum, r, summaryHeader.length - 2, rateStyle(row[7]));
    styleCell(wsSum, r, summaryHeader.length - 1, rateStyle(row[8]));
  });
  styleRow(wsSum, overallRowIdx, summaryHeader.length, withAlign(overallRowStyle, alignCenter));
  styleCell(wsSum, overallRowIdx, 1, withAlign(overallRowStyle, alignLeft));
  styleCell(wsSum, overallRowIdx, summaryHeader.length - 2, { ...rateStyle(totActual), border: overallRowStyle.border });
  styleCell(wsSum, overallRowIdx, summaryHeader.length - 1, { ...rateStyle(totThreshold), border: overallRowStyle.border });
  wsSum['!rows'] = [];
  wsSum['!rows'][TITLE_ROW] = { hpt: XL_ROW.title };
  wsSum['!rows'][SUB_ROW] = { hpt: XL_ROW.sub };
  wsSum['!rows'][RATE_GROUP_ROW] = { hpt: XL_ROW.header };
  wsSum['!rows'][HEADER_ROW] = { hpt: XL_ROW.header };
  for (let r = DATA_START; r <= overallRowIdx; r++) wsSum['!rows'][r] = { hpt: XL_ROW.body };
  XLSX.utils.book_append_sheet(wb, wsSum, 'Summary');

  // ── One sheet per session ─────────────────────────────────────────────────
  sessions.forEach((ses, si) => {
    const header = ['#', 'Student Name', 'Status', 'Clock In', 'Clock Out', 'Notes'];
    const dataRows = students.map((stu, idx) => {
      const rec = (matrix[stu.key] || {})[ses.session_id];
      return [
        idx + 1,
        stu.name,
        rec ? DOT_LABEL[rec.status] : 'No Record',
        rec?.clock_in_time  ? fmtTime(rec.clock_in_time)  : '—',
        rec?.clock_out_time ? fmtTime(rec.clock_out_time) : '—',
        rec?.notes || '',
      ];
    });

    const T_ROW = 0, S_ROW = 1, H_ROW = 3, D_START = 4;
    const aoaSes = [];
    aoaSes[T_ROW] = [`Session ${si + 1}: ${fmtSessionName(ses) || 'Untitled Session'}`];
    const sesTime = ses.start_time || ses.end_time ? `${fmtSessionTime(ses.start_time) || '—'} – ${fmtSessionTime(ses.end_time) || '—'}` : '—';
    aoaSes[S_ROW] = [`Intake: ${intake}   |   Date: ${fmtDate(ses.session_date)}   |   Time: ${sesTime}\nType: ${sessionTypeLabel(ses.session_type) || '—'}   |   Trainer: ${ses.trainer_name || '—'}`];
    aoaSes[2] = [];
    aoaSes[H_ROW] = header;
    dataRows.forEach((row, i) => { aoaSes[D_START + i] = row; });

    const ws = XLSX.utils.aoa_to_sheet(aoaSes);
    ws['!cols'] = [{ wch: 4 }, { wch: 32 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 30 }];
    ws['!merges'] = [
      { s: { r: T_ROW, c: 0 }, e: { r: T_ROW, c: header.length - 1 } },
      { s: { r: S_ROW, c: 0 }, e: { r: S_ROW, c: header.length - 1 } },
    ];
    styleRow(ws, T_ROW, header.length, titleStyle);
    styleRow(ws, S_ROW, header.length, subTitleStyle);
    styleRow(ws, H_ROW, header.length, headerStyle);
    styleCell(ws, H_ROW, 1, withAlign(headerStyle, alignLeft));
    styleCell(ws, H_ROW, 5, withAlign(headerStyle, alignLeft));
    dataRows.forEach((row, i) => {
      const r = D_START + i;
      const base = i % 2 === 1 ? bandFillStyle : bodyBorderStyle;
      for (let c = 0; c < header.length; c++) {
        // Names and notes read left-aligned; #, status and times are short, so centre them.
        styleCell(ws, r, c, withAlign(base, c === 1 || c === 5 ? alignLeft : alignCenter));
      }
      const statusKey = rec => rec === 'Present' ? 'present' : rec === 'Late' ? 'late' : rec === 'Absent' ? 'absent' : rec === 'MC' ? 'mc' : 'none';
      styleCell(ws, r, 2, statusStyle(statusKey(row[2])));
    });
    ws['!rows'] = [];
    ws['!rows'][T_ROW] = { hpt: XL_ROW.title };
    ws['!rows'][S_ROW] = { hpt: XL_ROW.sub };
    ws['!rows'][H_ROW] = { hpt: XL_ROW.header };
    for (let i = 0; i < dataRows.length; i++) ws['!rows'][D_START + i] = { hpt: XL_ROW.body };

    const sheetName = safeSheet(`${si + 1}. ${fmtShortDate(ses.session_date)}`);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  XLSX.writeFile(wb, `Attendance_${intake}_${today}.xlsx`);
}

const isoOfDate = localDateISO;

// Monday of the week containing the given ISO date (or today).
function mondayOf(iso) {
  const d = iso ? new Date(`${iso.slice(0, 10)}T00:00:00`) : new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return isoOfDate(d);
}

function attendancePresetRange(kind, intake) {
  const today = localDateISO();
  const t = new Date(`${today}T00:00:00`);
  if (kind === 'week') return { from: mondayOf(today), to: today };
  if (kind === '7days') { t.setDate(t.getDate() - 6); return { from: isoOfDate(t), to: today }; }
  if (kind === 'month') return { from: `${today.slice(0, 8)}01`, to: today };
  if (kind === 'intake' && intake?.soc_date) {
    const eoc = (intake.eoc_date || '').slice(0, 10);
    return { from: intake.soc_date.slice(0, 10), to: eoc && eoc < today ? eoc : today };
  }
  return { from: '', to: '' };
}

export function AttendancePage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [searchParams] = useSearchParams();
  const canEditAttendance = can('attendance', 'update') || can('attendance', 'view');
  const [intakes,  setIntakes]  = useState([]);
  const [intake,   setIntake]   = useState(() => searchParams.get('intake_code') || '');
  const [fromDate, setFromDate] = useState(() => attendancePresetRange('week').from);
  const [toDate, setToDate] = useState(() => localDateISO());
  const [modules,  setModules]  = useState([]);
  const [module_,  setModule]   = useState('');
  const [sessionTypeFilter, setSessionTypeFilter] = useState('');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState(false);
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [tooltip,  setTooltip]  = useState(null);
  const [search,   setSearch]   = useState('');
  const [flagOverrides, setFlagOverrides] = useState({});
  const [sending, setSending] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [attendanceForm, setAttendanceForm] = useState({ status: 'present' });
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [showAttendanceThreshold, setShowAttendanceThreshold] = useState(false);
  const [attendanceThreshold, setAttendanceThreshold] = useState('');
  const [thresholdSaving, setThresholdSaving] = useState(false);
  const [thresholdError, setThresholdError] = useState('');
  const tooltipTimer            = useRef(null);

  useEffect(() => { api.get('/intakes').then(r => setIntakes(r.data.data)).catch(() => {}); }, []);

  const selectedIntake = intakes.find(i => i.intake_code === intake);

  const weekRange = (() => {
    if (!fromDate && !toDate) return { from: '', to: '', label: 'All dates' };
    const label = fromDate && toDate ? `${fmtDate(fromDate)} – ${fmtDate(toDate)}`
      : fromDate ? `From ${fmtDate(fromDate)}` : `Up to ${fmtDate(toDate)}`;
    return { from: fromDate, to: toDate, label };
  })();

  const openAttendanceThreshold = () => {
    setAttendanceThreshold(selectedIntake?.min_attendance_rate ?? '');
    setThresholdError('');
    setShowAttendanceThreshold(true);
  };

  const saveAttendanceThreshold = async e => {
    e.preventDefault();
    const value = Number(attendanceThreshold);
    if (!selectedIntake || !Number.isInteger(value) || value < 0 || value > 100) {
      setThresholdError('Enter a whole number between 0 and 100.');
      return;
    }
    setThresholdSaving(true);
    try {
      await api.put(`/intakes/${selectedIntake.id}`, {
        intake_code: selectedIntake.intake_code,
        course_code: selectedIntake.course_code,
        soc_date: selectedIntake.soc_date,
        eoc_date: selectedIntake.eoc_date,
        intake_type: selectedIntake.intake_type,
        capacity: selectedIntake.capacity,
        min_attendance_rate: value,
        min_assessment_result: selectedIntake.min_assessment_result,
        is_active: selectedIntake.is_active,
      });
      setIntakes(current => current.map(i => i.id === selectedIntake.id ? { ...i, min_attendance_rate: value } : i));
      setShowAttendanceThreshold(false);
      toast('Minimum attendance rate updated', 'success');
    } catch (err) {
      setThresholdError(err.response?.data?.message || 'Failed to update attendance rate.');
    } finally {
      setThresholdSaving(false);
    }
  };

  useEffect(() => {
    setModule('');
    setModules([]);
    if (!selectedIntake) return;
    api.get(`/intakes/${selectedIntake.id}/modules`).then(r => setModules(r.data.data)).catch(() => {});
  }, [selectedIntake?.id]);

  const load = async ({ resetView = true } = {}) => {
    if (!intake) return;
    setLoading(true);
    try {
      const params = {};
      if (weekRange.from) params.from_date = weekRange.from;
      if (weekRange.to) params.to_date = weekRange.to;
      if (module_) params.module_code = module_;
      if (sessionTypeFilter) params.session_type = sessionTypeFilter;
      const res = await api.get(`/attendance/intake/${intake}`, { params });
      setRows(res.data.data);
      if (resetView) {
        setSearch('');
        setFlagOverrides({});
      }
    } catch { toast('Failed to load attendance', 'error'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [intake, module_, sessionTypeFilter, weekRange.from, weekRange.to]);

  // ── Pivot ─────────────────────────────────────────────────────────────────
  const sessions = React.useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (!map.has(r.session_id))
        map.set(r.session_id, {
          session_id: r.session_id, session_date: r.session_date,
          session_name: r.session_name, session_module_name: r.session_module_name, start_time: r.start_time, end_time: r.end_time,
          trainer_name: r.trainer_name, session_type: r.session_type,
        });
    });
    return [...map.values()].sort((a, b) => {
      const d = (a.session_date || '').localeCompare(b.session_date || '');
      return d !== 0 ? d : (a.start_time || '').localeCompare(b.start_time || '');
    });
  }, [rows]);

  // Group sessions that fall on the same calendar day into one matrix column
  const dateGroups = React.useMemo(() => {
    const map = new Map();
    sessions.forEach(ses => {
      const dateKey = (ses.session_date || '').slice(0, 10);
      if (!map.has(dateKey)) map.set(dateKey, { dateKey, date: ses.session_date, sessions: [] });
      map.get(dateKey).sessions.push(ses);
    });
    return [...map.values()];
  }, [sessions]);

  // Consecutive date columns grouped by Monday–Sunday week, for the grouping header row.
  const weekGroups = React.useMemo(() => {
    const groups = [];
    dateGroups.forEach(grp => {
      const monday = mondayOf(grp.dateKey);
      const last = groups[groups.length - 1];
      if (last && last.monday === monday) last.count += 1;
      else groups.push({ monday, count: 1 });
    });
    return groups;
  }, [dateGroups]);

  const students = React.useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      const key = r.student_id ? `id_${r.student_id}` : (r.student_email || r.student_phone);
      if (!map.has(key)) map.set(key, { key, student_no: r.student_no || null, name: r.name || r.student_phone || r.student_email || '—' });
    });
    return [...map.values()];
  }, [rows]);

  const matrix = React.useMemo(() => {
    const m = {};
    rows.forEach(r => {
      const key = r.student_id ? `id_${r.student_id}` : (r.student_email || r.student_phone);
      if (!m[key]) m[key] = {};
      m[key][r.session_id] = r;
    });
    return m;
  }, [rows]);

  // ── Overall stats ──────────────────────────────────────────────────────────
  const totalPresent = rows.filter(r => r.status === 'present').length;
  const totalLate    = rows.filter(r => r.status === 'late').length;
  const totalAbsent  = rows.filter(r => r.status === 'absent').length;
  const totalMc      = rows.filter(r => r.status === 'mc').length;
  const totalRecs    = rows.length;
  // Attendance rate: (Present + Late + MC) / Total Sessions.
  const overallActual = calculateAttendanceRate({ present: totalPresent, late: totalLate, mc: totalMc, total: totalRecs });
  const rateColor    = pct => pct >= 80 ? 'var(--sms-dot-present)' : pct >= 60 ? 'var(--sms-dot-late)' : 'var(--sms-dot-absent)';

  const minRate = selectedIntake?.min_attendance_rate;
  const calculateAttendanceThreshold = actualRate => 100 - actualRate;
  const attendanceRateColor = pct => minRate != null
    ? (pct >= minRate ? 'var(--sms-dot-present)' : 'var(--sms-dot-absent)')
    : rateColor(pct);

  // Per-student actual attendance, computed once so the table, sorting, and
  // flagging all read the same numbers instead of each recomputing them.
  const studentStats = React.useMemo(() => students.map(stu => {
    const stuRecs = matrix[stu.key] || {};
    const counts = sessions.reduce((acc, s) => {
      const status = stuRecs[s.session_id]?.status;
      if (status === 'present') acc.present++;
      else if (status === 'late') acc.late++;
      else if (status === 'mc') acc.mc++;
      else if (status === 'absent') acc.absent++;
      return acc;
    }, { present: 0, late: 0, absent: 0, mc: 0 });
    const totalSessions = sessions.length;
    const actualRate = calculateAttendanceRate({ ...counts, total: totalSessions });
    return { ...stu, actualRate };
  }), [students, matrix, sessions]);

  // Sortable by Student (default) or Actual — clicking a header
  // re-sorts the whole list, not just the current page.
  const { sorted: sortedStudents, sortKey: stuSortKey, sortDir: stuSortDir, toggleSort: toggleStuSort } =
    useSort(studentStats, 'name', 'asc');

  const filteredStudents = React.useMemo(() => {
    const query = search.toLowerCase();
    return sortedStudents.filter(s => {
      const matchesSearch = !query || s.name.toLowerCase().includes(query);
      const needsWarning = minRate != null && calculateAttendanceThreshold(s.actualRate) > minRate;
      const matchesStatus = !attendanceStatusFilter || needsWarning;
      return matchesSearch && matchesStatus;
    });
  }, [sortedStudents, search, attendanceStatusFilter, minRate]);

  // Pagination over filtered students
  const {
    paged: pagedStudents, page, setPage,
    pageSize, setPageSize, totalPages, total: stuTotal, start: stuStart,
  } = usePagination(filteredStudents, 20);

  // ── Below-minimum flagging ──────────────────────────────────────────────────
  // Students whose attendance threshold exceeds the intake minimum are auto-flagged;
  // staff can untick individual students before sending.
  const isFlagged = key => {
    if (flagOverrides[key] !== undefined) return flagOverrides[key];
    if (minRate == null) return false;
    const stat = studentStats.find(s => s.key === key);
    return !!stat && calculateAttendanceThreshold(stat.actualRate) > minRate;
  };
  const flaggedCount = students.filter(s => isFlagged(s.key)).length;

  // Sends via whichever channel(s) each flagged student opted into
  // (students.notify_email / notify_whatsapp) — the backend looks that up
  // per student_no, this just supplies who's flagged and their numbers.
  const handleSendNotify = async () => {
    const flagged = studentStats.filter(s => isFlagged(s.key));
    const missingNo = flagged.filter(s => !s.student_no);
    const payload = flagged.filter(s => s.student_no).map(s => ({
      student_no: s.student_no, name: s.name,
      actual_rate: s.actualRate, threshold: minRate ?? 0,
    }));
    if (!payload.length) {
      toast('None of the flagged students could be matched to a student record', 'error');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/attendance/notify', { intake_code: intake, students: payload });
      const results = res.data.data;
      const sentCount = results.filter(r => r.status === 'sent').length;
      const failedCount = results.length - sentCount;
      toast(
        failedCount
          ? `Sent to ${sentCount} of ${results.length} student(s) — ${failedCount} failed/skipped`
          : `Sent to ${sentCount} student(s)`,
        failedCount ? 'error' : 'success'
      );
      if (missingNo.length) {
        toast(`${missingNo.length} flagged student(s) had no matching record and were skipped`, 'error');
      }
    } catch {
      toast('Failed to send notifications', 'error');
    } finally {
      setSending(false);
    }
  };

  // ── Tooltip ────────────────────────────────────────────────────────────────
  const showTooltip = (e, rec) => {
    clearTimeout(tooltipTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ rec, x: rect.left + rect.width / 2, y: rect.top });
  };
  const hideTooltip = () => {
    tooltipTimer.current = setTimeout(() => setTooltip(null), 80);
  };

  const openAttendanceEditor = rec => {
    setTooltip(null);
    setAttendanceError('');
    setAttendanceForm({ status: rec.status || 'present' });
    setEditingAttendance(rec);
  };

  const handleAttendanceSave = async e => {
    e.preventDefault();
    if (!editingAttendance) return;
    if (!attendanceForm.status) {
      setAttendanceError('Please choose a status.');
      return;
    }
    setAttendanceSaving(true);
    try {
      await api.put(`/attendance/${editingAttendance.id}`, { status: attendanceForm.status });
      toast('Attendance status updated', 'success');
      setEditingAttendance(null);
      await load({ resetView: false });
    } catch (err) {
      setAttendanceError(err.response?.data?.message || 'Update failed');
    } finally {
      setAttendanceSaving(false);
    }
  };

  const hasData = intake && !loading && rows.length > 0;

  return (
    <>
      <PageHeader
        title="Attendance Record"
        actions={hasData && (
          <button className="sms-btn sms-btn-outline" onClick={() => {
            // Same students the table shows (search + "needs warning" applied, all pages).
            const keys = new Set(filteredStudents.map(s => s.key));
            const exportRows = rows.filter(r => keys.has(r.student_id ? `id_${r.student_id}` : (r.student_email || r.student_phone)));
            const moduleName = modules.find(m => m.module_code === module_)?.module_name;
            const filterLabel = [
              `Dates: ${weekRange.label}`,
              module_ && `Module: ${moduleName || module_}`,
              sessionTypeFilter && `Session type: ${sessionTypeLabel(sessionTypeFilter) || sessionTypeFilter}`,
              search.trim() && `Student search: "${search.trim()}"`,
              attendanceStatusFilter && 'Needs warning only',
            ].filter(Boolean).join('  ·  ');
            exportAttendanceExcel({ intake, sessions, students: filteredStudents, matrix, rows: exportRows, filterLabel });
          }}>
            <i className="bi bi-file-earmark-excel" aria-hidden="true" /> Export Excel
          </button>
        )}
      />

      {/* ── Filters ─────────────────────────────────────────────────────────── */}
      <div className="sms-card mb-3">
        <div className="row g-3">
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="att-intake" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake</label>
            <SearchableSelect id="att-intake" value={intake} onChange={e => setIntake(e.target.value)} placeholder="-- Select intake --"
              options={intakes.map(i => ({
                value: i.intake_code,
                label: i.intake_code,
                sublabel: (i.soc_date && i.eoc_date) ? `${fmtDate(i.soc_date)} – ${fmtDate(i.eoc_date)}` : '',
              }))} />
          </div>
          <div className="col-12 col-md-6 col-xl-3">
            <label htmlFor="att-module" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module</label>
            <SearchableSelect id="att-module" value={module_} onChange={e => setModule(e.target.value)}
              placeholder={intake ? '-- All modules --' : 'Select an intake first'} disabled={!intake}
              options={modules.map(m => ({ value: m.module_code, label: m.module_name, right: m.module_code }))} />
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <label htmlFor="att-from" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Date Range</label>
            <div className="sms-att-range">
              <input id="att-from" type="date" className="sms-form-control" aria-label="From date" value={fromDate} max={toDate || localDateISO()}
                onChange={e => { const v = e.target.value; setFromDate(v); if (v && toDate && v > toDate) setToDate(v); }} disabled={!intake} />
              <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>to</span>
              <input id="att-to" type="date" className="sms-form-control" aria-label="To date" value={toDate} min={fromDate || undefined} max={localDateISO()}
                onChange={e => { const v = e.target.value > localDateISO() ? localDateISO() : e.target.value; setToDate(v); if (v && fromDate && v < fromDate) setFromDate(v); }} disabled={!intake} />
            </div>
            <div className="sms-att-presets" role="group" aria-label="Date range presets">
              {[['week', 'This week'], ['7days', 'Last 7 days'], ['month', 'This month'], ['intake', 'Whole intake']].map(([kind, text]) => {
                const r = attendancePresetRange(kind, selectedIntake);
                const active = r.from && r.from === fromDate && r.to === toDate;
                return (
                  <button key={kind} type="button" className={`sms-att-preset${active ? ' active' : ''}`}
                    disabled={!intake || (kind === 'intake' && !r.from)}
                    onClick={() => { setFromDate(r.from); setToDate(r.to); }}>{text}</button>
                );
              })}
            </div>
            {!weekRange.from && !weekRange.to && (
              <div style={{ fontSize: 12, color: 'var(--sms-txt3)', marginTop: 4 }}>Showing all dates.</div>
            )}
          </div>
          <div className="col-12 col-md-6 col-xl-2">
            <label htmlFor="att-session-type" style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Session Type</label>
            <SearchableSelect id="att-session-type" value={sessionTypeFilter} onChange={e => setSessionTypeFilter(e.target.value)}
              placeholder={intake ? '-- All session types --' : 'Select an intake first'} disabled={!intake}
              options={Object.entries(sessionTypeBadgeMap).map(([value, cfg]) => ({ value, label: cfg.label }))} />
          </div>
        </div>
        {selectedIntake && selectedIntake.min_attendance_rate == null && (
          <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 mb-0 mt-3" style={{ fontSize: 12 }}>
            <span><i className="bi bi-info-circle" style={{ marginRight: 6 }} />No minimum attendance rate is set for this intake.</span>
            {can('intake_master', 'update') && (
              <button type="button" className="sms-btn sms-btn-outline-warning" style={{ whiteSpace: 'nowrap', padding: '5px 9px', fontSize: 12 }} onClick={openAttendanceThreshold}>
                Set minimum rate
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Summary cards ───────────────────────────────────────────────────── */}
      {hasData && (
        <div className="row g-2 mb-3 sms-stat-anim-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))' }} role="region" aria-label="Attendance summary">
          {[
            { label: 'Students',  val: students.length,      color: 'var(--sms-txt)' },
            { label: 'Sessions',  val: sessions.length,      color: 'var(--sms-txt)' },
            { label: 'Present',   val: totalPresent,         color: 'var(--sms-dot-present)' },
            { label: 'Late',      val: totalLate,            color: 'var(--sms-dot-late)' },
            { label: 'Absent',    val: totalAbsent,          color: 'var(--sms-dot-absent)' },
            { label: 'MC',        val: totalMc,              color: 'var(--sms-dot-mc)' },
            { label: 'Overall',   val: `${fmtPct(overallActual)}`,  color: rateColor(overallActual) },
          ].map(c => (
            <div key={c.label} className="col">
              <div className="sms-stat-card text-center" style={{ padding: '14px 10px' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: c.color }} aria-label={`${c.label}: ${c.val}`}>{c.val}</div>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', marginTop: 2 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Matrix card ─────────────────────────────────────────────────────── */}
      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!intake
          ? <EmptyState icon="bi-calendar3" message="Select an intake to view attendance" />
          : loading
            ? <Spinner minHeight="240px" />
            : rows.length === 0
              ? <EmptyState message="No attendance records found" />
              : (
                <>
                  {/* Toolbar */}
                  <div className="sms-am-toolbar">
                    <div className="sms-am-search">
                      <i className="bi bi-search" aria-hidden="true"
                        style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--sms-txt2)', fontSize: 13 }} />
                      <input
                        className="sms-form-control" style={{ paddingLeft: 30 }}
                        placeholder="Search student…" aria-label="Search students"
                        value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                      />
                    </div>
                    <label htmlFor="att-status" className="sms-am-status-filter">
                      <input
                        id="att-status"
                        type="checkbox"
                        checked={attendanceStatusFilter}
                        onChange={e => setAttendanceStatusFilter(e.target.checked)}
                        disabled={!intake || minRate == null}
                      />
                      <span>Needs warning only</span>
                    </label>
                    {/* Legend */}
                    <div className="sms-am-legend" role="list" aria-label="Status legend">
                      {Object.entries(DOT_LABEL).map(([status, label]) => (
                        <div key={status} role="listitem" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 500, color: 'var(--sms-txt)' }}>
                          <span className={`sms-attend-dot ${status}`} aria-hidden="true" />
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Scrollable matrix */}
                  <div className="sms-am-scroll" role="region" aria-label="Attendance matrix" tabIndex={0}>
                    <table className="sms-attend-matrix" aria-label={`Attendance for intake ${intake}`}>
                      <thead>
                        <tr>
                          <th scope="col" rowSpan={2} className="sms-am-sticky sms-am-head-name"
                            onClick={() => toggleStuSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}
                            aria-sort={stuSortKey === 'name' ? (stuSortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              Student
                              <i className={`bi ${stuSortKey === 'name' ? (stuSortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up'}`}
                                aria-hidden="true" style={{ fontSize: 12, color: stuSortKey === 'name' ? 'var(--sms-acc)' : 'var(--sms-txt3)' }} />
                            </span>
                            <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', marginTop: 1 }}>
                              {stuTotal} student{stuTotal !== 1 ? 's' : ''}
                            </span>
                          </th>
                          {weekGroups.map((wk, i) => (
                            <th key={wk.monday} scope="colgroup" colSpan={wk.count} className="sms-am-week-hd">
                              Wk {i + 1} · {fmtShortDate(wk.monday)}
                            </th>
                          ))}
                          <th scope="colgroup" colSpan={2} className="sms-am-rate-hd sms-am-rate-group-hd">Rate</th>
                          <th scope="col" rowSpan={2} className="sms-am-rate-hd" style={{ textAlign: 'center' }}>Flag</th>
                        </tr>
                        <tr>
                          {dateGroups.map((grp, i) => (
                            <th key={grp.dateKey} scope="col" className="sms-am-col-hd"
                              title={`${fmtDate(grp.date)}\n${grp.sessions.map(s => `${sessionTypeLabel(s.session_type) || 'Session'} — ${fmtSessionName(s) || 'Untitled'}`).join('\n')}`}
                              aria-label={`Column ${i + 1}: ${fmtDate(grp.date)}${grp.sessions.length > 1 ? ` (${grp.sessions.length} sessions)` : ''}`}>
                              <div style={{ fontWeight: 700 }}>{i + 1}</div>
                              <div style={{ fontWeight: 600, color: 'var(--sms-txt2)', fontSize: 12, whiteSpace: 'nowrap' }}>
                                {fmtShortDate(grp.date)}
                              </div>
                              <div style={{ fontWeight: 500, color: 'var(--sms-txt3)', fontSize: 12, whiteSpace: 'nowrap' }}>
                                {fmtYear(grp.date)}
                              </div>
                              {[...new Set(grp.sessions.map(s => sessionTypeKey(s.session_type)).filter(Boolean))].map(type => (
                                <span key={type} className={`badge rounded-pill sms-am-session-type ${sessionTypeBadgeMap[type]?.cls || 'badge-inactive'}`}>
                                  {sessionTypeLabel(type)}
                                </span>
                              ))}
                              {grp.sessions.length > 1 && (
                                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt3)' }}>{grp.sessions.length}×</div>
                              )}
                            </th>
                          ))}
                          <th scope="col" className="sms-am-rate-hd sms-am-rate-actual"
                            onClick={() => toggleStuSort('actualRate')} style={{ cursor: 'pointer', userSelect: 'none' }}
                            aria-sort={stuSortKey === 'actualRate' ? (stuSortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              Actual
                              <i className={`bi ${stuSortKey === 'actualRate' ? (stuSortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down') : 'bi-arrow-down-up'}`}
                                aria-hidden="true" style={{ fontSize: 12, color: stuSortKey === 'actualRate' ? 'var(--sms-acc)' : 'var(--sms-txt3)' }} />
                              <i
                                className="bi bi-info-circle" aria-hidden="true" tabIndex={0}
                                onClick={e => e.stopPropagation()}
                                style={{ color: 'var(--sms-acc)', fontSize: 12, cursor: 'help' }}
                                title="(Present + Late + MC) ÷ Total Sessions, rounded to a whole percent."
                              />
                            </span>
                          </th>
                          <th scope="col" className="sms-am-rate-hd sms-am-rate-reduce"
                            style={{ textAlign: 'center' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              Threshold
                              <i
                                className="bi bi-info-circle" aria-hidden="true" tabIndex={0}
                                onClick={e => e.stopPropagation()}
                                style={{ color: 'var(--sms-acc)', fontSize: 12, cursor: 'help' }}
                                title="Threshold is calculated as 100% minus the student's actual attendance. A warning is shown when it exceeds the configured minimum rate."
                              />
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedStudents.map((stu, idx) => {
                          const stuRecs = matrix[stu.key] || {};
                          const { actualRate } = stu;
                          const threshold = calculateAttendanceThreshold(actualRate);
                          const needsWarning = minRate != null && threshold > minRate;

                          return (
                            <tr key={stu.key}>
                              <td className="sms-am-sticky sms-am-name">
                                <span className="sms-am-rownum" aria-hidden="true">{stuStart + idx + 1}</span>
                                {stu.name}
                              </td>
                              {dateGroups.map(grp => {
                                const recs = grp.sessions.map(ses => stuRecs[ses.session_id]).filter(Boolean);
                                return (
                                  <td key={grp.dateKey} className="sms-am-cell">
                                    <div className="sms-am-dot-group">
                                      {recs.map((rec, di) => (
                                      <span
                                          key={di}
                                          role="img"
                                          className={`sms-attend-dot ${rec.status}`}
                                          aria-label={`${DOT_LABEL[rec.status]} on ${fmtDate(rec.session_date)}${fmtSessionName(rec) ? ' — ' + fmtSessionName(rec) : ''}`}
                                          title={canEditAttendance ? 'Click to change status' : undefined}
                                          onMouseEnter={e => showTooltip(e, rec)}
                                          onMouseLeave={hideTooltip}
                                          onFocus={e => showTooltip(e, rec)}
                                          onBlur={hideTooltip}
                                          onClick={canEditAttendance ? e => { e.stopPropagation(); openAttendanceEditor(rec); } : undefined}
                                          onKeyDown={canEditAttendance ? e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              openAttendanceEditor(rec);
                                            }
                                          } : undefined}
                                          tabIndex={0}
                                          style={canEditAttendance ? { cursor: 'pointer' } : undefined}
                                        />
                                      ))}
                                    </div>
                                  </td>
                                );
                              })}
                              <td className="sms-am-rate sms-am-rate-actual" style={{ color: attendanceRateColor(actualRate) }} aria-label={`${fmtPct(actualRate)} actual attendance rate`}>
                                {fmtPct(actualRate)}
                              </td>
                              <td className="sms-am-rate sms-am-rate-reduce" style={{ color: minRate != null ? 'var(--sms-txt2)' : 'var(--sms-txt3)' }} aria-label={minRate != null ? `${fmtPct(threshold)} calculated attendance threshold` : 'No attendance threshold'}>
                                {minRate != null ? `${fmtPct(threshold)}` : '—'}
                              </td>
                              <td className="sms-am-rate" style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={isFlagged(stu.key)}
                                  onChange={e => setFlagOverrides(f => ({ ...f, [stu.key]: e.target.checked }))}
                                  aria-label={`Flag ${stu.name} for below-minimum attendance`}
                                />
                              </td>
                            </tr>
                          );
                        })}

                        {/* Overall footer row */}
                        <tr className="sms-am-overall">
                          <th scope="row" className="sms-am-sticky sms-am-overall-lbl">Overall</th>
                          {dateGroups.map(grp => {
                            // One rate per session, in the same order as the dots above,
                            // so two classes on one date are never blended together.
                            const sesRates = grp.sessions.map(ses => {
                              const sesRows = rows.filter(r => r.session_id === ses.session_id);
                              if (!sesRows.length) return { ses, rate: null };
                              const count = status => sesRows.filter(r => r.status === status).length;
                              return { ses, rate: calculateAttendanceRate({ present: count('present'), late: count('late'), mc: count('mc'), absent: count('absent'), total: sesRows.length }) };
                            });
                            const perSessionLabel = sesRates.map(({ ses, rate }, i) =>
                              `${i + 1}. ${sessionTypeLabel(ses.session_type) || 'Session'} — ${fmtSessionName(ses) || 'Untitled'}: ${rate !== null ? `${fmtPct(rate)}` : 'No data'}`).join('\n');
                            if (sesRates.length >= 3) {
                              // Too many classes to show side by side: one combined rate for
                              // the date, with each class's own rate on hover.
                              const dayRows = rows.filter(r => grp.sessions.some(s => s.session_id === r.session_id));
                              const count = status => dayRows.filter(r => r.status === status).length;
                              const dayRate = dayRows.length
                                ? calculateAttendanceRate({ present: count('present'), late: count('late'), mc: count('mc'), absent: count('absent'), total: dayRows.length })
                                : null;
                              return (
                                <td key={grp.dateKey} className="sms-am-cell sms-am-overall-cell" title={perSessionLabel}
                                  aria-label={`Combined ${dayRate !== null ? `${fmtPct(dayRate)}` : 'No data'}. ${perSessionLabel.replace(/\n/g, '. ')}`}
                                  style={{ color: dayRate !== null ? rateColor(dayRate) : 'var(--sms-txt2)' }}>
                                  {dayRate !== null ? `${fmtPct(dayRate)}` : '—'}
                                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt3)' }}>{sesRates.length} classes</div>
                                </td>
                              );
                            }
                            return (
                              <td key={grp.dateKey} className="sms-am-cell sms-am-overall-cell"
                                aria-label={sesRates.map(({ rate }, i) => `${sesRates.length > 1 ? `Session ${i + 1}: ` : ''}${rate !== null ? `${fmtPct(rate)}` : 'No data'}`).join(', ')}>
                                <div className="sms-am-dot-group">
                                  {sesRates.map(({ ses, rate }, i) => (
                                    <span key={ses.session_id}
                                      title={`${sessionTypeLabel(ses.session_type) || 'Session'} — ${fmtSessionName(ses) || 'Untitled'}`}
                                      style={{ color: rate !== null ? rateColor(rate) : 'var(--sms-txt2)' }}>
                                      {rate !== null ? `${fmtPct(rate)}` : '—'}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            );
                          })}
                          <td className="sms-am-rate sms-am-overall-rate sms-am-rate-actual" style={{ color: attendanceRateColor(overallActual) }}
                            aria-label={`Overall actual attendance rate: ${fmtPct(overallActual)}`}>
                            {fmtPct(overallActual)}
                          </td>
                          <td className="sms-am-rate sms-am-overall-rate sms-am-rate-reduce" style={{ color: minRate != null ? 'var(--sms-txt2)' : 'var(--sms-txt3)' }}
                            aria-label={minRate != null ? `Overall calculated attendance threshold: ${fmtPct(calculateAttendanceThreshold(overallActual))}` : 'No attendance threshold'}>
                            {minRate != null ? `${fmtPct(calculateAttendanceThreshold(overallActual))}` : '—'}
                          </td>
                          <td className="sms-am-rate sms-am-overall-rate" />
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <Pagination
                    page={page} totalPages={totalPages} total={stuTotal}
                    pageSize={pageSize} setPage={setPage} setPageSize={setPageSize}
                    start={stuStart}
                  />

                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--sms-bdr)' }}>
                    <button
                      className={`sms-btn ${minRate == null ? 'sms-btn-outline-warning' : 'sms-btn-primary'}`}
                      disabled={sending || (minRate == null ? !can('intake_master', 'update') : flaggedCount === 0)}
                      title={minRate == null ? 'Set a minimum attendance rate first' : flaggedCount === 0 ? 'Flag at least one student first' : 'Notify flagged students via their preferred channel(s)'}
                      onClick={minRate == null ? openAttendanceThreshold : handleSendNotify}
                    >
                      {minRate == null
                        ? <><i className="bi bi-sliders" aria-hidden="true" /> Set Minimum Rate</>
                        : sending
                        ? <><InlineSpinner style={{ marginRight: 4 }} />Sending…</>
                        : <><i className="bi bi-envelope" aria-hidden="true" /> Notify Flagged ({flaggedCount})</>}
                    </button>
                    <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--sms-txt3)' }}>
                      {minRate != null
                        ? `Students below the ${fmtPct(minRate)} minimum attendance rate${(weekRange.from || weekRange.to) ? ' for the selected date range' : ' for this intake'} are auto-flagged.`
                        : 'Set a minimum attendance rate to enable automatic flagging and notifications.'}
                    </span>
                  </div>
                </>
              )}
      </div>

      <Modal show={showAttendanceThreshold} onClose={() => setShowAttendanceThreshold(false)} title="Set Minimum Attendance Rate">
        <form onSubmit={saveAttendanceThreshold} noValidate>
          {thresholdError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{thresholdError}</div>}
          <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginBottom: 14 }}>
            Intake: <strong style={{ color: 'var(--sms-txt)' }}>{selectedIntake?.intake_code || '—'}</strong>
          </div>
          <FormField
            id="attendance-threshold"
            label="Minimum Attendance Rate (%)"
            type="number"
            value={attendanceThreshold}
            onChange={e => setAttendanceThreshold(e.target.value === '' ? '' : Number(e.target.value))}
            hint={<span style={{ whiteSpace: 'nowrap' }}>Required rate from SOC to EOC; absences reduce the rate.</span>}
            required
          />
          <SaveButtons
            saving={thresholdSaving}
            onCancel={() => setShowAttendanceThreshold(false)}
            label="Save Rate"
          />
        </form>
      </Modal>

      <Modal show={!!editingAttendance} onClose={() => setEditingAttendance(null)} title="Edit Attendance Status" size="lg">
        <form onSubmit={handleAttendanceSave} noValidate>
          {attendanceError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{attendanceError}</div>}
          {editingAttendance && (
            <div className="sms-card" style={{ marginBottom: 16, background: 'var(--sms-sur2)' }}>
              <div className="att-edit-label"><i className="bi bi-person" aria-hidden="true" />Student</div>
              <div className="att-edit-value" style={{ fontWeight: 700, marginBottom: 12 }}>{editingAttendance.name || editingAttendance.student_name || editingAttendance.student_no || '—'}</div>
              <div className="row g-3">
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-mortarboard" aria-hidden="true" />Intake</div>
                  <div className="att-edit-value">{intake || '—'}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-journal-text" aria-hidden="true" />Session</div>
                  <div className="att-edit-value">{fmtSessionName(editingAttendance) || '—'}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-calendar3" aria-hidden="true" />Session Date</div>
                  <div className="att-edit-value">{fmtDate(editingAttendance.session_date)}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-clock" aria-hidden="true" />Session Time</div>
                  <div className="att-edit-value">{fmtSessionTime(editingAttendance.start_time) || '—'} – {fmtSessionTime(editingAttendance.end_time) || '—'}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-box-arrow-in-right" aria-hidden="true" />Clock In</div>
                  <div className="att-edit-value">{fmtTime(editingAttendance.clock_in_time) || '—'}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-box-arrow-right" aria-hidden="true" />Clock Out</div>
                  <div className="att-edit-value">{fmtTime(editingAttendance.clock_out_time) || '—'}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-person-badge" aria-hidden="true" />Trainer</div>
                  <div className="att-edit-value">{editingAttendance.trainer_name || '—'}</div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="att-edit-label"><i className="bi bi-flag" aria-hidden="true" />Current Status</div>
                  <Badge value={editingAttendance.status} map={attendanceBadgeMap} />
                </div>
              </div>
            </div>
          )}
          <div className="row g-3">
            <FormField
              id="attendance-status"
              label="Status"
              value={attendanceForm.status}
              onChange={e => setAttendanceForm({ status: e.target.value })}
              options={ATTENDANCE_STATUS_OPTIONS}
              required
            />
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--sms-txt2)' }}>
            Status changes are audited so staff can correct QR scan mistakes without losing the original record.
          </div>
          <SaveButtons
            saving={attendanceSaving}
            onCancel={() => setEditingAttendance(null)}
            label="Save Changes"
          />
        </form>
      </Modal>

      {/* ── Tooltip ─────────────────────────────────────────────────────────── */}
      {tooltip && (
        <div
          role="tooltip"
          className="sms-attend-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
          onMouseEnter={() => clearTimeout(tooltipTimer.current)}
          onMouseLeave={hideTooltip}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
            <span className={`sms-attend-dot ${tooltip.rec.status}`} style={{ width: 10, height: 10, cursor: 'default' }} aria-hidden="true" />
            <span style={{ fontWeight: 700, color: 'var(--sms-txt)', fontSize: 13 }}>{DOT_LABEL[tooltip.rec.status]}</span>
          </div>
          <div style={{ fontWeight: 600, color: 'var(--sms-txt)', marginBottom: 4 }}>
            <i className="bi bi-calendar3" aria-hidden="true" style={{ color: 'var(--sms-txt2)', marginRight: 6 }} />{fmtDate(tooltip.rec.session_date)}
            {(tooltip.rec.start_time || tooltip.rec.end_time) && (
              <>
                <span style={{ color: 'var(--sms-txt3)', margin: '0 7px' }} aria-hidden="true">·</span>
                <i className="bi bi-clock" aria-hidden="true" style={{ color: 'var(--sms-txt2)', marginRight: 5 }} />
                {fmtSessionTime(tooltip.rec.start_time) || '—'} – {fmtSessionTime(tooltip.rec.end_time) || '—'}
              </>
            )}
          </div>
          {sessionTypeLabel(tooltip.rec.session_type) && (
            <div style={{ marginBottom: 4 }}>
              <Badge value={sessionTypeKey(tooltip.rec.session_type)} map={sessionTypeBadgeMap} />
            </div>
          )}
          {fmtSessionName(tooltip.rec) && (
            <div style={{ color: 'var(--sms-txt2)', fontSize: 12, marginBottom: 4 }}>
              <i className="bi bi-journal-text" aria-hidden="true" style={{ marginRight: 6 }} />{fmtSessionName(tooltip.rec)}
            </div>
          )}
          {intake && (
            <div style={{ color: 'var(--sms-txt2)', fontSize: 12, marginBottom: 4 }}>
              <i className="bi bi-mortarboard" aria-hidden="true" style={{ marginRight: 6 }} />{intake}
            </div>
          )}
          {tooltip.rec.trainer_name && (
            <div style={{ color: 'var(--sms-txt2)', fontSize: 12, marginBottom: 6 }}>
              <i className="bi bi-person-badge" aria-hidden="true" style={{ marginRight: 6 }} />{tooltip.rec.trainer_name}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 14px' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}><i className="bi bi-box-arrow-in-right" aria-hidden="true" style={{ marginRight: 5 }} />Clock In</div>
              <div style={{ fontWeight: 600, color: 'var(--sms-txt)', fontSize: 13 }}>{fmtTime(tooltip.rec.clock_in_time) || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}><i className="bi bi-box-arrow-right" aria-hidden="true" style={{ marginRight: 5 }} />Clock Out</div>
              <div style={{ fontWeight: 600, color: 'var(--sms-txt)', fontSize: 13 }}>{fmtTime(tooltip.rec.clock_out_time) || '—'}</div>
            </div>
          </div>
          {tooltip.rec.notes && (
            <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid var(--sms-bdr)', fontSize: 12, color: 'var(--sms-txt2)' }}>
              {tooltip.rec.notes}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ─── DISCIPLINARY ─────────────────────────────────────────────────────────────
const DISC_EMPTY = { student_id:'', incident_date:'', type:'', description:'', action_taken:'', severity:'low', status:'open' };

export function DisciplinaryPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,    setRows]    = useState([]);
  const [students,setStudents]= useState([]);
  const [loading, setLoading] = useState(true);
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(DISC_EMPTY);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [delId,   setDelId]   = useState(null);
  const [viewing, setViewing] = useState(null);
  const attachRef = useRef();

  const [discSearch, setDiscSearch] = React.useState('');
  const [discSeverityFilter, setDiscSeverityFilter] = React.useState('');
  const [discStatusFilter,   setDiscStatusFilter]   = React.useState('');
  const discFiltered = rows.filter(r =>
    (!discSearch || (r.student_name||'').toLowerCase().includes(discSearch.toLowerCase()) || (r.student_id||'').toLowerCase().includes(discSearch.toLowerCase()) || (r.type||'').toLowerCase().includes(discSearch.toLowerCase())) &&
    (!discSeverityFilter || r.severity === discSeverityFilter) &&
    (!discStatusFilter || r.status === discStatusFilter)
  );
  const { sorted: discSorted, sortKey: discSK, sortDir: discSD, toggleSort: discSort } = useSort(discFiltered);
  const { paged: discPaged, page: discPage, setPage: setDiscPage, pageSize: discPageSize, setPageSize: setDiscPageSize, totalPages: discTotalPages, total: discTotal, start: discStart } = usePagination(discSorted);
  const clearDiscFilters = () => { setDiscSearch(''); setDiscSeverityFilter(''); setDiscStatusFilter(''); };

  const load = () => {
    setLoading(true);
    api.get('/disciplinary').then(r => setRows(r.data.data)).catch(()=>toast('Load failed','error')).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); api.get('/students?status=active').then(r=>setStudents(r.data.data)).catch(()=>{}); }, []);

  const openCreate = () => { setEditing(null); setForm(DISC_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ student_id:r.student_id, incident_date:fmtDate(r.incident_date)||'', type:r.type, description:r.description, action_taken:r.action_taken||'', severity:r.severity, status:r.status }); setError(''); setShow(true); };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.student_id||!form.incident_date||!form.type||!form.description) { setError('Please fill required fields.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/disciplinary/${editing.id}`, form);
        await attachRef.current?.flush(editing.id);
      } else {
        const res = await api.post('/disciplinary', form);
        await attachRef.current?.flush(res.data.id);
      }
      toast(`Record ${editing?'updated':'created'}`, 'success'); setShow(false); load();
    } catch (err) { setError(err.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/disciplinary/${delId}`); toast('Record deleted','success'); setDelId(null); load(); }
    catch { toast('Delete failed','error'); }
  };

  return (
    <>
      <PageHeader title="Disciplinary Records" subtitle={`${discTotal} records`}
        actions={can('disciplinary','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Record</button>} />
      <FilterBar search={discSearch} onSearch={setDiscSearch} searchPlaceholder="Search student or type…" onClear={clearDiscFilters}>
        <FilterSelect value={discSeverityFilter} onChange={setDiscSeverityFilter} placeholder="All severities"
          options={[{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }]} />
        <FilterSelect value={discStatusFilter} onChange={setDiscStatusFilter} placeholder="All statuses"
          options={[{ value: 'open', label: 'Open' }, { value: 'resolved', label: 'Resolved' }, { value: 'appealed', label: 'Appealed' }]} />
      </FilterBar>
      <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
        {!loading && discFiltered.length===0 ? <EmptyState icon="bi-exclamation-circle" message="No disciplinary records" /> : (
          <>
          <div style={{ overflowX:'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th style={{width:40}}>#</th>
                <SortTh label="Student ID" field="student_id" sortKey={discSK} sortDir={discSD} onSort={discSort}/>
                <SortTh label="Student Name" field="student_name" sortKey={discSK} sortDir={discSD} onSort={discSort}/>
                <SortTh label="Incident Date" field="incident_date" sortKey={discSK} sortDir={discSD} onSort={discSort}/>
                <SortTh label="Type" field="type" sortKey={discSK} sortDir={discSD} onSort={discSort}/>
                <SortTh label="Severity" field="severity" sortKey={discSK} sortDir={discSD} onSort={discSort}/>
                <SortTh label="Status" field="status" sortKey={discSK} sortDir={discSD} onSort={discSort}/>
                {(can('disciplinary','update')||can('disciplinary','delete')) && <th style={{width:90}}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={7 + ((can('disciplinary','update')||can('disciplinary','delete')) ? 1 : 0)} /> : discPaged.map((r,idx) => (
                  <tr key={r.id} onClick={()=>setViewing(r)} style={{cursor:'pointer'}}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{discStart+idx+1}</td>
                    <td>{r.student_id}</td>
                    <td style={{ fontWeight:500 }}>{r.student_name}</td>
                    <td>{fmtDate(r.incident_date)}</td>
                    <td>{r.type}</td>
                    <td><Badge value={r.severity} map={severityBadgeMap} /></td>
                    <td><Badge value={r.status} map={disciplinaryStatusBadgeMap} /></td>
                    {(can('disciplinary','update')||can('disciplinary','delete')) && (
                      <td onClick={e=>e.stopPropagation()}><div className="d-flex gap-1">
                        {can('disciplinary','update') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px'}} onClick={()=>openEdit(r)}><i className="bi bi-pencil"/></button>}
                        {can('disciplinary','delete') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px',color: 'var(--sms-stat-red)'}} onClick={()=>setDelId(r.id)}><i className="bi bi-trash"/></button>}
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={discPage} totalPages={discTotalPages} total={discTotal} pageSize={discPageSize} setPage={setDiscPage} setPageSize={setDiscPageSize} start={discStart}/>}
          </>
        )}
      </div>
      <Modal show={show} onClose={()=>setShow(false)} title={editing?'Edit Record':'Add Disciplinary Record'} size="lg">
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{fontSize:13}}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Student *</label>
              <SearchableSelect value={form.student_id} onChange={e=>setForm(f=>({...f,student_id:e.target.value}))} placeholder="-- Select student --"
                options={students.map(s=>({value:s.student_no,label:s.name,right:s.student_no}))} />
            </div>
            <FormField id="idate" label="Incident Date" type="date" value={form.incident_date} onChange={e=>setForm(f=>({...f,incident_date:e.target.value}))} required />
            <FormField id="dtype" label="Type" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))} required />
            <FormField id="dsev" label="Severity" value={form.severity} onChange={e=>setForm(f=>({...f,severity:e.target.value}))} options={[{value:'low',label:'Low'},{value:'medium',label:'Medium'},{value:'high',label:'High'}]} />
            <FormField id="dstat" label="Status" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} options={[{value:'open',label:'Open'},{value:'resolved',label:'Resolved'},{value:'appealed',label:'Appealed'}]} />
            <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Description *</label><textarea className="sms-form-control" rows={3} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>
            <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Action Taken</label><textarea className="sms-form-control" rows={2} value={form.action_taken||''} onChange={e=>setForm(f=>({...f,action_taken:e.target.value}))} /></div>
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="disciplinary" recordId={editing?.id ?? null}
              canUpload={can('disciplinary','create')||can('disciplinary','update')} canDelete={can('disciplinary','delete')} />
          </div>
          <SaveButtons saving={saving} onCancel={()=>setShow(false)} label={editing?'Update':'Create'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this disciplinary record?" onConfirm={handleDelete} onCancel={()=>setDelId(null)} />
      <DetailModal show={!!viewing} onClose={()=>setViewing(null)} title="Disciplinary Record Details" size="lg" fields={viewing ? [
        { label: 'Student ID',     value: viewing.student_id },
        { label: 'Student Name',   value: viewing.student_name },
        { label: 'Incident Date',  value: fmtDate(viewing.incident_date) },
        { label: 'Type',           value: viewing.type },
        { label: 'Severity',       value: <Badge value={viewing.severity} map={severityBadgeMap} /> },
        { label: 'Status',         value: <Badge value={viewing.status} map={disciplinaryStatusBadgeMap} /> },
        { label: 'Recorded By',    value: viewing.recorded_by_name },
        { label: 'Description',    value: viewing.description, full: true },
        { label: 'Action Taken',   value: viewing.action_taken, full: true },
      ] : []}>
        {viewing && (
          <FileAttachments module="disciplinary" recordId={viewing.id} canUpload={false} canDelete={false} />
        )}
      </DetailModal>
    </>
  );
}

// ─── ALUMNI ───────────────────────────────────────────────────────────────────
const ALUMNI_EMPTY = { student_id:'', graduation_date:'', final_grade:'', employer:'', job_title:'', remarks:'' };

export function AlumniPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,    setRows]    = useState([]);
  const [students,setStudents]= useState([]);
  const [loading, setLoading] = useState(true);
  const [show,    setShow]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState(ALUMNI_EMPTY);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');
  const [delId,   setDelId]   = useState(null);
  const [search,  setSearch]  = useState('');
  const [intakeFilter, setIntakeFilter] = useState('');
  const [viewing, setViewing] = useState(null);
  const attachRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/alumni').then(r=>setRows(r.data.data)).catch(()=>toast('Load failed','error')).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); api.get('/students').then(r=>setStudents(r.data.data)).catch(()=>{}); },[]);

  const openCreate = () => { setEditing(null); setForm(ALUMNI_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ student_id:r.student_id, graduation_date:fmtDate(r.graduation_date)||'', final_grade:r.final_grade||'', employer:r.employer||'', job_title:r.job_title||'', remarks:r.remarks||'' }); setError(''); setShow(true); };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.student_id||!form.graduation_date) { setError('Student and graduation date required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/alumni/${editing.id}`,form);
        await attachRef.current?.flush(editing.id);
      } else {
        const res = await api.post('/alumni',form);
        await attachRef.current?.flush(res.data.id);
      }
      toast(`Alumni record ${editing?'updated':'created'}`, 'success'); setShow(false); load();
    } catch (err) { setError(err.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/alumni/${delId}`); toast('Record deleted','success'); setDelId(null); load(); }
    catch { toast('Delete failed','error'); }
  };

  const intakeOptions = [...new Set(rows.map(r => r.intake || r.intake_code).filter(Boolean))].sort().map(i => ({ value: i, label: i }));
  const filtered = rows.filter(r =>
    ((r.name||r.full_name||'').toLowerCase().includes(search.toLowerCase()) || (r.student_id||'').toLowerCase().includes(search.toLowerCase()) || (r.intake||r.intake_code||'').toLowerCase().includes(search.toLowerCase())) &&
    (!intakeFilter || (r.intake || r.intake_code) === intakeFilter)
  );
  const { sorted: alumniSorted, sortKey: alumniSK, sortDir: alumniSD, toggleSort: alumniSort } = useSort(filtered);
  const { paged: alumniPaged, page: alumniPage, setPage: setAlumniPage, pageSize: alumniPageSize, setPageSize: setAlumniPageSize, totalPages: alumniTotalPages, total: alumniTotal, start: alumniStart } = usePagination(alumniSorted);
  const clearFilters = () => { setSearch(''); setIntakeFilter(''); };

  return (
    <>
      <PageHeader title="Alumni Records" subtitle={`${alumniTotal} graduates`}
        actions={can('alumni','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Alumni</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search name or intake…" onClear={clearFilters}>
        <FilterSelect value={intakeFilter} onChange={setIntakeFilter} placeholder="All intakes" options={intakeOptions} />
      </FilterBar>
      <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
        {!loading && filtered.length===0 ? <EmptyState icon="bi-award" message="No alumni records" /> : (
          <>
          <div style={{overflowX:'auto'}}>
            <table className="sms-table">
              <thead><tr>
                <th style={{width:40}}>#</th>
                <SortTh label="Student ID" field="student_id" sortKey={alumniSK} sortDir={alumniSD} onSort={alumniSort}/>
                <SortTh label="Name" field="name" sortKey={alumniSK} sortDir={alumniSD} onSort={alumniSort}/>
                <SortTh label="Intake" field="intake" sortKey={alumniSK} sortDir={alumniSD} onSort={alumniSort}/>
                <SortTh label="Graduation" field="graduation_date" sortKey={alumniSK} sortDir={alumniSD} onSort={alumniSort}/>
                <SortTh label="Grade" field="final_grade" sortKey={alumniSK} sortDir={alumniSD} onSort={alumniSort}/>
                <SortTh label="Employer" field="employer" sortKey={alumniSK} sortDir={alumniSD} onSort={alumniSort}/>
                {(can('alumni','update')||can('alumni','delete')) && <th style={{width:90}}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={7 + ((can('alumni','update')||can('alumni','delete')) ? 1 : 0)} /> : alumniPaged.map((r,idx)=>(
                  <tr key={r.id} onClick={()=>setViewing(r)} style={{cursor:'pointer'}}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{alumniStart+idx+1}</td>
                    <td>{r.student_id}</td>
                    <td style={{ fontWeight:500 }}>{r.name}</td>
                    <td>{r.intake}</td>
                    <td>{fmtDate(r.graduation_date)}</td>
                    <td>{r.final_grade||'—'}</td>
                    <td>{r.employer||'—'}</td>
                    {(can('alumni','update')||can('alumni','delete')) && (
                      <td onClick={e=>e.stopPropagation()}><div className="d-flex gap-1">
                        {can('alumni','update') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px'}} onClick={()=>openEdit(r)}><i className="bi bi-pencil"/></button>}
                        {can('alumni','delete') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px',color: 'var(--sms-stat-red)'}} onClick={()=>setDelId(r.id)}><i className="bi bi-trash"/></button>}
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={alumniPage} totalPages={alumniTotalPages} total={alumniTotal} pageSize={alumniPageSize} setPage={setAlumniPage} setPageSize={setAlumniPageSize} start={alumniStart}/>}
          </>
        )}
      </div>
      <Modal show={show} onClose={()=>setShow(false)} title={editing?'Edit Alumni':'Add Alumni Record'} size="lg">
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{fontSize:13}}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Student *</label>
              <SearchableSelect value={form.student_id} onChange={e=>setForm(f=>({...f,student_id:e.target.value}))} placeholder="-- Select student --"
                options={students.map(s=>({value:s.student_no,label:s.name,right:s.student_no}))} />
            </div>
            <FormField id="gdate" label="Graduation Date" type="date" value={form.graduation_date} onChange={e=>setForm(f=>({...f,graduation_date:e.target.value}))} required />
            <FormField id="fg" label="Final Grade" value={form.final_grade} onChange={e=>setForm(f=>({...f,final_grade:e.target.value}))} />
            <FormField id="emp" label="Employer" value={form.employer} onChange={e=>setForm(f=>({...f,employer:e.target.value}))} />
            <FormField id="jt" label="Job Title" value={form.job_title} onChange={e=>setForm(f=>({...f,job_title:e.target.value}))} />
            <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Remarks</label><textarea className="sms-form-control" rows={2} value={form.remarks||''} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} /></div>
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="alumni" recordId={editing?.id ?? null}
              canUpload={can('alumni','create')||can('alumni','update')} canDelete={can('alumni','delete')} />
          </div>
          <SaveButtons saving={saving} onCancel={()=>setShow(false)} label={editing?'Update':'Create'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this alumni record?" onConfirm={handleDelete} onCancel={()=>setDelId(null)} />
      <DetailModal show={!!viewing} onClose={()=>setViewing(null)} title="Alumni Details" size="lg" fields={viewing ? [
        { label: 'Student ID',       value: viewing.student_id },
        { label: 'Name',             value: viewing.name },
        { label: 'Intake',           value: viewing.intake },
        { label: 'Graduation Date',  value: fmtDate(viewing.graduation_date) },
        { label: 'Final Grade',      value: viewing.final_grade },
        { label: 'Email',            value: viewing.email },
        { label: 'Phone',            value: viewing.phone },
        { label: 'Employer',         value: viewing.employer },
        { label: 'Job Title',        value: viewing.job_title },
        { label: 'Remarks',          value: viewing.remarks, full: true },
      ] : []}>
        {viewing && can('alumni','view') && (
          <FileAttachments module="alumni" recordId={viewing.id} canUpload={false} canDelete={false} />
        )}
      </DetailModal>
    </>
  );
}

// ─── REQUESTS ────────────────────────────────────────────────────────────────
const REQ_EMPTY = { student_id:'', request_type:'', description:'', priority:'normal', start_date:'', end_date:'' };

function RequestAttendancePanel({ studentId, startDate, endDate }) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null });
    api.get(`/attendance/student/${studentId}`, { params: { from_date: startDate, to_date: endDate } })
      .then(r => { if (!cancelled) setState({ loading: false, data: r.data.data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, data: { summary: null, records: [] } }); });
    return () => { cancelled = true; };
  }, [studentId, startDate, endDate]);

  const { loading, data } = state;
  const hasRecords = data && data.summary && data.summary.total_sessions > 0;

  return (
    <div className="mt-3">
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>
        <i className="bi bi-calendar-check" style={{ marginRight: 5 }} />
        Attendance during requested leave ({fmtDate(startDate)} – {fmtDate(endDate)})
      </label>
      {!loading && !hasRecords ? (
        <EmptyState icon="bi-calendar-x" message="No attendance records found for this date range" />
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--sms-txt2)', marginBottom: 8 }}>
            {!loading && `Present: ${data.summary.present} · Late: ${data.summary.late} · Absent: ${data.summary.absent} · ${data.summary.total_sessions} session(s)`}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr><th>Session</th><th>Date</th><th>Trainer</th><th>Status</th></tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={4} /> : data.records.map(r => (
                  <tr key={r.id}>
                    <td>{fmtSessionName(r)}</td>
                    <td>{fmtDate(r.session_date)}</td>
                    <td>{r.trainer_name}</td>
                    <td><Badge value={r.status} map={attendanceBadgeMap} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function RequestsPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,     setRows]    = useState([]);
  const [students, setStudents]= useState([]);
  const [loading,  setLoading] = useState(true);
  const [show,     setShow]    = useState(false);
  const [editing,  setEditing] = useState(null);
  const [form,     setForm]    = useState(REQ_EMPTY);
  const [saving,   setSaving]  = useState(false);
  const [error,    setError]   = useState('');
  const [delId,    setDelId]   = useState(null);
  const [filter,   setFilter]  = useState('');
  const [viewing,  setViewing] = useState(null);
  const attachRef = useRef();

  const load = () => {
    setLoading(true);
    const params = filter ? { status: filter } : {};
    api.get('/requests', { params }).then(r=>setRows(r.data.data)).catch(()=>toast('Load failed','error')).finally(()=>setLoading(false));
  };
  const [reqSearch, setReqSearch] = React.useState('');
  const [priorityFilter, setPriorityFilter] = React.useState('');
  const reqFiltered = rows.filter(r =>
    (!reqSearch || (r.student_name||'').toLowerCase().includes(reqSearch.toLowerCase()) || (r.student_id||'').toLowerCase().includes(reqSearch.toLowerCase()) || (r.request_type||'').toLowerCase().includes(reqSearch.toLowerCase())) &&
    (!priorityFilter || r.priority === priorityFilter)
  );
  const { sorted: reqSorted, sortKey: reqSK, sortDir: reqSD, toggleSort: reqSort } = useSort(reqFiltered);
  const { paged: reqPaged, page: reqPage, setPage: setReqPage, pageSize: reqPageSize, setPageSize: setReqPageSize, totalPages: reqTotalPages, total: reqTotal, start: reqStart } = usePagination(reqSorted);

  useEffect(()=>{ api.get('/students').then(r=>setStudents(r.data.data)).catch(()=>{}); },[]);
  useEffect(()=>{ load(); },[filter]);

  const openCreate = () => { setEditing(null); setForm(REQ_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ student_id:r.student_id, source:r.source, request_type:r.request_type, description:r.description||'', priority:r.priority, status:r.status, remarks:r.remarks||'', start_date:r.start_date||'', end_date:r.end_date||'' }); setError(''); setShow(true); };
  const locked = editing && form.source === 'student';

  const handleSave = async e => {
    e.preventDefault();
    if (!form.student_id||!form.request_type) { setError('Student and request type required.'); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) { setError('End date cannot be before start date.'); return; }
    if (!editing && form.start_date && form.end_date) {
      const overlapping = rows.some(r =>
        r.student_id === form.student_id && r.status !== 'cancelled' && r.status !== 'rejected' && r.start_date && r.end_date &&
        r.start_date <= form.end_date && r.end_date >= form.start_date
      );
      if (overlapping) { setError('This student already has a request covering part of this date range.'); return; }
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/requests/${editing.id}`,form);
        await attachRef.current?.flush(editing.id);
      } else {
        const res = await api.post('/requests',form);
        await attachRef.current?.flush(res.data.id);
      }
      toast(`Request ${editing?'updated':'created'}`, 'success'); setShow(false); load();
    } catch (err) { setError(err.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/requests/${delId}`); toast('Request deleted','success'); setDelId(null); load(); }
    catch { toast('Delete failed','error'); }
  };

  return (
    <>
      <PageHeader title="Student Requests" subtitle={`${reqTotal} requests`}
        actions={can('requests','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> New Request</button>} />

      <FilterBar search={reqSearch} onSearch={setReqSearch} searchPlaceholder="Search student, request type…"
        onClear={() => { setReqSearch(''); setFilter(''); setPriorityFilter(''); }}>
        <FilterSelect value={filter} onChange={setFilter} placeholder="All statuses"
          options={['new','pending','in_review','approved','rejected','cancelled'].map(s=>({value:s,label:s==='in_review'?'In Review':s==='approved'?'Acknowledge':s.charAt(0).toUpperCase()+s.slice(1)}))} />
        <FilterSelect value={priorityFilter} onChange={setPriorityFilter} placeholder="All priorities"
          options={[{ value: 'normal', label: 'Normal' }, { value: 'urgent', label: 'Urgent' }]} />
      </FilterBar>
      <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
        {!loading && reqFiltered.length===0 ? <EmptyState icon="bi-envelope-open" message="No requests found" /> : (
          <>
          <div style={{overflowX:'auto'}}>
            <table className="sms-table">
              <thead><tr>
                <th style={{width:40}}>#</th>
                <SortTh label="Student ID" field="student_id" sortKey={reqSK} sortDir={reqSD} onSort={reqSort}/>
                <SortTh label="Student Name" field="student_name" sortKey={reqSK} sortDir={reqSD} onSort={reqSort}/>
                <SortTh label="Request Type" field="request_type" sortKey={reqSK} sortDir={reqSD} onSort={reqSort}/>
                <SortTh label="Priority" field="priority" sortKey={reqSK} sortDir={reqSD} onSort={reqSort}/>
                <SortTh label="Status" field="status" sortKey={reqSK} sortDir={reqSD} onSort={reqSort}/>
                <SortTh label="Date" field="created_at" sortKey={reqSK} sortDir={reqSD} onSort={reqSort}/>
                {(can('requests','update')||can('requests','delete')) && <th style={{width:90}}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={7 + ((can('requests','update')||can('requests','delete')) ? 1 : 0)} /> : reqPaged.map((r,idx)=>(
                  <tr key={r.id} onClick={()=>setViewing(r)} style={{cursor:'pointer'}}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{reqStart+idx+1}</td>
                    <td>{r.student_id}</td>
                    <td style={{ fontWeight:500 }}>{r.student_name}</td>
                    <td>{r.request_type}</td>
                    <td><span className={`badge rounded-pill ${r.priority==='urgent'?'badge-absent':'badge-inactive'}`} style={{fontSize: 12}}>{r.priority ? r.priority.charAt(0).toUpperCase()+r.priority.slice(1) : ''}</span></td>
                    <td><Badge value={r.status} map={requestBadgeMap} /></td>
                    <td>{fmtDate(r.created_at)}</td>
                    {(can('requests','update')||can('requests','delete')) && (
                      <td onClick={e=>e.stopPropagation()}><div className="d-flex gap-1">
                        {can('requests','update') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px'}} disabled={r.status==='cancelled'||r.status==='approved'} title={r.status==='cancelled'?'Cancelled by student':r.status==='approved'?'Already acknowledged — cannot be modified':undefined} onClick={()=>openEdit(r)}><i className="bi bi-pencil"/></button>}
                        {can('requests','delete') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px',color: 'var(--sms-stat-red)'}} onClick={()=>setDelId(r.id)}><i className="bi bi-trash"/></button>}
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={reqPage} totalPages={reqTotalPages} total={reqTotal} pageSize={reqPageSize} setPage={setReqPage} setPageSize={setReqPageSize} start={reqStart}/>}
          </>
        )}
      </div>
      <Modal show={show} onClose={()=>setShow(false)} title={editing?'Update Request':'New Request'} size="lg">
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{fontSize:13}}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Student *</label>
              <SearchableSelect value={form.student_id} onChange={e=>setForm(f=>({...f,student_id:e.target.value}))} placeholder="-- Select student --"
                options={students.map(s=>({value:s.student_no,label:s.name,right:s.student_no}))} disabled={locked} />
            </div>
            <FormField id="rtype" label="Request Type" value={form.request_type} onChange={e=>setForm(f=>({...f,request_type:e.target.value}))} options={REQUEST_TYPE_OPTIONS} required disabled={locked} />
            <FormField id="rpri" label="Priority" value={form.priority||'normal'} onChange={e=>setForm(f=>({...f,priority:e.target.value}))} options={[{value:'normal',label:'Normal'},{value:'urgent',label:'Urgent'}]} disabled={locked} />
            {editing && <FormField id="rstat" label="Status" value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))} options={[{value:'new',label:'New'},{value:'pending',label:'Pending'},{value:'in_review',label:'In Review'},{value:'approved',label:'Acknowledge'},{value:'rejected',label:'Rejected'}]} />}
            {DATE_RANGE_TYPES.has(form.request_type) && (
              <>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Start Date</label>
                  <input type="date" className="sms-form-control" value={form.start_date||''} disabled={locked}
                    onChange={e=>setForm(f=>({...f,start_date:e.target.value}))} />
                </div>
                <div className="col-12 col-sm-6">
                  <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>End Date</label>
                  <input type="date" className="sms-form-control" value={form.end_date||''} disabled={locked} min={form.start_date||undefined}
                    onChange={e=>setForm(f=>({...f,end_date:e.target.value}))} />
                </div>
              </>
            )}
            <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Description</label><textarea className="sms-form-control" rows={3} value={form.description||''} onChange={e=>setForm(f=>({...f,description:e.target.value}))} disabled={locked} /></div>
            {editing && <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Remarks</label><textarea className="sms-form-control" rows={2} value={form.remarks||''} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} /></div>}
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="requests" recordId={editing?.id ?? null}
              canUpload={can('requests','create')||can('requests','update')} canDelete={can('requests','delete')} />
          </div>
          {editing && form.start_date && form.end_date && can('attendance','view') && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--sms-bdr)' }}>
              <RequestAttendancePanel studentId={form.student_id} startDate={form.start_date} endDate={form.end_date} />
            </div>
          )}
          <SaveButtons saving={saving} onCancel={()=>setShow(false)} label={editing?'Update':'Submit'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this request?" onConfirm={handleDelete} onCancel={()=>setDelId(null)} />
      <DetailModal show={!!viewing} onClose={()=>setViewing(null)} title="Request Details" size="lg" fields={viewing ? [
        { label: 'Student ID',    value: viewing.student_id },
        { label: 'Student Name',  value: viewing.student_name },
        { label: 'Request Type',  value: viewing.request_type },
        { label: 'Priority',      value: viewing.priority ? viewing.priority.charAt(0).toUpperCase()+viewing.priority.slice(1) : '' },
        { label: 'Status',        value: <Badge value={viewing.status} map={requestBadgeMap} /> },
        { label: 'Date',          value: fmtDate(viewing.created_at) },
        { label: 'Handled By',    value: viewing.handled_by_name },
        { label: 'Requested Leave Dates', value: viewing.start_date ? `${fmtDate(viewing.start_date)} – ${fmtDate(viewing.end_date)}` : null },
        { label: 'Description',   value: viewing.description, full: true },
        { label: 'Remarks',       value: viewing.remarks, full: true },
      ] : []}>
        {viewing && (
          <>
            <FileAttachments module="requests" recordId={viewing.id} canUpload={false} canDelete={false} />
            {viewing.start_date && viewing.end_date && can('attendance','view') && (
              <RequestAttendancePanel studentId={viewing.student_id} startDate={viewing.start_date} endDate={viewing.end_date} />
            )}
          </>
        )}
      </DetailModal>
    </>
  );
}

// ─── LOG ACTIVITIES ───────────────────────────────────────────────────────────
const LOG_EMPTY = { student_id:'', log_date:'', category:'', description:'' };
const LOG_CATS  = ['Academic','Behaviour','Medical','Counselling','Achievement','Other'];

export function LogActivitiesPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,     setRows]    = useState([]);
  const [students, setStudents]= useState([]);
  const [loading,  setLoading] = useState(true);
  const [show,     setShow]    = useState(false);
  const [editing,  setEditing] = useState(null);
  const [form,     setForm]    = useState(LOG_EMPTY);
  const [saving,   setSaving]  = useState(false);
  const [error,    setError]   = useState('');
  const [delId,    setDelId]   = useState(null);
  const [search,   setSearch]  = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [viewing,  setViewing] = useState(null);
  const attachRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/log-activities').then(r=>setRows(r.data.data)).catch(()=>toast('Load failed','error')).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); api.get('/students').then(r=>setStudents(r.data.data)).catch(()=>{}); },[]);

  const openCreate = () => { setEditing(null); setForm(LOG_EMPTY); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ student_id:r.student_id, log_date:r.log_date?.slice(0,10)||'', category:r.category, description:r.description }); setError(''); setShow(true); };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.student_id||!form.log_date||!form.category||!form.description) { setError('All fields required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/log-activities/${editing.id}`,form);
        await attachRef.current?.flush(editing.id);
      } else {
        const res = await api.post('/log-activities',form);
        await attachRef.current?.flush(res.data.id);
      }
      toast(`Log ${editing?'updated':'created'}`, 'success'); setShow(false); load();
    } catch (err) { setError(err.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/log-activities/${delId}`); toast('Log deleted','success'); setDelId(null); load(); }
    catch { toast('Delete failed','error'); }
  };

  const filtered = rows.filter(r =>
    ((r.student_name||'').toLowerCase().includes(search.toLowerCase()) || (r.student_id||'').toLowerCase().includes(search.toLowerCase()) || (r.category||'').toLowerCase().includes(search.toLowerCase())) &&
    (!categoryFilter || r.category === categoryFilter)
  );
  const { sorted: logSorted, sortKey: logSK, sortDir: logSD, toggleSort: logSort } = useSort(filtered);
  const { paged: logPaged, page: logPage, setPage: setLogPage, pageSize: logPageSize, setPageSize: setLogPageSize, totalPages: logTotalPages, total: logTotal, start: logStart } = usePagination(logSorted);
  const clearFilters = () => { setSearch(''); setCategoryFilter(''); };

  return (
    <>
      <PageHeader title="Log Activities" subtitle={`${logTotal} entries`}
        actions={can('activities','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Log</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search student or category…" onClear={clearFilters}>
        <FilterSelect value={categoryFilter} onChange={setCategoryFilter} placeholder="All categories"
          options={LOG_CATS.map(c => ({ value: c, label: c }))} />
      </FilterBar>
      <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
        {!loading && filtered.length===0 ? <EmptyState icon="bi-activity" message="No activity logs" /> : (
          <>
          <div style={{overflowX:'auto'}}>
            <table className="sms-table">
              <thead><tr>
                <th style={{width:40}}>#</th>
                <SortTh label="Student ID" field="student_id" sortKey={logSK} sortDir={logSD} onSort={logSort}/>
                <SortTh label="Student Name" field="student_name" sortKey={logSK} sortDir={logSD} onSort={logSort}/>
                <SortTh label="Date" field="log_date" sortKey={logSK} sortDir={logSD} onSort={logSort}/>
                <SortTh label="Category" field="category" sortKey={logSK} sortDir={logSD} onSort={logSort}/>
                <th>Description</th>
                <SortTh label="Recorded By" field="recorded_by_name" sortKey={logSK} sortDir={logSD} onSort={logSort}/>
                {(can('activities','update')||can('activities','delete')) && <th style={{width:90}}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={7 + ((can('activities','update')||can('activities','delete')) ? 1 : 0)} /> : logPaged.map((r,idx)=>(
                  <tr key={r.id} onClick={()=>setViewing(r)} style={{cursor:'pointer'}}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{logStart+idx+1}</td>
                    <td>{r.student_id}</td>
                    <td style={{ fontWeight:500 }}>{r.student_name}</td>
                    <td>{fmtDate(r.log_date)}</td>
                    <td><span className="badge rounded-pill badge-active" style={{fontSize: 12}}>{r.category}</span></td>
                    <td style={{ maxWidth:240,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{r.description}</td>
                    <td>{r.recorded_by_name}</td>
                    {(can('activities','update')||can('activities','delete')) && (
                      <td onClick={e=>e.stopPropagation()}><div className="d-flex gap-1">
                        {can('activities','update') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px'}} onClick={()=>openEdit(r)}><i className="bi bi-pencil"/></button>}
                        {can('activities','delete') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px',color: 'var(--sms-stat-red)'}} onClick={()=>setDelId(r.id)}><i className="bi bi-trash"/></button>}
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={logPage} totalPages={logTotalPages} total={logTotal} pageSize={logPageSize} setPage={setLogPage} setPageSize={setLogPageSize} start={logStart}/>}
          </>
        )}
      </div>
      <Modal show={show} onClose={()=>setShow(false)} title={editing?'Edit Log':'Add Activity Log'} size="lg">
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{fontSize:13}}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Student *</label>
              <SearchableSelect value={form.student_id} onChange={e=>setForm(f=>({...f,student_id:e.target.value}))} placeholder="-- Select student --"
                options={students.map(s=>({value:s.student_no,label:s.name,right:s.student_no}))} />
            </div>
            <FormField id="ldate" label="Log Date" type="date" value={form.log_date} onChange={e=>setForm(f=>({...f,log_date:e.target.value}))} required />
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Category *</label>
              <SearchableSelect value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} placeholder="-- Select category --"
                options={LOG_CATS.map(c=>({value:c,label:c}))} />
            </div>
            <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Description *</label><textarea className="sms-form-control" rows={3} value={form.description||''} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="activities" recordId={editing?.id ?? null}
              canUpload={can('activities','create')||can('activities','update')} canDelete={can('activities','delete')} />
          </div>
          <SaveButtons saving={saving} onCancel={()=>setShow(false)} label={editing?'Update':'Add Log'} />
        </form>
      </Modal>
      <ConfirmDialog show={!!delId} danger message="Delete this log?" onConfirm={handleDelete} onCancel={()=>setDelId(null)} />
      <DetailModal show={!!viewing} onClose={()=>setViewing(null)} title="Activity Log Details" size="lg" fields={viewing ? [
        { label: 'Student ID',   value: viewing.student_id },
        { label: 'Student Name', value: viewing.student_name },
        { label: 'Date',         value: fmtDate(viewing.log_date) },
        { label: 'Category',     value: viewing.category },
        { label: 'Recorded By',  value: viewing.recorded_by_name },
        { label: 'Description',  value: viewing.description, full: true },
      ] : []}>
        {viewing && (
          <FileAttachments module="activities" recordId={viewing.id} canUpload={false} canDelete={false} />
        )}
      </DetailModal>
    </>
  );
}

// ─── RECEIPTS ────────────────────────────────────────────────────────────────
const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'credit_card',   label: 'Credit Card' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'paynow',        label: 'PayNow' },
];
const PAYMENT_METHOD_LABELS = Object.fromEntries(PAYMENT_METHOD_OPTIONS.map(o => [o.value, o.label]));
const todayStr = () => new Date().toISOString().slice(0, 10);
const receiptEmpty = () => ({ student_id: '', student_course_id: '', amount: '', payment_method: 'cash', payment_date: todayStr(), remarks: '' });
const COURSE_STATUS_LABELS = { registered: 'Registered', active: 'Active', completed: 'Completed', withdrawn: 'Withdrawn', transferred: 'Transferred' };
const RECEIPT_STATUS_LABELS = { paid: 'Paid', void: 'Void' };

function fmtAmount(value) {
  const n = Number(value);
  if (isNaN(n)) return '—';
  return `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ReceiptsPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [rows,     setRows]     = useState([]);
  const [students, setStudents] = useState([]);
  const [courses,  setCourses]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [show,     setShow]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(receiptEmpty);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [voidId,     setVoidId]     = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [search,   setSearch]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [viewing,  setViewing]  = useState(null);
  const attachRef = useRef();

  const load = () => {
    setLoading(true);
    api.get('/receipts').then(r=>setRows(r.data.data)).catch(()=>toast('Load failed','error')).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); api.get('/students').then(r=>setStudents(r.data.data)).catch(()=>{}); },[]);

  // Course choices always follow the currently-selected student — fetched
  // fresh whenever it changes, and cleared when there's no student yet.
  useEffect(() => {
    if (!form.student_id) { setCourses([]); return; }
    api.get(`/students/${form.student_id}/courses`).then(r=>setCourses(r.data.data)).catch(()=>setCourses([]));
  }, [form.student_id]);

  const openCreate = () => { setEditing(null); setForm(receiptEmpty()); setError(''); setShow(true); };
  const openEdit   = r  => { setEditing(r); setForm({ student_id:r.student_id, student_course_id:r.student_course_id, amount:r.amount, payment_method:r.payment_method||'cash', payment_date:(r.payment_date||'').slice(0,10), remarks:r.remarks||'' }); setError(''); setShow(true); };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.student_id||!form.student_course_id||!form.amount||!form.payment_date) { setError('Student, course, amount and payment date are required.'); return; }
    setSaving(true);
    try {
      if (editing) {
        const { student_id, student_course_id, ...payload } = form;
        await api.put(`/receipts/${editing.id}`,payload);
        await attachRef.current?.flush(editing.id);
      } else {
        const res = await api.post('/receipts',form);
        await attachRef.current?.flush(res.data.id);
      }
      toast(`Receipt ${editing?'updated':'created'}`, 'success'); setShow(false); load();
    } catch (err) { setError(err.response?.data?.message||'Save failed'); }
    finally { setSaving(false); }
  };

  const handleVoid = async () => {
    try {
      const res = await api.post(`/receipts/${voidId}/void`, { reason: voidReason });
      toast(res.data.message || 'Receipt voided', 'success');
      setVoidId(null); setVoidReason(''); load();
    } catch (err) { toast(err.response?.data?.message || 'Void failed','error'); }
  };

  const filtered = rows.filter(r =>
    ((r.receipt_no||'').toLowerCase().includes(search.toLowerCase()) || (r.student_name||'').toLowerCase().includes(search.toLowerCase()) || (r.student_id||'').toLowerCase().includes(search.toLowerCase())) &&
    (!statusFilter || r.status === statusFilter) &&
    (!methodFilter || r.payment_method === methodFilter)
  );
  const { sorted: rcptSorted, sortKey: rcptSK, sortDir: rcptSD, toggleSort: rcptSort } = useSort(filtered);
  const { paged: rcptPaged, page: rcptPage, setPage: setRcptPage, pageSize: rcptPageSize, setPageSize: setRcptPageSize, totalPages: rcptTotalPages, total: rcptTotal, start: rcptStart } = usePagination(rcptSorted);
  const clearFilters = () => { setSearch(''); setStatusFilter(''); setMethodFilter(''); };

  return (
    <>
      <PageHeader title="Receipts" subtitle={`${rcptTotal} receipt(s)`}
        actions={can('receipts','create') && <button className="sms-btn sms-btn-primary" onClick={openCreate}><i className="bi bi-plus-lg" /> Add Receipt</button>} />
      <FilterBar search={search} onSearch={setSearch} searchPlaceholder="Search receipt no, name or student ID…" onClear={clearFilters}>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="All statuses"
          options={[{ value: 'paid', label: 'Paid' }, { value: 'void', label: 'Void' }]} />
        <FilterSelect value={methodFilter} onChange={setMethodFilter} placeholder="All methods" options={PAYMENT_METHOD_OPTIONS} />
      </FilterBar>
      <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
        {!loading && filtered.length===0 ? <EmptyState icon="bi-receipt" message="No receipts" /> : (
          <>
          <div style={{overflowX:'auto'}}>
            <table className="sms-table">
              <thead><tr>
                <th style={{width:40}}>#</th>
                <SortTh label="Receipt No" field="receipt_no" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                <SortTh label="Student ID" field="student_id" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                <SortTh label="Name" field="student_name" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                <SortTh label="Intake" field="intake_code" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                <SortTh label="Amount" field="amount" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort} style={{ textAlign: 'right' }}/>
                <SortTh label="Method" field="payment_method" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                <SortTh label="Date" field="payment_date" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                <SortTh label="Status" field="status" sortKey={rcptSK} sortDir={rcptSD} onSort={rcptSort}/>
                {(can('receipts','update')||can('receipts','delete')) && <th style={{width:90}}>Actions</th>}
              </tr></thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={8 + ((can('receipts','update')||can('receipts','delete')) ? 1 : 0)} /> : rcptPaged.map((r,idx)=>(
                  <tr key={r.id} onClick={()=>setViewing(r)} className={r.status==='void'?'sms-row-muted':undefined} style={{cursor:'pointer'}}>
                    <td style={{ color: 'var(--sms-txt2)' }}>{rcptStart+idx+1}</td>
                    <td>{r.receipt_no}</td>
                    <td>{r.student_id}</td>
                    <td style={{ fontWeight:500 }}>{r.student_name}</td>
                    <td>{r.intake_code||'—'}</td>
                    <td style={{ textAlign: 'right', textDecoration:r.status==='void'?'line-through':'none' }}>{fmtAmount(r.amount)}</td>
                    <td>{PAYMENT_METHOD_LABELS[r.payment_method]||r.payment_method}</td>
                    <td>{fmtDate(r.payment_date)}</td>
                    <td><span className={`badge rounded-pill ${r.status==='void'?'badge-void':'badge-paid'}`} style={{fontSize: 12}}>{RECEIPT_STATUS_LABELS[r.status]||r.status}</span></td>
                    {(can('receipts','update')||can('receipts','delete')) && (
                      <td onClick={e=>e.stopPropagation()}><div className="d-flex gap-1">
                        {can('receipts','update') && <button className="sms-btn sms-btn-outline" style={{padding:'4px 8px'}} disabled={r.status==='void'} onClick={()=>openEdit(r)}><i className="bi bi-pencil"/></button>}
                        {can('receipts','delete') && <button className="sms-btn sms-btn-outline-danger" style={{padding:'4px 8px'}} disabled={r.status==='void'} title="Void receipt" onClick={()=>{setVoidId(r.id);setVoidReason('');}}><i className="bi bi-x-circle"/></button>}
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && <Pagination page={rcptPage} totalPages={rcptTotalPages} total={rcptTotal} pageSize={rcptPageSize} setPage={setRcptPage} setPageSize={setRcptPageSize} start={rcptStart}/>}
          </>
        )}
      </div>
      <Modal show={show} onClose={()=>setShow(false)} title={editing?'Edit Receipt':'Add Receipt'} size="lg">
        <form onSubmit={handleSave} noValidate>
          {error && <div className="alert alert-danger py-2" style={{fontSize:13}}>{error}</div>}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Student *</label>
              <SearchableSelect value={form.student_id} onChange={e=>setForm(f=>({...f,student_id:e.target.value,student_course_id:''}))} placeholder="-- Select student --"
                disabled={!!editing} options={students.map(s=>({value:s.student_no,label:s.name,right:s.student_no}))} />
            </div>
            <div className="col-12 col-sm-6">
              <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Course *</label>
              <SearchableSelect value={form.student_course_id} onChange={e=>setForm(f=>({...f,student_course_id:e.target.value}))}
                placeholder={form.student_id ? '-- Select course --' : '-- Select student first --'}
                disabled={!!editing || !form.student_id}
                options={courses.map(c=>({value:String(c.id),label:c.course_name||c.course_code||'—',sublabel:`Intake: ${c.intake_code} — ${COURSE_STATUS_LABELS[c.status]||c.status}`}))} />
            </div>
            <FormField id="rcpt-amount" label="Amount" type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))} required />
            <FormField id="rcpt-method" label="Payment Method" value={form.payment_method} onChange={e=>setForm(f=>({...f,payment_method:e.target.value}))} options={PAYMENT_METHOD_OPTIONS} />
            <FormField id="rcpt-date" label="Payment Date" type="date" value={form.payment_date} onChange={e=>setForm(f=>({...f,payment_date:e.target.value}))} required />
            <div className="col-12"><label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Remarks</label><textarea className="sms-form-control" rows={2} value={form.remarks||''} onChange={e=>setForm(f=>({...f,remarks:e.target.value}))} /></div>
            {form.student_course_id && courses.find(c=>String(c.id)===String(form.student_course_id))?.status==='registered' && !editing && (
              <div className="col-12"><div className="alert alert-info py-2" style={{fontSize:12,marginBottom:0}}>
                <i className="bi bi-info-circle" style={{marginRight:5}} />This course is currently Registered — saving this receipt will mark it Active.
              </div></div>
            )}
          </div>
          <div className="mt-3">
            <FileAttachments ref={attachRef} module="receipts" recordId={editing?.id ?? null}
              canUpload={can('receipts','create')||can('receipts','update')} canDelete={can('receipts','delete')} />
          </div>
          <SaveButtons saving={saving} onCancel={()=>setShow(false)} label={editing?'Update':'Create'} />
        </form>
      </Modal>
      <Modal show={!!voidId} onClose={()=>setVoidId(null)} title="Void Receipt" size="sm">
        <p style={{fontSize:13,color:'var(--sms-txt2)'}}>
          This keeps the receipt on record but flags it non-paid. If nothing else depends on it, the linked course/module status will revert to Registered/Scheduled.
        </p>
        <label style={{fontSize:12,fontWeight:500,color:'var(--sms-txt2)',display:'block',marginBottom:5}}>Reason (optional)</label>
        <textarea className="sms-form-control" rows={2} value={voidReason} onChange={e=>setVoidReason(e.target.value)} />
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="sms-btn sms-btn-outline" onClick={()=>setVoidId(null)}>Cancel</button>
          <button className="sms-btn sms-btn-danger" onClick={handleVoid}>Void Receipt</button>
        </div>
      </Modal>
      <DetailModal show={!!viewing} onClose={()=>setViewing(null)} title="Receipt Details" size="lg" fields={viewing ? [
        { label: 'Receipt No',      value: viewing.receipt_no },
        { label: 'Student ID',      value: viewing.student_id },
        { label: 'Name',            value: viewing.student_name },
        { label: 'Amount',          value: fmtAmount(viewing.amount) },
        { label: 'Payment Method',  value: PAYMENT_METHOD_LABELS[viewing.payment_method]||viewing.payment_method },
        { label: 'Payment Date',    value: fmtDate(viewing.payment_date) },
        { label: 'Status',          value: RECEIPT_STATUS_LABELS[viewing.status]||viewing.status },
        ...(viewing.status==='void' ? [
          { label: 'Voided By',     value: viewing.voided_by_name },
          { label: 'Voided At',     value: fmtDate(viewing.voided_at) },
          { label: 'Void Reason',   value: viewing.void_reason, full: true },
        ] : []),
        { label: 'Intake',          value: viewing.intake_code },
        { label: 'Course',          value: viewing.course_code },
        { label: 'Course Status',   value: COURSE_STATUS_LABELS[viewing.course_status]||viewing.course_status },
        { label: 'Remarks',         value: viewing.remarks, full: true },
      ] : []}>
        {viewing && can('receipts','view') && (
          <FileAttachments module="receipts" recordId={viewing.id} canUpload={false} canDelete={false} />
        )}
      </DetailModal>
    </>
  );
}
